import arriereBackoffice, {
  AutoExecutionCalendarEntryKind,
  AutoExecutionCalendarMemberSource,
  AutoExecutionCalendarMode,
  AutoExecutionCalendarModeReason,
  type AutoExecutionCalendarQueueStatus,
  AutoExecutionCalendarTimeBasis,
  type AutoExecutionDispatchAction,
  AutoExecutionLever,
  type AutoExecutionPolicyInput,
  RevenueManagementAnalystActionType,
  RevenueManagementAutomationChangeLogScope,
  RevenueManagementAutomationChangeLogSourceType,
  RevenueManagementAutomationCohortMode,
  RevenueManagementAutomationCohortType,
  RevenueManagementAutomationDecisionStatus,
  RevenueManagementAutomationDecisionType,
  RevenueManagementAutomationKnowledgeDocType,
  RevenueManagementAutomationSortOrder,
  type RevenueManagementMutationsSelection,
  type RevenueManagementQueriesSelection,
  RevenueManagementWorkflowAdjustmentType,
  RevenueManagementWorkflowCohortType,
  type RevenueManagementWorkflowControlsInput,
  RevenueManagementWorkflowExclusionStage,
  RevenueManagementWorkflowExclusionType,
  RevenueManagementWorkflowFloorMode,
  RevenueManagementWorkflowPropertyMetric,
  RevenueManagementWorkflowRouting,
  type RevenueManagementWorkflowRunOutcome,
  RevenueManagementWorkflowRunRequestStatus,
  RevenueManagementWorkflowRunStatus,
  RevenueManagementWorkflowScheduleCadence,
  RevenueManagementWorkflowScopeKind,
  RevenueManagementWorkflowSetStatus,
  RevenueManagementWorkflowStatus,
  RevenueManagementWorkflowTriggerType,
} from '@avantstay/arriere-clients/dist/arriereBackoffice'

import { compareEventNoDesc } from './autoExecutionSort'
import * as revenueTicketsModule from './revenueTickets'
import { type WorkflowAuthoringSaveResult } from './workflowAuthoringSaveResults'

export {
  AutoExecutionLever,
  RevenueManagementWorkflowRouting,
  RevenueManagementAutomationCohortMode,
  RevenueManagementAutomationCohortType,
  RevenueManagementAutomationDecisionType,
  RevenueManagementWorkflowAdjustmentType,
  RevenueManagementWorkflowCohortType,
  RevenueManagementWorkflowExclusionStage,
  RevenueManagementWorkflowExclusionType,
  RevenueManagementWorkflowFloorMode,
  RevenueManagementWorkflowPropertyMetric,
  RevenueManagementWorkflowRunRequestStatus,
  RevenueManagementWorkflowRunStatus,
  RevenueManagementWorkflowScheduleCadence,
  RevenueManagementWorkflowScopeKind,
  RevenueManagementWorkflowStatus,
  RevenueManagementWorkflowTriggerType,
}

export { compareEventNoDesc } from './autoExecutionSort'
export type { RevenueManagementWorkflowControlsInput }
export type { WorkflowAuthoringSaveResult } from './workflowAuthoringSaveResults'

// ---------------------------------------------------------------------------
// Data shape types — owned by this service module so callers can import them
// from a single source of truth.
// ---------------------------------------------------------------------------

export type DecisionStatus = `${RevenueManagementAutomationDecisionStatus}`

export type IndicatorStatus = 'good' | 'warning' | 'poor' | 'neutral'

/** `pending` — counting down. `deferred` — waiting on capacity, WILL be
 *  re-admitted, not stuck. `failed` — an attempt failed and will be retried. */
export type ScheduledDispatchStatus = 'pending' | 'deferred' | 'failed'

/**
 * An unclaimed active reservation, from a policy or a reviewer request.
 * A missing reservation may mean the row was claimed or became terminal;
 * consult the queue and execution tasks before inferring its outcome.
 */
export interface DecisionScheduledDispatch {
  origin: string | null
  action: string | null
  reviewerName: string | null
  driftPolicy: string | null
  status: ScheduledDispatchStatus | null
  /** Absolute fire time, for an exact-time tooltip. NOT for computing the
   *  countdown — see `AutoExecQueueRow.firesInSeconds`. */
  notBefore: string | null
  policyId: string | null
  policyName: string | null
}

export interface DecisionLifecycleEvent {
  id: string
  event_no: string | null
  event_type: string
  from_status: string | null
  to_status: string | null
  occurred_at: string | null
  recorded_at: string | null
  actor_label: string | null
  actor_type: string | null
  reason: string | null
  /**
   * Free-form event context (JSON scalar). For a cohort execution event it carries
   * `applied_count` / `member_count` / `stale_child_count` / `outcome` / `failed_count`
   * so the timeline can tell a real partial apply from a fully-stale (0-applied) cohort.
   */
  event_payload: Record<string, unknown> | null
}

export interface Decision {
  id: string
  cycle_id: string
  property_id: string
  decision_type: string
  priority: string
  confidence: number
  status: DecisionStatus
  current_setting: string
  current_value: string
  proposed_setting: string
  proposed_value: string
  effective_scope: string
  effective_start: string | null
  effective_end: string | null
  observation: string
  diagnosis: string
  action_rationale: string
  expected_outcome: string
  risk_factors: string
  constraints_checked: string
  built_payloads: string
  built_rollbacks: string
  /**
   * The typed `SemanticChange` intent (BE `orchestrator/types.ts`), exposed as
   * a JSON scalar. Used by the Pricing Tool impact overlay to project the
   * decision onto the daily price / min-stay charts, and by RateScheduleTable
   * to show the per-date rate schedule of `custom_rate_set` decisions. Null on
   * selections that don't request it. Shape: `decisionImpactProjection.ts`.
   */
  change: unknown
  reviewed_at: string | null
  reviewed_by: string | null
  review_feedback: string | null
  review_feedback_types: string[]
  final_version_id: string | null
  review_session_id: string | null
  created_at: string
  updated_at: string
  cycle_date?: string
  analysis_summary?: string
  situation_category?: string
  summary?: string
  summary_changing: string[]
  summary_context: string[]
  summary_outcomes: string[]
  summary_risks: string[]
  // V22 revive feature — defaults to safe values on properties where the
  // BE query didn't select these fields yet (older selections, list views
  // that opt out of the WH read cost).
  can_revive_decision: boolean
  revive_blocked_reason: string | null
  replaces_decision_id: string | null
  // Rollback eligibility, same contract: false on any selection that opted out.
  can_rollback_decision: boolean
  rollback_blocked_reason: string | null
  decision_lifecycle: DecisionLifecycleEvent[]
  scheduled_dispatch: DecisionScheduledDispatch | null
  cohort_id?: string | null
  cohort_type?: RevenueManagementAutomationCohortType | null
  cohort_mode?: RevenueManagementAutomationCohortMode | null
  unit_count?: number | null
  decision_scope?: string
  parent_decision_id?: string | null
  children?: Decision[]
}

export interface RmProperty {
  id: string
  voyagePropertyId: string
  name: string
  market: string
  marketId: string | null
  address: string
  image_url: string
  total_revenue_ltm: number
  avg_occupancy: number
  rev_par: number
}

export interface PropertyDetail extends RmProperty {
  analyst?: string | null
  pending_decisions: Decision[]
  // `approvedWithChanges` is the subset of `approved` that was modified via the
  // review-chat reviewer flow before being approved (final_version_id IS NOT NULL).
  // It is always <= `approved` — never sum the two together.
  decisionsStats: {
    approved: number
    approvedWithChanges: number
    pending: number
    rejected: number
  }
  lastCycleDate: string | null
}

/**
 * Loads the full analysis-cycle detail (session meta + investigation log +
 * queries + notes + flags + profile updates) the `Agent Session` side panel
 * needs. Since the revenueManagement.analysisCycles query can only filter by
 * property, we request all cycles for the decision's property and pick the
 * one matching the requested cycleId.
 */
export async function getCycleDetails(cycleId: string, propertyId: string) {
  const result = await arriereBackoffice.queries.revenueManagement({
    __alias: 'revenueAgentsCycleDetails',
    analysisCycles: {
      __args: { propertyId },
      id: true,
      cycleDate: true,
      analysisSummary: true,
      sessionStatus: true,
      sessionDurationSeconds: true,
      sessionCostUsd: true,
      sessionTokenCount: true,
      sessionModel: true,
      sessionError: true,
      investigationLog: { id: true, stepOrder: true, description: true },
      noActionNotes: { id: true, area: true, note: true },
      flagsForHuman: { id: true, flag: true, resolved: true, resolvedAt: true },
      agentQueries: {
        id: true,
        queryOrder: true,
        queryType: true,
        queryText: true,
        resultSummary: true,
        success: true,
      },
      suggestedProfileUpdates: {
        id: true,
        section: true,
        proposedUpdate: true,
        evidence: true,
        status: true,
      },
      sessionTranscripts: {
        id: true,
        rawOutput: true,
        structuredJson: true,
      },
    },
  })

  const cycle = (result.analysisCycles ?? []).find(c => c.id === cycleId)
  if (!cycle) {
    return {
      session: null,
      investigationLog: [],
      noActionNotes: [],
      flags: [],
      queries: [],
      profileUpdates: [],
    }
  }

  const transcript = cycle.sessionTranscripts?.[0] ?? null

  return {
    session: {
      cycle_date: cycle.cycleDate ?? '',
      status: cycle.sessionStatus ?? 'completed',
      duration_seconds: cycle.sessionDurationSeconds ?? null,
      cost_usd: cycle.sessionCostUsd ?? null,
      token_count: cycle.sessionTokenCount ?? null,
      model: cycle.sessionModel ?? null,
      error: cycle.sessionError ?? null,
      raw_output: transcript?.rawOutput ?? null,
      structured_json: transcript?.structuredJson ?? null,
      analysis_summary: cycle.analysisSummary ?? null,
    },
    investigationLog: (cycle.investigationLog ?? []).map(s => ({
      id: s.id ?? 0,
      step_order: s.stepOrder ?? 0,
      description: s.description ?? '',
    })),
    noActionNotes: (cycle.noActionNotes ?? []).map(n => ({
      id: n.id ?? 0,
      area: n.area ?? '',
      note: n.note ?? '',
    })),
    flags: (cycle.flagsForHuman ?? []).map(f => ({
      id: f.id ?? 0,
      flag: f.flag ?? '',
      resolved: !!f.resolved,
      resolvedAt: f.resolvedAt ? String(f.resolvedAt) : null,
    })),
    queries: (cycle.agentQueries ?? []).map(q => ({
      query_order: q.queryOrder ?? 0,
      query_type: q.queryType ?? '',
      query_text: q.queryText ?? '',
      result_summary: q.resultSummary ?? null,
      success: q.success ? 1 : 0,
    })),
    profileUpdates: (cycle.suggestedProfileUpdates ?? []).map(u => ({
      id: u.id ?? 0,
      cycle_id: cycleId,
      section: u.section ?? '',
      proposed_update: u.proposedUpdate ?? '',
      evidence: u.evidence ?? null,
      status: u.status ?? 'pending',
    })),
  }
}

export interface KnowledgeDocument {
  content: string
  content_sha256: string
  doc_type: string
  file_path: string
  market: string
  property_id: string
  scope: string
}

interface CycleKnowledgeContext {
  cycle_date: string | null
  started_at: string | null
  documents: KnowledgeDocument[]
}

/**
 * Loads the cycle-time snapshot of markdown docs the agent actually used while
 * running this analysis cycle, along with the cycle's date / start time so the
 * UI can label the snapshot.
 */
export async function getCycleKnowledgeContext(
  cycleId: string,
  propertyId: string,
): Promise<CycleKnowledgeContext> {
  const result = await arriereBackoffice.queries.revenueManagement({
    __alias: 'revenueAgentsCycleKnowledgeContext',
    analysisCycles: {
      __args: { propertyId },
      id: true,
      cycleDate: true,
      startedAt: true,
      knowledgeContext: {
        content: true,
        contentSha256: true,
        docType: true,
        filePath: true,
        market: true,
        propertyId: true,
        scope: true,
      },
    },
  })

  const cycle = (result.analysisCycles ?? []).find(c => c.id === cycleId)
  const documents = (cycle?.knowledgeContext ?? []).map(k => ({
    content: k.content ?? '',
    content_sha256: k.contentSha256 ?? '',
    doc_type: k.docType ?? '',
    file_path: k.filePath ?? '',
    market: k.market ?? '',
    property_id: k.propertyId ?? '',
    scope: k.scope ? String(k.scope) : 'global',
  }))

  return {
    cycle_date: cycle?.cycleDate ?? null,
    started_at: cycle?.startedAt ? String(cycle.startedAt) : null,
    documents,
  }
}

export const getDecisionKnowledgeContext = async (decisionId: string) => {
  const { data, errors } = await arriereBackoffice.queries.revenueManagement.raw({
    __alias: 'getDecisionKnowledgeContext',
    decision: {
      __args: { id: decisionId },
      id: true,
      property: { id: true, marketId: true },
      cycle: {
        id: true,
        cycleDate: true,
        knowledgeContext: {
          filePath: true,
          contentSha256: true,
          docType: true,
          scope: true,
          content: true,
        },
      },
      cohortCycle: {
        id: true,
        cycleDate: true,
        market: true,
        knowledgeContext: {
          filePath: true,
          contentSha256: true,
          docType: true,
          scope: true,
          content: true,
        },
      },
    },
  })

  if (errors?.length) throw new Error(errors[errors.length - 1].message)

  return data?.decision ?? null
}

export const getLatestKnowledgeDocumentHash = async (filePath: string) => {
  const { data, errors } = await arriereBackoffice.queries.revenueManagement.raw({
    __alias: 'getLatestKnowledgeDocumentHash',
    knowledgeDocument: {
      __args: { filePath },
      filePath: true,
      contentSha256: true,
    },
  })

  if (errors?.length) throw new Error(errors[errors.length - 1].message)

  return data?.knowledgeDocument ?? null
}

export const getLatestKnowledgeDocument = async (filePath: string) => {
  const { data, errors } = await arriereBackoffice.queries.revenueManagement.raw({
    __alias: 'getLatestKnowledgeDocument',
    knowledgeDocument: {
      __args: { filePath },
      filePath: true,
      content: true,
    },
  })

  if (errors?.length) throw new Error(errors[errors.length - 1].message)

  return data?.knowledgeDocument ?? null
}

export type KnowledgeDocumentTarget =
  | { docType: RevenueManagementAutomationKnowledgeDocType.marketStrategy; marketId: string }
  | { docType: RevenueManagementAutomationKnowledgeDocType.propertyProfile; propertyId: string }

export const getKnowledgeDocumentForEditing = async (target: KnowledgeDocumentTarget) => {
  const { knowledgeDocumentForEditing: document } =
    await arriereBackoffice.queries.revenueManagement({
      __alias: 'getKnowledgeDocumentForEditing',
      knowledgeDocumentForEditing: {
        __args: target,
        documentId: true,
        docType: true,
        market: true,
        propertyId: true,
        filePath: true,
        canonicalSource: true,
        editingEnabled: true,
        versionId: true,
        contentSha256: true,
        content: true,
        updatedAt: true,
        catalogVersion: true,
        sections: {
          id: true,
          key: true,
          owner: true,
          editable: true,
          title: true,
          sha256: true,
          content: true,
          body: true,
        },
        addableSections: { key: true, heading: true },
      },
    })

  if (document == null) return null

  return {
    ...document,
    content: document.content ?? null,
    sections: document.sections ?? [],
    addableSections: document.addableSections ?? [],
  }
}

export const getCurrentKnowledgeDocuments = async (propertyId: string) => {
  const { data, errors } = await arriereBackoffice.queries.revenueManagement.raw({
    __alias: 'revenueAgentsKnowledgeDocuments',
    knowledgeDocuments: {
      __args: { propertyId },
      content: true,
      contentSha256: true,
      docType: true,
      filePath: true,
      market: true,
      propertyId: true,
      scope: true,
    },
  })

  if (errors?.length) throw new Error(errors[errors.length - 1].message)

  return (data?.knowledgeDocuments ?? []).map(document => ({
    ...document,
    content: document.content ?? null,
  }))
}

export interface KnowledgeSectionEdit {
  section: string
  body?: string
  remove?: boolean
  baseSha?: string
}

interface PreviewKnowledgeEditParams {
  target: KnowledgeDocumentTarget
  edits: KnowledgeSectionEdit[]
}

export const previewKnowledgeEdit = async ({ target, edits }: PreviewKnowledgeEditParams) => {
  const { data, errors } = await arriereBackoffice.queries.revenueManagement.raw({
    __alias: 'previewKnowledgeEdit',
    previewKnowledgeEdit: {
      __args: { ...target, edits },
      valid: true,
      code: true,
      errors: true,
      changed: { id: true, key: true, owner: true, linesAdded: true, linesRemoved: true },
      guardrails: {
        changed: true,
        maxAllowedDiscountPct: { before: true, after: true },
        minAllowedBasePrice: { before: true, after: true },
        rulesAdded: true,
        rulesRemoved: true,
      },
      profileGuardrails: {
        after: {
          classification: true,
          ownerHardFloorPrice: true,
          ownerHardFloorSource: true,
          ownerHardCeilingPrice: true,
          ownerHardCeilingSource: true,
          minStayRequirement: true,
          minStaySource: true,
        },
        delta: {
          changed: true,
          classification: { before: true, after: true },
          ownerHardFloorPrice: { before: true, after: true, sourceBefore: true, sourceAfter: true },
          ownerHardCeilingPrice: {
            before: true,
            after: true,
            sourceBefore: true,
            sourceAfter: true,
          },
          minStayRequirement: { before: true, after: true, sourceBefore: true, sourceAfter: true },
        },
      },
      ownership: { status: true, analysts: { name: true } },
    },
  })

  if (errors?.length) throw new Error(errors[errors.length - 1].message)

  return data?.previewKnowledgeEdit ?? null
}

interface SaveKnowledgeEditParams extends PreviewKnowledgeEditParams {
  reason: string
}

export const saveKnowledgeEdit = async ({ target, edits, reason }: SaveKnowledgeEditParams) => {
  const result = await arriereBackoffice.mutations.revenueManagement({
    __alias: 'saveKnowledgeEdit',
    saveKnowledgeEdit: {
      __args: { ...target, edits, reason },
      versionId: true,
      contentSha256: true,
    },
  })

  return result.saveKnowledgeEdit ?? null
}

interface GetKnowledgeDocumentHistoryParams {
  target: KnowledgeDocumentTarget
  limit: number
}

export const getKnowledgeDocumentHistory = async ({
  target,
  limit,
}: GetKnowledgeDocumentHistoryParams) => {
  const { data, errors } = await arriereBackoffice.queries.revenueManagement.raw({
    __alias: 'getKnowledgeDocumentHistory',
    knowledgeDocumentHistory: {
      __args: { ...target, limit },
      versions: {
        id: true,
        createdAt: true,
        source: true,
        authorName: true,
        reason: true,
        sectionsChanged: true,
        parentVersionId: true,
      },
      events: {
        id: true,
        eventType: true,
        actorType: true,
        actorLabel: true,
        actorSurface: true,
        reason: true,
        occurredAt: true,
        eventPayload: true,
      },
    },
  })

  if (errors?.length) throw new Error(errors[errors.length - 1].message)

  return {
    versions: data?.knowledgeDocumentHistory?.versions ?? [],
    events: data?.knowledgeDocumentHistory?.events ?? [],
  }
}

export const getKnowledgeDocumentVersion = async (versionId: number) => {
  const { data, errors } = await arriereBackoffice.queries.revenueManagement.raw({
    __alias: 'getKnowledgeDocumentVersion',
    knowledgeDocumentVersion: {
      __args: { versionId },
      versionId: true,
      createdAt: true,
      content: true,
      sections: { id: true, key: true, owner: true, title: true, content: true, body: true },
    },
  })

  if (errors?.length) throw new Error(errors[errors.length - 1].message)

  const version = data?.knowledgeDocumentVersion
  if (version == null) return null

  return { ...version, content: version.content ?? null, sections: version.sections ?? [] }
}

interface RestoreKnowledgeSectionsParams {
  target: KnowledgeDocumentTarget
  versionId: number
  sections: string[]
  reason: string
}

export const restoreKnowledgeSections = async ({
  target,
  versionId,
  sections,
  reason,
}: RestoreKnowledgeSectionsParams) => {
  const result = await arriereBackoffice.mutations.revenueManagement({
    __alias: 'restoreKnowledgeSections',
    restoreKnowledgeSections: {
      __args: { ...target, versionId, sections, reason },
      versionId: true,
      changed: { id: true },
    },
  })

  return result.restoreKnowledgeSections ?? null
}

export type CycleDetails = Awaited<ReturnType<typeof getCycleDetails>>

export interface ApproveDecisionResult {
  /** The decision's status AFTER execution. `rejected_stale` means the
   *  approval was refused because live Wheelhouse state drifted since build. */
  status: string
  /** On a `rejected_stale` outcome, the exact drift reason (which fields
   *  changed) — surfaced verbatim to the reviewer. */
  builderTrace: string | null
}

/**
 * Approve a decision and trigger execution. NOTE: the mutation does not throw
 * when execution is refused — a stale decision comes back with
 * `status: "rejected_stale"` and the cause on `builderTrace`. Callers MUST
 * inspect the returned status, not assume "no throw = approved".
 */
export async function approveDecision(
  id: string,
  approvalFeedback?: string,
  /** Set true ONLY after the reviewer was shown the live drift in the
   *  approve-preflight modal and confirmed it — dispatches over the current
   *  state instead of refusing as `rejected_stale`. */
  acknowledgeDrift?: boolean,
): Promise<ApproveDecisionResult> {
  // Cast until @avantstay/arriere-clients is regenerated against the rm-api
  // subgraph schema that exposes the `acknowledgeDrift` arg on approveDecision.
  const mutations = arriereBackoffice.mutations as unknown as {
    revenueManagement: (selection: Record<string, unknown>) => Promise<{
      approveDecision?: { status?: string | null; builderTrace?: string | null } | null
    }>
  }
  const args: Record<string, unknown> = { id }
  if (approvalFeedback) args.approvalFeedback = approvalFeedback
  if (acknowledgeDrift) args.acknowledgeDrift = true
  const result = await mutations.revenueManagement({
    __alias: 'approveDecision',
    approveDecision: {
      __args: args,
      id: true,
      status: true,
      builderTrace: true,
    },
  })
  const d = result.approveDecision
  return {
    status: d?.status ?? '',
    builderTrace: d?.builderTrace ?? null,
  }
}

export async function rejectDecision(id: string, feedback: string, feedbackTypes: string[]) {
  await arriereBackoffice.mutations.revenueManagement({
    __alias: 'rejectDecision',
    rejectDecision: {
      __args: { id, feedback, feedbackTypes },
      id: true,
      status: true,
    },
  })
}

/** Per-id verdict from the BE batch mutations (`ok: false` = skipped, with why). */
export interface BatchDecisionResult {
  id: string
  ok: boolean
  status: string | null
  error: string | null
}

/**
 * Mark several decisions stale in ONE GraphQL call. Unlike the single-id
 * mutation this one is guarded on the reviewable window server-side: an id
 * another reviewer already resolved comes back `ok: false` with a reason
 * instead of being stamped, and one skip never aborts the rest.
 */
export async function batchRejectStaleDecisions(
  ids: string[],
  opts?: { feedbackType?: string; feedback?: string },
): Promise<BatchDecisionResult[]> {
  // Cast until @avantstay/arriere-clients is regenerated against the rm-api
  // subgraph schema that exposes batchRejectStaleDecisions.
  const mutations = arriereBackoffice.mutations as unknown as {
    revenueManagement: (selection: Record<string, unknown>) => Promise<unknown>
  }
  const args: Record<string, unknown> = { ids }
  if (opts?.feedbackType) args.feedbackType = opts.feedbackType
  if (opts?.feedback) args.feedback = opts.feedback
  const result = (await mutations.revenueManagement({
    __alias: 'batchRejectStaleDecisions',
    batchRejectStaleDecisions: {
      __args: args,
      id: true,
      ok: true,
      status: true,
      error: true,
    },
  })) as { batchRejectStaleDecisions?: BatchDecisionResult[] | null } | null
  return result?.batchRejectStaleDecisions ?? []
}

/**
 * Mark a "flag for human attention" raised during a RevenueAgents review cycle
 * as resolved. Returns the updated flag so callers can reflect the new
 * resolved state without a full refetch if they wish.
 */
export async function setFlagAsResolved(id: number) {
  const result = await arriereBackoffice.mutations.revenueManagement({
    __alias: 'setFlagAsResolved',
    resolveFlag: {
      __args: { id },
      id: true,
      flag: true,
      resolved: true,
      resolvedAt: true,
      resolvedBy: true,
    },
  })
  return result.resolveFlag ?? null
}

/**
 * Revive a previously human-rejected decision. The backend clones the
 * original row, marks the clone `approved`, and dispatches it via the
 * normal execute path (drift-checked at dispatch time). The original
 * rejection row is left intact so its `review_feedback` and
 * `feedback_signal_*` annotations stay available to the Feedback Judge.
 *
 * Eligibility (status='rejected', not voyage/buyout-managed, payloads
 * present, dates valid, WH state matches built_state_hash) is enforced
 * server-side. Gate the UI button with `canReviveDecision` /
 * `reviveBlockedReason` on the decision row.
 */
export async function reviveDecision(id: string): Promise<string> {
  const res = await arriereBackoffice.mutations.revenueManagement({
    __alias: 'reviveDecision',
    reviveDecision: {
      __args: { id },
      id: true,
      status: true,
    },
  })
  return res?.reviveDecision?.id ?? ''
}

/**
 * Roll back an approved+executed decision. The BE undoes the change in
 * Wheelhouse/Voyage (dispatching the stored reverse payloads), stamps the
 * original execution tasks `rolled_back`, and reopens the decision as
 * `pending_review` so it can be re-approved (re-run) or rejected. Near-term
 * use is undoing an accidental approval.
 *
 * Eligibility (status='approved' AND it pushed ≥1 successful pricing-tool
 * change) is enforced server-side; an ineligible decision rejects with
 * DECISION_NOT_ROLLBACKABLE. If the undo dispatch itself fails the call
 * rejects with ROLLBACK_EXECUTION_FAILED and the decision is left approved.
 *
 * Returns the decision's status after rollback ('pending_review' on success).
 */
export async function rollbackDecision(id: string): Promise<string> {
  const res = await arriereBackoffice.mutations.revenueManagement({
    __alias: 'rollbackDecision',
    rollbackDecision: {
      __args: { id },
      id: true,
      status: true,
    },
  })
  return res?.rollbackDecision?.status ?? ''
}

type BatchApprovalArgs = NonNullable<
  RevenueManagementMutationsSelection['batchApproveDecisions']
>['__args']
export type BatchApprovalOptions = Omit<BatchApprovalArgs, 'ids'>

export async function batchApproveDecisions(ids: string[], options: BatchApprovalOptions = {}) {
  const result = await arriereBackoffice.mutations.revenueManagement({
    __alias: 'batchApproveDecisions',
    __retry: false,
    batchApproveDecisions: {
      __args: { ...options, ids },
      id: true,
      outcome: true,
      queueId: true,
      errorCode: true,
      message: true,
    },
  })
  const results = result.batchApproveDecisions
  // Anything but one result per distinct id means the outcome is unknown.
  const expected = new Set(ids)
  if (
    !results ||
    results.length !== expected.size ||
    new Set(results.map(item => item.id)).size !== expected.size ||
    results.some(item => !expected.has(item.id))
  ) {
    throw new Error('Approval response was incomplete. Refresh the decisions before retrying.')
  }
  return results
}

export type BatchApprovalResult = Awaited<ReturnType<typeof batchApproveDecisions>>[number]

export async function getDecisionApprovalState(id: string) {
  const result = await arriereBackoffice.queries.revenueManagement({
    __alias: 'decisionApprovalState',
    decision: {
      __args: { id },
      id: true,
      status: true,
      decisionScope: true,
      reviewedBy: true,
      scheduledDispatch: { origin: true, action: true },
      executionTasks: { status: true },
    },
  })
  return result.decision ?? null
}

/**
 * Retry failed (or incomplete) execution for an approved cohort parent decision.
 * Re-dispatches only members that still need application; already-applied members
 * are counted on `alreadyAppliedCount` and left alone.
 *
 * Queued responses acknowledge acceptance; their counts are a pre-execution
 * snapshot. Inline/no-work responses describe outcomes. Check queued first.
 */
export async function retryCohortExecution(id: string): Promise<{
  queued: boolean
  queueId: string | null
  allApplied: boolean
  alreadyAppliedCount: number
  dispatchedCount: number
  failedChildIds: string[]
  failedCount: number
  memberCount: number
  parentId: string
  staleChildCount: number
  staleChildIds: string[]
}> {
  const result = await arriereBackoffice.mutations.revenueManagement({
    __alias: 'retryCohortExecution',
    retryCohortExecution: {
      __args: { id },
      queued: true,
      queueId: true,
      allApplied: true,
      alreadyAppliedCount: true,
      dispatchedCount: true,
      failedChildIds: true,
      failedCount: true,
      memberCount: true,
      parentId: true,
      staleChildCount: true,
      staleChildIds: true,
    },
  })
  const summary = result.retryCohortExecution
  return {
    queued: summary?.queued ?? false,
    queueId: summary?.queueId ?? null,
    allApplied: summary?.allApplied ?? false,
    alreadyAppliedCount: summary?.alreadyAppliedCount ?? 0,
    dispatchedCount: summary?.dispatchedCount ?? 0,
    failedChildIds: (summary?.failedChildIds ?? []).filter((v): v is string => !!v),
    failedCount: summary?.failedCount ?? 0,
    memberCount: summary?.memberCount ?? 0,
    parentId: summary?.parentId ?? id,
    staleChildCount: summary?.staleChildCount ?? 0,
    staleChildIds: (summary?.staleChildIds ?? []).filter((v): v is string => !!v),
  }
}

/**
 * One cohort member's outcome in a cohort rollback.
 *
 * `driftStatus` is the part a reviewer has to see. Rollback dispatches the
 * reverse calls captured at BUILD time, so it does not "undo the decision" — it
 * forces the property back to the values it held when the decision was built.
 * `drifted` means somebody changed that property in the pricing tool since, and
 * this undo overwrote them. `unknown` means the live read failed, so we cannot
 * say either way — deliberately distinct from `clean`.
 */
export interface CohortRollbackMember {
  decisionId: string
  /** rolled_back | already_rolled_back | not_rollbackable | failed */
  outcome: string
  revertedCalls: number
  blockedReason: string | null
  error: string | null
  /** clean | drifted | unknown */
  driftStatus: string
  driftedFields: string[]
}

export interface CohortRollbackSummary {
  queued: boolean
  queueId: string | null
  parentId: string
  memberCount: number
  rolledBackCount: number
  alreadyRolledBackCount: number
  skippedCount: number
  failedCount: number
  /** Members whose undo FAILED — still applied in the pricing tool. */
  failedChildIds: string[]
  /** Members with nothing to undo (never applied, or no reverse call built). */
  skippedChildIds: string[]
  revertedCalls: number
  /** Members whose live state had changed — their undo overwrote it. */
  driftedChildIds: string[]
  /** Members whose live-state read failed, so drift is unknown. */
  driftUnknownChildIds: string[]
  parentStatus: string
  allRolledBack: boolean
  members: CohortRollbackMember[]
}

const COHORT_ROLLBACK_SUMMARY_SELECTION = {
  queued: true,
  queueId: true,
  parentId: true,
  memberCount: true,
  rolledBackCount: true,
  alreadyRolledBackCount: true,
  skippedCount: true,
  failedCount: true,
  failedChildIds: true,
  skippedChildIds: true,
  revertedCalls: true,
  driftedChildIds: true,
  driftUnknownChildIds: true,
  parentStatus: true,
  allRolledBack: true,
  members: {
    decisionId: true,
    outcome: true,
    revertedCalls: true,
    blockedReason: true,
    driftStatus: true,
    driftedFields: true,
    error: true,
  },
} as const

const compactIds = (raw: readonly (string | null)[] | null | undefined): string[] =>
  (raw ?? []).filter((v): v is string => !!v)

/**
 * Roll back a whole cohort, returning the per-member outcome.
 *
 * Prefer this over `rollbackDecision(parentId)` for a cohort: that one returns
 * a single `decisions` row, which cannot express "undid 25 of 28, these 3 are
 * still live" — exactly what a reviewer needs to know. It is also the
 * IDEMPOTENT one: a second call reports every member `already_rolled_back`
 * rather than throwing, so it is safe to click twice.
 */
export async function rollbackCohortDecision(id: string): Promise<CohortRollbackSummary> {
  const res = await arriereBackoffice.mutations.revenueManagement({
    __alias: 'rollbackCohortDecision',
    rollbackCohortDecision: {
      __args: { id },
      ...COHORT_ROLLBACK_SUMMARY_SELECTION,
    },
  })
  const s = res?.rollbackCohortDecision
  return {
    queued: s?.queued ?? false,
    queueId: s?.queueId ?? null,
    parentId: s?.parentId ?? id,
    memberCount: s?.memberCount ?? 0,
    rolledBackCount: s?.rolledBackCount ?? 0,
    alreadyRolledBackCount: s?.alreadyRolledBackCount ?? 0,
    skippedCount: s?.skippedCount ?? 0,
    failedCount: s?.failedCount ?? 0,
    failedChildIds: compactIds(s?.failedChildIds),
    skippedChildIds: compactIds(s?.skippedChildIds),
    revertedCalls: s?.revertedCalls ?? 0,
    driftedChildIds: compactIds(s?.driftedChildIds),
    driftUnknownChildIds: compactIds(s?.driftUnknownChildIds),
    parentStatus: s?.parentStatus ?? '',
    // Defaults to FALSE: an unreadable response must never be reported to the
    // reviewer as "everything was undone".
    allRolledBack: s?.allRolledBack ?? false,
    members: (s?.members ?? []).map(m => ({
      decisionId: m?.decisionId ?? '',
      outcome: m?.outcome ?? 'failed',
      revertedCalls: m?.revertedCalls ?? 0,
      blockedReason: m?.blockedReason ?? null,
      error: m?.error ?? null,
      // Same reasoning: absent drift reads as `unknown`, never as `clean`.
      driftStatus: m?.driftStatus ?? 'unknown',
      driftedFields: compactIds(m?.driftedFields),
    })),
  }
}

export interface CohortReviveMember {
  originalDecisionId: string
  cloneDecisionId: string | null
  /** revived | already_revived | skipped | failed */
  outcome: string
  blockedReason: string | null
  /** For blockedReason 'state_drifted': which hashed fields changed. */
  driftFields: string[]
}

export interface CohortReviveSummary {
  queued: boolean
  queueId: string | null
  originalParentId: string
  parentCloneId: string
  memberCount: number
  revivedCount: number
  alreadyRevivedCount: number
  skippedCount: number
  failedCount: number
  /** Members NOT cloned — ineligible at revive time (see each blockedReason). */
  skippedChildIds: string[]
  /** Members cloned but whose dispatch did not land; originals keep their rejection. */
  failedChildIds: string[]
  allRevived: boolean
  members: CohortReviveMember[]
}

const COHORT_REVIVE_SUMMARY_SELECTION = {
  queued: true,
  queueId: true,
  originalParentId: true,
  parentCloneId: true,
  memberCount: true,
  revivedCount: true,
  alreadyRevivedCount: true,
  skippedCount: true,
  failedCount: true,
  skippedChildIds: true,
  failedChildIds: true,
  allRevived: true,
  members: {
    originalDecisionId: true,
    cloneDecisionId: true,
    outcome: true,
    blockedReason: true,
    driftFields: true,
  },
} as const

/**
 * Revive a whole rejected cohort, returning the per-member outcome.
 *
 * Idempotent AND resumable: an existing live parent clone is reused and members
 * that already have one are re-dispatched, so re-running a revive that only got
 * part way through finishes the rest instead of erroring.
 */
export async function reviveCohortDecision(id: string): Promise<CohortReviveSummary> {
  const res = await arriereBackoffice.mutations.revenueManagement({
    __alias: 'reviveCohortDecision',
    reviveCohortDecision: {
      __args: { id },
      ...COHORT_REVIVE_SUMMARY_SELECTION,
    },
  })
  const s = res?.reviveCohortDecision
  return {
    queued: s?.queued ?? false,
    queueId: s?.queueId ?? null,
    originalParentId: s?.originalParentId ?? id,
    parentCloneId: s?.parentCloneId ?? '',
    memberCount: s?.memberCount ?? 0,
    revivedCount: s?.revivedCount ?? 0,
    alreadyRevivedCount: s?.alreadyRevivedCount ?? 0,
    skippedCount: s?.skippedCount ?? 0,
    failedCount: s?.failedCount ?? 0,
    skippedChildIds: compactIds(s?.skippedChildIds),
    failedChildIds: compactIds(s?.failedChildIds),
    allRevived: s?.allRevived ?? false,
    members: (s?.members ?? []).map(m => ({
      originalDecisionId: m?.originalDecisionId ?? '',
      cloneDecisionId: m?.cloneDecisionId ?? null,
      outcome: m?.outcome ?? 'failed',
      blockedReason: m?.blockedReason ?? null,
      driftFields: compactIds(m?.driftFields),
    })),
  }
}

/**
 * Apply an agent-proposed decision version. The review chat only produces
 * reviewer-safe fields; the BE merges them, then spawns the payload_builder
 * agent to regenerate the Wheelhouse payloads. Blocks until the rebuild
 * terminates (~3-10 s). Final decision status is one of:
 *   `pending_review` (built, ready to approve) or `builder_error` (rebuild failed).
 */
export async function applyDecisionVersion(versionId: string) {
  // Cast until @avantstay/arriere-clients is regenerated against the updated
  // rm-api subgraph schema that exposes this mutation.
  const mutations = arriereBackoffice.mutations as unknown as {
    revenueManagement: (selection: Record<string, unknown>) => Promise<unknown>
  }
  await mutations.revenueManagement({
    __alias: 'applyDecisionVersion',
    applyDecisionVersion: {
      __args: { versionId },
      id: true,
      status: true,
    },
  })
}

/**
 * Dismiss all active proposed versions for a decision. Leaves the decision
 * row untouched (reviewer may keep chatting). Returns dismissed count.
 */
export async function dismissDecisionVersions(decisionId: string): Promise<number> {
  const mutations = arriereBackoffice.mutations as unknown as {
    revenueManagement: (selection: Record<string, unknown>) => Promise<{
      revenueManagement: { dismissDecisionVersions: number }
    }>
  }
  const res = await mutations.revenueManagement({
    __alias: 'dismissDecisionVersions',
    dismissDecisionVersions: {
      __args: { decisionId },
    },
  })
  return res?.revenueManagement?.dismissDecisionVersions ?? 0
}

/**
 * Loads the execution task history for a decision from the revenue management
 * API. The returned shape matches the snake_cased `ExecutionTask` consumed by
 * the ApiCallSidePanel so the dialog can be populated without further mapping.
 */
export async function getExecutionTasks(decisionId: string) {
  const result = await arriereBackoffice.queries.revenueManagement({
    __alias: 'revenueAgentsExecutionTasks',
    executionTasks: {
      __args: { decisionId },
      id: true,
      apiEndpoint: true,
      apiMethod: true,
      apiPayload: true,
      executionResponse: true,
      executionError: true,
      status: true,
      executedAt: true,
      retryCount: true,
      createdAt: true,
    },
  })

  return (result.executionTasks ?? []).map(t => ({
    id: t.id ?? '',
    decision_id: decisionId,
    api_endpoint: t.apiEndpoint ?? '',
    api_method: t.apiMethod ?? '',
    api_payload: t.apiPayload ?? '',
    execution_response: t.executionResponse ?? null,
    execution_error: t.executionError ?? null,
    status: (t.status ?? 'pending') as
      | 'pending'
      | 'queued'
      | 'executing'
      | 'success'
      | 'failed'
      | 'rolled_back'
      | 'cancelled',
    executed_at: t.executedAt ? String(t.executedAt) : null,
    retry_count: t.retryCount ?? 0,
    created_at: t.createdAt ? String(t.createdAt) : new Date().toISOString(),
  }))
}

export type ExecutionTask = Awaited<ReturnType<typeof getExecutionTasks>>[number]

/** Which way a hotel yield child moves its room type's price. */
export type HotelMoveDirection = 'RAISE' | 'CUT'

/** Which nights of the judged window a hotel move applies to (weekend = Fri + Sat). */
export type HotelDayFilter = 'ALL' | 'WEEKDAY' | 'WEEKEND'

/**
 * A hotel yield child's room type, move and judged window (BE PRI-1706,
 * `hotelContext`). Null on every non-hotel decision and on cohort parents.
 *
 * A hotel child is filed under ONE reference unit standing in for its whole
 * room type, so its `propertyName` names a single unit — label it with
 * `displayTitle`. Raises and cuts share `decision_type`
 * `booking_window_discount`; `direction` is what tells them apart, and
 * `priority` is always `medium` on these rows.
 */
export interface HotelDecisionContext {
  hotelId?: string | null
  hotelOtbPct?: number | null
  againstBuildingOccupancy?: boolean | null
  againstBuildingReason?: string | null
  /** "<Room type> · <Building>", e.g. "Deluxe King · The Gilmore". */
  displayTitle: string
  building: string
  roomTypeId: string
  roomTypeName: string
  /** Units of the room type this one decision prices. */
  units: number | null
  /** Null only on a malformed row. */
  direction: HotelMoveDirection | null
  /** Signed: +12 = raise 12%, -10 = cut 10%. Null when `edited` or unknown. */
  authoredPct: number | null
  /** Judged window, inclusive YYYY-MM-DD — identical across the room types priced together. */
  windowStart: string | null
  windowEnd: string | null
  dayFilter: HotelDayFilter | null
  /** Nights this decision actually changes (sold-out and floor-pinned nights are not written). */
  nights: number | null
  /** Nights left untouched because Wheelhouse already sits at its min-price floor. Null = unknown. */
  floorPinnedNights: number | null
  /** Shared intent within a workflow run; final dates and magnitudes may differ. */
  groupKey: string | null
  /** Modified in review after the AI built it — never bulk-grouped. */
  edited: boolean
}

export interface FeedbackFollowupCoverRef {
  id: string
  propertyName?: string | null
}

export interface RevenueDecision {
  id: string
  cohort_id: string | null
  cohort_type: RevenueManagementAutomationCohortType | null
  cohort_mode: RevenueManagementAutomationCohortMode | null
  /** How many identical units the listed reference property stands for. */
  unit_count: number | null
  decision_scope: string
  parent_decision_id: string | null
  /** Per-unit child decisions when this row is a cohort-level parent. */
  children: RevenueDecision[]
  cycle_id: string
  decision_type: string
  priority: string
  confidence: number
  status: string
  current_value: string
  proposed_value: string
  observation: string
  diagnosis: string
  action_rationale: string
  expected_outcome: string
  risk_factors: string
  constraints_checked: string
  built_payloads: string
  built_rollbacks: string
  /**
   * The typed `SemanticChange` intent (BE `orchestrator/types.ts`) as a JSON
   * scalar. For `custom_rate_set` decisions this carries the `per_date` map —
   * the exact rate applied to each stay date on approval. The rate-schedule
   * chart reads its KEYS (the nights touched); the depth it plots comes from
   * the applied map below or the proposal wording, never from these values
   * (see `utils/authoredCut.ts`). Null when the row predates typed changes.
   */
  change: unknown
  /**
   * What THIS decision takes off, per stay date, in adjustment points (positive
   * = points off). Written by the engine into
   * `current_state_snapshot.applied_discount_pct_by_date` (BE PRI-1562).
   *
   * This is the number a reviewer is approving. `change.per_date` is NOT: it
   * carries the RESULTING adjustment, and stacking is additive, so reading
   * `per_date − 100` reports the cumulative position versus the Wheelhouse
   * engine rather than this decision's cut (see `utils/authoredCut.ts`).
   *
   * Undefined until the dedicated `appliedDiscountPctByDate` field reaches the
   * federated schema snapshot the typed client is generated from — the column
   * that holds it (`currentStateSnapshot`) is exposed but carries the full
   * Wheelhouse read per decision, far too heavy for a 200-row list query. Until
   * then `authoredCut` falls back to the engine's own `proposed_value` wording.
   */
  applied_discount_pct_by_date?: Record<string, number> | null
  /**
   * The `change` the map above was computed from. Review chat can edit a
   * decision's dates or depths — which rebuilds `change` but cannot recompute
   * the map — so a consumer must confirm the two still match before captioning
   * the decision with the map's numbers.
   */
  applied_discount_change?: unknown
  effective_scope: string
  effective_start: string | null
  effective_end: string | null
  created_at: string
  reviewed_at: string | null
  reviewed_by: string | null
  review_feedback: string | null
  review_feedback_types: string[]
  execution_tasks: ExecutionTask[]
  decision_lifecycle: DecisionLifecycleEvent[]
  scheduled_dispatch: DecisionScheduledDispatch | null
  situation_category: string | null
  summary: string
  summary_changing: string[]
  /** Deterministic, fee/value-labeled bullets from the typed change (e.g.
   *  "Cleaning fee: $0 → $105"). Computed server-side from `change`; unlike
   *  summary_changing it has no LLM lag and always names the setting. */
  summary_scope: string[]
  summary_context: string[]
  summary_outcomes: string[]
  summary_risks: string[]
  propertyId: string
  propertyName: string
  market: string
  image_url: string
  /** For a workflow-authored decision: the workflow_runs id that created it;
   *  null on analyst/AI decisions. */
  workflow_run_id: string | null
  /** The workflow SET this decision's workflow belongs to — groups a
   *  multi-market policy's decisions together; null otherwise. */
  workflow_set_id: string | null
  /**
   * V22/V23 rollback + revive eligibility, straight from the BE gates
   * (`rollbackEligibility.ts` / `reviveEligibility.ts`). The detail panes gate
   * their Rollback/Revive controls on these instead of on `status` alone, so an
   * ineligible decision disables with a reason rather than failing on click.
   *
   * Only the DETAIL path selects them (`REVENUE_DECISION_FIELDS`, reached via
   * `getDecisionById`). They are deliberately absent from the list-row
   * selections: `canRollbackDecision` is a COUNT per row and `canReviveDecision`
   * a live Wheelhouse read per unique listing, and requesting them across a
   * 340-row queue once exhausted the API's connection pool.
   */
  can_rollback_decision: boolean
  /** Why rollback is blocked (when `can_rollback_decision` is false) — for a tooltip. */
  rollback_blocked_reason: string | null
  can_revive_decision: boolean
  /** Why revive is blocked (when `can_revive_decision` is false) — for a tooltip. */
  revive_blocked_reason: string | null
  /** For a revived decision: id of the original rejected row it was cloned from. */
  replaces_decision_id: string | null
  /** Hotel yield children only (see HotelDecisionContext); null/absent otherwise. */
  hotel_context?: HotelDecisionContext | null
  feedbackFollowupStatus: string | null
  feedbackFollowupCoveredBy: FeedbackFollowupCoverRef | null
}

// What the hotel review list groups, sorts and labels on (PRI-1708). Cheap on
// both shapes: `decision(id)` computes it in memory from the row it already
// loaded, and `pendingDecisionsList` batches it into one query per request.
// Deliberately NOT on the Reviewed list selection — the opened cohort hydrates
// through `decision(id)`, which carries it.
const HOTEL_CONTEXT_FIELDS = {
  hotelId: true,
  hotelOtbPct: true,
  againstBuildingOccupancy: true,
  againstBuildingReason: true,
  displayTitle: true,
  building: true,
  roomTypeId: true,
  roomTypeName: true,
  units: true,
  direction: true,
  authoredPct: true,
  windowStart: true,
  windowEnd: true,
  dayFilter: true,
  nights: true,
  floorPinnedNights: true,
  groupKey: true,
  edited: true,
} as const

type RawHotelDecisionContext = {
  hotelId?: string | null
  hotelOtbPct?: number | null
  againstBuildingOccupancy?: boolean | null
  againstBuildingReason?: string | null
  displayTitle?: string | null
  building?: string | null
  roomTypeId?: string | null
  roomTypeName?: string | null
  units?: number | null
  direction?: string | null
  authoredPct?: number | null
  windowStart?: string | null
  windowEnd?: string | null
  dayFilter?: string | null
  nights?: number | null
  floorPinnedNights?: number | null
  groupKey?: string | null
  edited?: boolean | null
}

const HOTEL_MOVE_DIRECTIONS: readonly HotelMoveDirection[] = ['RAISE', 'CUT']
const HOTEL_DAY_FILTERS: readonly HotelDayFilter[] = ['ALL', 'WEEKDAY', 'WEEKEND']

const finiteOrNull = (value: number | null | undefined): number | null =>
  typeof value === 'number' && Number.isFinite(value) ? value : null

export function mapHotelDecisionContext(
  raw: RawHotelDecisionContext | null | undefined,
): HotelDecisionContext | null {
  if (!raw) return null
  const direction = HOTEL_MOVE_DIRECTIONS.find(value => value === raw.direction) ?? null
  const dayFilter = HOTEL_DAY_FILTERS.find(value => value === raw.dayFilter) ?? null
  const roomTypeName = raw.roomTypeName ?? ''
  const building = raw.building ?? ''
  return {
    displayTitle:
      raw.displayTitle || [roomTypeName, building].filter(Boolean).join(' · ') || 'Room type',
    building,
    hotelId: raw.hotelId ?? null,
    hotelOtbPct: finiteOrNull(raw.hotelOtbPct),
    againstBuildingOccupancy: raw.againstBuildingOccupancy ?? null,
    againstBuildingReason: raw.againstBuildingReason ?? null,
    roomTypeId: raw.roomTypeId ?? '',
    roomTypeName,
    units: finiteOrNull(raw.units),
    direction,
    authoredPct: finiteOrNull(raw.authoredPct),
    windowStart: raw.windowStart || null,
    windowEnd: raw.windowEnd || null,
    dayFilter,
    nights: finiteOrNull(raw.nights),
    floorPinnedNights: finiteOrNull(raw.floorPinnedNights),
    groupKey: raw.groupKey || null,
    edited: raw.edited ?? false,
  }
}

const DECISION_LIFECYCLE_FIELDS = {
  id: true,
  eventNo: true,
  eventType: true,
  fromStatus: true,
  toStatus: true,
  occurredAt: true,
  recordedAt: true,
  actorLabel: true,
  actorType: true,
  reason: true,
  eventPayload: true,
} as const

const REVENUE_DECISION_FIELDS = {
  id: true,
  cohortId: true,
  cohortType: true,
  cohortMode: true,
  totalUnits: true,
  decisionScope: true,
  parentDecisionId: true,
  workflowSetId: true,
  workflowRunId: true,
  decisionType: true,
  priority: true,
  confidence: true,
  status: true,
  currentValue: true,
  proposedValue: true,
  observation: true,
  diagnosis: true,
  actionRationale: true,
  expectedOutcome: true,
  riskFactors: true,
  constraintsChecked: true,
  builtPayloads: true,
  builtRollbacks: true,
  change: true,
  effectiveScope: true,
  effectiveStart: true,
  effectiveEnd: true,
  summary: true,
  summaryChanging: true,
  summaryScope: true,
  summaryContext: true,
  summaryOutcomes: true,
  summaryRisks: true,
  createdAt: true,
  reviewedAt: true,
  reviewedBy: true,
  reviewFeedback: true,
  reviewFeedbackType: true,
  feedbackFollowupStatus: true,
  feedbackFollowupCoveredBy: {
    id: true,
    propertyName: true,
  },
  decisionLifecycle: DECISION_LIFECYCLE_FIELDS,
  // Active unclaimed reservation. Policy countdowns come from the queue join;
  // reviewer requests show execution progress instead.
  scheduledDispatch: {
    origin: true,
    action: true,
    reviewerName: true,
    driftPolicy: true,
    notBefore: true,
    status: true,
    policyId: true,
    policyName: true,
  },
  executionTasks: {
    id: true,
    status: true,
    executionError: true,
    executedAt: true,
    retryCount: true,
    createdAt: true,
  },
  cycle: {
    id: true,
    situationCategory: true,
  },
  property: {
    id: true,
    name: true,
    market: true,
    imageUrl: true,
  },
  hotelContext: HOTEL_CONTEXT_FIELDS,
  // Rollback eligibility for the detail panes' action footers. DETAIL ONLY —
  // never add this to REVENUE_DECISION_LIST_ROW_FIELDS or PENDING_LIST_FIELDS:
  // selecting it across a 340-row queue exhausted the API's connection pool
  // once already. It is now resolved through a per-request batched loader, so
  // a decision plus its cohort children costs one query regardless of size.
  canRollbackDecision: true,
  rollbackBlockedReason: true,
} as const

// Revive eligibility is the EXPENSIVE half and is parent-level only.
//
// `canReviveDecision` short-circuits with no I/O for anything that isn't
// `rejected` — but a REJECTED cohort is precisely the one a reviewer opens to
// revive, and there every child is rejected, so each costs a live Wheelhouse
// read for its own listing. Spreading these into `children` made opening a
// 95-unit rejected cohort issue ~95 live reads in a single getDecisionById.
//
// The parent answers for the whole cohort on its own (the backend aggregates
// over its members and deliberately skips the live-state check for exactly
// this reason), and nothing renders per-unit revive state today. So: parent
// only. Re-adding these to the children selection is how the cost comes back.
const REVENUE_DECISION_REVIVE_FIELDS = {
  canReviveDecision: true,
  reviveBlockedReason: true,
  replacesDecisionId: true,
} as const

// Cohort-level decisions come back as a parent row plus per-unit `children`
// (one level deep — children never have children of their own).
const REVENUE_DECISION_SELECTION = {
  ...REVENUE_DECISION_FIELDS,
  ...REVENUE_DECISION_REVIVE_FIELDS,
  children: REVENUE_DECISION_FIELDS,
} as const

// What a LIST ROW actually renders and filters on: identity, badges,
// filter/sort/chart inputs and cohort grouping. Everything else — payloads,
// lifecycle, execution tasks, reasoning text, summaries — is detail-pane
// content the selected decision hydrates through getDecisionById (full
// REVENUE_DECISION_SELECTION). Keeping the Reviewed page slim matters because
// the full selection ships builtPayloads/builtRollbacks JSON blobs for every
// row AND every cohort child. `change`/`proposedValue` stay because CohortRow's
// authored-cut badge (utils/authoredCut.ts) derives the cut span from them.
// The pending list has its own narrower shape — see PENDING_LIST_FIELDS.
const REVENUE_DECISION_LIST_ROW_FIELDS = {
  id: true,
  cohortId: true,
  cohortType: true,
  // cohortMode/totalUnits drive CohortDetail's per-unit approve affordances,
  // which must be right even before hydration resolves — and both come from
  // the same batched lineage lookup as the other cohort fields.
  cohortMode: true,
  totalUnits: true,
  decisionScope: true,
  parentDecisionId: true,
  workflowSetId: true,
  workflowRunId: true,
  decisionType: true,
  priority: true,
  confidence: true,
  status: true,
  currentValue: true,
  proposedValue: true,
  change: true,
  createdAt: true,
  reviewedAt: true,
  reviewedBy: true,
  reviewFeedback: true,
  reviewFeedbackType: true,
  property: {
    id: true,
    name: true,
    market: true,
    imageUrl: true,
  },
} as const

const REVENUE_DECISION_LIST_SELECTION = {
  ...REVENUE_DECISION_LIST_ROW_FIELDS,
  children: REVENUE_DECISION_LIST_ROW_FIELDS,
} as const

type RawRevenueDecision = {
  id?: string | null
  cohortId?: string | null
  cohortType?: RevenueManagementAutomationCohortType | null
  cohortMode?: RevenueManagementAutomationCohortMode | null
  totalUnits?: number | null
  decisionScope?: string | null
  parentDecisionId?: string | null
  workflowSetId?: string | null
  workflowRunId?: string | null
  children?: RawRevenueDecision[] | null
  decisionType?: string | null
  priority?: string | null
  confidence?: number | null
  status?: string | null
  currentValue?: string | null
  proposedValue?: string | null
  observation?: string | null
  diagnosis?: string | null
  actionRationale?: string | null
  expectedOutcome?: string | null
  riskFactors?: string | null
  constraintsChecked?: string | null
  builtPayloads?: string | null
  builtRollbacks?: string | null
  /** Typed SemanticChange intent (JSON scalar) — see RevenueDecision.change. */
  change?: unknown
  /** See RevenueDecision.applied_discount_pct_by_date — absent from selections
   *  until the field reaches the federated schema. */
  appliedDiscountPctByDate?: Record<string, number> | null
  appliedDiscountChange?: unknown
  effectiveScope?: string | null
  effectiveStart?: unknown
  effectiveEnd?: unknown
  summary?: string | null
  summaryChanging?: (string | null)[] | null
  summaryScope?: (string | null)[] | null
  summaryContext?: (string | null)[] | null
  summaryOutcomes?: (string | null)[] | null
  summaryRisks?: (string | null)[] | null
  createdAt?: unknown
  reviewedAt?: unknown
  reviewedBy?: string | null
  reviewFeedback?: string | null
  reviewFeedbackType?: (string | null)[] | string | null
  feedbackFollowupStatus?: string | null
  feedbackFollowupCoveredBy?: FeedbackFollowupCoverRef | null
  decisionLifecycle?: RawDecisionLifecycleEvent[] | null
  scheduledDispatch?: {
    origin?: string | null
    action?: string | null
    reviewerName?: string | null
    driftPolicy?: string | null
    notBefore?: unknown
    status?: string | null
    policyId?: string | null
    policyName?: string | null
  } | null
  executionTasks?: RawExecutionTask[] | null
  cycle?: { id?: string | null; situationCategory?: string | null } | null
  property?: {
    id?: string | null
    name?: string | null
    market?: string | null
    imageUrl?: string | null
  } | null
  canRollbackDecision?: boolean | null
  rollbackBlockedReason?: string | null
  canReviveDecision?: boolean | null
  reviveBlockedReason?: string | null
  replacesDecisionId?: string | null
  hotelContext?: RawHotelDecisionContext | null
}

export type RawDecisionLifecycleEvent = {
  id?: string | null
  eventNo?: string | null
  eventType?: string | null
  fromStatus?: string | null
  toStatus?: string | null
  occurredAt?: unknown
  recordedAt?: unknown
  actorLabel?: string | null
  actorType?: string | null
  reason?: string | null
  eventPayload?: unknown
}

type RawExecutionTask = {
  id?: string | null
  apiEndpoint?: string | null
  apiMethod?: string | null
  apiPayload?: string | null
  executionResponse?: string | null
  executionError?: string | null
  status?: string | null
  executedAt?: unknown
  retryCount?: number | null
  createdAt?: unknown
}

function normalizeCohortType(
  value: RevenueManagementAutomationCohortType | null | undefined,
): RevenueManagementAutomationCohortType | null {
  return value && Object.values(RevenueManagementAutomationCohortType).includes(value)
    ? value
    : null
}

function normalizeCohortMode(
  value: RevenueManagementAutomationCohortMode | null | undefined,
): RevenueManagementAutomationCohortMode | null {
  return value && Object.values(RevenueManagementAutomationCohortMode).includes(value)
    ? value
    : null
}

function compactStrings(raw: (string | null)[] | null | undefined): string[] {
  return (raw ?? []).filter((i): i is string => !!i)
}

function normalizeReviewFeedbackTypes(raw: RawRevenueDecision['reviewFeedbackType']): string[] {
  if (Array.isArray(raw)) return compactStrings(raw)
  return raw ? [raw] : []
}

function mapExecutionTask(t: RawExecutionTask, decisionId: string): ExecutionTask {
  return {
    id: t.id ?? '',
    decision_id: decisionId,
    api_endpoint: t.apiEndpoint ?? '',
    api_method: t.apiMethod ?? '',
    api_payload: t.apiPayload ?? '',
    execution_response: t.executionResponse ?? null,
    execution_error: t.executionError ?? null,
    status: (t.status ?? 'pending') as ExecutionTask['status'],
    executed_at: t.executedAt ? String(t.executedAt) : null,
    retry_count: t.retryCount ?? 0,
    created_at: t.createdAt ? String(t.createdAt) : new Date().toISOString(),
  }
}

// eventPayload is a JSON scalar — the client may hand it back already parsed or,
// depending on transport, as a JSON string. Normalize to a plain object (or null),
// mirroring the string-or-object handling in parseSubscores.
function toEventPayload(raw: unknown): Record<string, unknown> | null {
  let value = raw
  if (typeof value === 'string') {
    try {
      value = JSON.parse(value)
    } catch {
      return null
    }
  }
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null
}

export function mapScheduledDispatch(
  raw:
    | {
        origin?: string | null
        action?: string | null
        reviewerName?: string | null
        driftPolicy?: string | null
        notBefore?: unknown
        status?: string | null
        policyId?: string | null
        policyName?: string | null
      }
    | null
    | undefined,
): DecisionScheduledDispatch | null {
  if (!raw) return null
  return {
    origin: raw.origin ?? null,
    action: raw.action ?? null,
    reviewerName: raw.reviewerName ?? null,
    driftPolicy: raw.driftPolicy ?? null,
    status: (raw.status ?? null) as ScheduledDispatchStatus | null,
    notBefore: raw.notBefore == null ? null : String(raw.notBefore),
    policyId: raw.policyId ?? null,
    policyName: raw.policyName ?? null,
  }
}

export function mapDecisionLifecycleEvent(e: RawDecisionLifecycleEvent): DecisionLifecycleEvent {
  return {
    id: e.id ?? '',
    event_no: e.eventNo ?? null,
    event_type: e.eventType ?? '',
    from_status: e.fromStatus ?? null,
    to_status: e.toStatus ?? null,
    occurred_at: e.occurredAt ? String(e.occurredAt) : null,
    recorded_at: e.recordedAt ? String(e.recordedAt) : null,
    actor_label: e.actorLabel ?? null,
    actor_type: e.actorType ?? null,
    reason: e.reason ?? null,
    event_payload: toEventPayload(e.eventPayload),
  }
}

const PRIORITY_ORDER = ['high', 'medium', 'low']

function highestPriority(children: readonly { priority: string }[]): string | null {
  let best: string | null = null
  for (const c of children) {
    const rank = PRIORITY_ORDER.indexOf(c.priority)
    if (rank >= 0 && (best === null || rank < PRIORITY_ORDER.indexOf(best))) best = c.priority
  }
  return best
}

// Cohort parents carry the cohort-wide finding but the per-unit specifics
// (type, confidence, priority, property) live on the children — fall back to
// them so list filters/sorting/chart buckets work on parent rows too.
function mapRevenueDecision(d: RawRevenueDecision, scope?: string): RevenueDecision {
  const children = (d.children ?? []).map(c => mapRevenueDecision(c, 'cohort_child'))
  const head: RevenueDecision | undefined = children[0]
  const cohortType = normalizeCohortType(d.cohortType)
  const avgChildConfidence = children.length
    ? children.reduce((sum, c) => sum + c.confidence, 0) / children.length
    : null
  return {
    id: d.id ?? '',
    cohort_id: d.cohortId ?? null,
    cohort_type: cohortType,
    cohort_mode: normalizeCohortMode(d.cohortMode),
    unit_count: d.totalUnits ?? null,
    decision_scope:
      d.decisionScope ?? scope ?? (children.length > 0 ? 'cohort_parent' : 'property'),
    parent_decision_id: d.parentDecisionId ?? null,
    workflow_set_id: d.workflowSetId ?? head?.workflow_set_id ?? null,
    workflow_run_id: d.workflowRunId ?? head?.workflow_run_id ?? null,
    children,
    cycle_id: d.cycle?.id ?? head?.cycle_id ?? '',
    decision_type: d.decisionType ?? head?.decision_type ?? '',
    priority: d.priority ?? highestPriority(children) ?? 'low',
    confidence: d.confidence ?? avgChildConfidence ?? 0,
    status: d.status ?? 'pending',
    current_value: d.currentValue ?? '',
    proposed_value: d.proposedValue ?? '',
    observation: d.observation ?? '',
    diagnosis: d.diagnosis ?? '',
    action_rationale: d.actionRationale ?? '',
    expected_outcome: d.expectedOutcome ?? '',
    risk_factors: d.riskFactors ?? '',
    constraints_checked: d.constraintsChecked ?? '',
    built_payloads: d.builtPayloads ?? '',
    change: d.change ?? null,
    applied_discount_pct_by_date: d.appliedDiscountPctByDate ?? null,
    applied_discount_change: d.appliedDiscountChange,
    built_rollbacks: d.builtRollbacks ?? '',
    // Absent on the list-row selections (see REVENUE_DECISION_FIELDS), so the
    // defaults must read as "not eligible": a slim row must never render an
    // enabled Rollback/Revive before the detail fetch resolves.
    can_rollback_decision: d.canRollbackDecision ?? false,
    rollback_blocked_reason: d.rollbackBlockedReason ?? null,
    can_revive_decision: d.canReviveDecision ?? false,
    revive_blocked_reason: d.reviveBlockedReason ?? null,
    replaces_decision_id: d.replacesDecisionId ?? null,
    hotel_context: mapHotelDecisionContext(d.hotelContext),
    effective_scope: d.effectiveScope ?? '',
    effective_start: d.effectiveStart ? String(d.effectiveStart) : null,
    effective_end: d.effectiveEnd ? String(d.effectiveEnd) : null,
    summary: d.summary ?? '',
    summary_changing: compactStrings(d.summaryChanging),
    summary_scope: compactStrings(d.summaryScope),
    summary_context: compactStrings(d.summaryContext),
    summary_outcomes: compactStrings(d.summaryOutcomes),
    summary_risks: compactStrings(d.summaryRisks),
    created_at: d.createdAt ? String(d.createdAt) : new Date().toISOString(),
    reviewed_at: d.reviewedAt ? String(d.reviewedAt) : null,
    reviewed_by: d.reviewedBy ?? null,
    review_feedback: d.reviewFeedback ?? null,
    review_feedback_types: normalizeReviewFeedbackTypes(d.reviewFeedbackType),
    feedbackFollowupStatus: d.feedbackFollowupStatus ?? null,
    feedbackFollowupCoveredBy: d.feedbackFollowupCoveredBy ?? null,
    execution_tasks: (d.executionTasks ?? []).map(t => mapExecutionTask(t, d.id ?? '')),
    decision_lifecycle: (d.decisionLifecycle ?? []).map(mapDecisionLifecycleEvent),
    scheduled_dispatch: mapScheduledDispatch(d.scheduledDispatch),
    situation_category: d.cycle?.situationCategory ?? head?.situation_category ?? null,
    propertyId: d.property?.id ?? head?.propertyId ?? '',
    propertyName: d.property?.name ?? head?.propertyName ?? '',
    market: d.property?.market ?? head?.market ?? '',
    image_url: d.property?.imageUrl ?? head?.image_url ?? '',
  }
}

// When the API returns both a cohort parent and its child rows at the top
// level, keep only the parent — the children ride along on `parent.children`.
// A child whose parent is NOT in the list (e.g. parent already resolved)
// stays, so no pending decision is dropped.
function dropChildRows(decisions: RevenueDecision[]): RevenueDecision[] {
  const parentIds = new Set(decisions.filter(d => d.children.length > 0).map(d => d.id))
  return decisions.filter(d => !(d.parent_decision_id && parentIds.has(d.parent_decision_id)))
}

export interface PendingDecisionsResult {
  decisions: RevenueDecision[]
  /** True when the response carried GraphQL errors — `decisions` may be a
   *  partial list (some subgraph requests failed). */
  hasErrors: boolean
}

export interface ReviewedDecisionsPageResult extends PendingDecisionsResult {
  currentPage: number
  nextPage: number | null
  pages: number
  total: number
}

export interface ReviewedDecisionsFilters extends RevenueAgentsFilters {
  decisionTypes?: string[]
  propertyId?: string
  /** Free text matched server-side against the decision's property name and
   *  market, the cohort slug and type, and the names and markets of the
   *  cohort's children. Terms are whitespace-separated and ANDed; the server
   *  bounds them to 8 terms of 64 chars and escapes LIKE wildcards.
   *
   *  NOT an exact mirror of the pending tab's client-side filter: that one
   *  matches the cohort label as rendered ("Workflow: Fee Override · Poconos"),
   *  which is derived from the slug and has no column to match here. */
  search?: string
  sortOrder?: RevenueManagementAutomationSortOrder
  status?: RevenueManagementAutomationDecisionStatus
}

const PENDING_LIST_FIELDS = {
  id: true,
  cohortId: true,
  cohortType: true,
  parentDecisionId: true,
  workflowSetId: true,
  decisionType: true,
  priority: true,
  confidence: true,
  status: true,
  currentValue: true,
  proposedValue: true,
  change: true,
  effectiveStart: true,
  effectiveEnd: true,
  createdAt: true,
  reviewedAt: true,
  reviewedBy: true,
  reviewFeedback: true,
  property: {
    id: true,
    name: true,
    market: true,
    imageUrl: true,
  },
  hotelContext: HOTEL_CONTEXT_FIELDS,
} as const

const PENDING_LIST_SELECTION = {
  ...PENDING_LIST_FIELDS,
  children: PENDING_LIST_FIELDS,
} as const

/**
 * Loads every decision in the pending bucket (all `pending*` statuses the API
 * groups together) with just the fields the /revenue/agents/actions list
 * renders — the detail pane loads the rest per row via `getDecisionById`.
 *
 * Uses the silent `.raw` endpoint: the router can return partial data
 * alongside errors, and the default loud mode would throw the data away.
 */
export async function getPendingDecisionsList(
  filters: RevenueAgentsFilters = {},
): Promise<PendingDecisionsResult> {
  const { data, errors } = await arriereBackoffice.queries.revenueManagement.raw({
    __alias: 'revenueAgentsPendingDecisionsList',
    pendingDecisionsList: {
      __args: {
        analystId: filters.analystId || undefined,
      },
      ...PENDING_LIST_SELECTION,
    },
  })

  const rows = (data?.pendingDecisionsList ?? []).filter(d => !!d)
  return {
    decisions: dropChildRows(rows.map(d => mapRevenueDecision(d))),
    hasErrors: !!errors?.length,
  }
}

/**
 * Loads a single decision by id (any status) joined with the property data
 * needed to render the resolved/missing-decision fallback on the
 * /revenue/agents/actions view. The actions list only holds `pending*`
 * decisions, so a `decisionId` deep-link that points at an already
 * approved/rejected decision falls back to this lookup. Returns null when the
 * id doesn't resolve to a decision.
 */
export async function getDecisionById(id: string): Promise<RevenueDecision | null> {
  const result = await arriereBackoffice.queries.revenueManagement({
    __alias: 'revenueAgentsDecisionById',
    decision: {
      __args: { id },
      ...REVENUE_DECISION_SELECTION,
    },
  })
  const d = result.decision
  if (!d) return null
  return mapRevenueDecision(d)
}

export interface DecisionCohortParent {
  parentId: string
  childId: string
}

/** Parent cohort of a cohort child id; null for parents, property decisions and unknown ids. */
export async function getDecisionCohortParent(id: string): Promise<DecisionCohortParent | null> {
  const result = await arriereBackoffice.queries.revenueManagement({
    __alias: 'revenueAgentsDecisionCohortParent',
    decisionCohortParent: {
      __args: { id },
      parentId: true,
      childId: true,
    },
  })
  const parent = result.decisionCohortParent
  return parent ? { parentId: parent.parentId, childId: parent.childId } : null
}

/** Loads one page of terminal decisions for the Actions page's Reviewed view. */
export async function getReviewedDecisions(
  filters: ReviewedDecisionsFilters = {},
  page = 1,
  size = 50,
): Promise<ReviewedDecisionsPageResult> {
  const { data, errors } = await arriereBackoffice.queries.revenueManagement.raw({
    __alias: 'revenueAgentsReviewedDecisions',
    reviewedDecisions: {
      __args: {
        analystId: filters.analystId || undefined,
        marketId: filters.marketId || undefined,
        propertyId: filters.propertyId || undefined,
        decisionType: filters.decisionTypes?.length ? filters.decisionTypes : undefined,
        // The `.raw` path does NOT type-check `__args`, so a missing arg on the
        // router fails nothing at compile time — the query fails validation at
        // runtime instead, taking the whole reviewed list with it. The rm-api
        // subgraph exposing `reviewedDecisions(search:)` must be published
        // before this ships.
        search: filters.search || undefined,
        sortOrder: filters.sortOrder ?? RevenueManagementAutomationSortOrder.desc,
        statuses: filters.status ? [filters.status] : undefined,
        pagination: { page, size },
      },
      currentPage: true,
      nextPage: true,
      pages: true,
      total: true,
      results: REVENUE_DECISION_LIST_SELECTION,
    },
  })
  const result = data?.reviewedDecisions
  const currentPage = result?.currentPage ?? page
  const pages = result?.pages ?? currentPage
  const nextPage = result?.nextPage ?? (currentPage < pages ? currentPage + 1 : null)
  return {
    decisions: dropChildRows((result?.results ?? []).map(d => mapRevenueDecision(d))),
    currentPage,
    nextPage,
    pages,
    total: result?.total ?? 0,
    hasErrors: !!errors?.length,
  }
}

// ---------------------------------------------------------------------------
// Decision review drift — live build→now Wheelhouse drift for ONE decision.
// ---------------------------------------------------------------------------

/**
 * Which hashed Wheelhouse fields drifted between when a decision was built
 * and now. `checkFailed` is true when the live-WH read failed (drift could
 * not be determined — treat as unknown, not "no drift").
 */
export interface DecisionReviewDrift {
  drifted: boolean
  fields: string[]
  checkFailed: boolean
}

/**
 * Live build→now Wheelhouse drift for a single decision under review.
 *
 * Backs the non-blocking drift banner on the decision reviewer: the BE
 * recomputes the decision's `built_state_hash` against current Wheelhouse
 * state and reports which hashed fields changed. Drift is informational —
 * a human-reviewed decision is no longer hard-rejected on drift at execute
 * time; the reviewer sees this and decides.
 *
 * Fetched lazily, one decision at a time (one MCP read per decision opened)
 * — deliberately NOT folded into the bulk pending-decisions selection.
 *
 * Returns null when the decision is not in review (`pending_review`),
 * predates the build-hash pipeline, or the BE field is unavailable.
 */
export async function getDecisionReviewDrift(
  decisionId: string,
): Promise<DecisionReviewDrift | null> {
  // Cast until @avantstay/arriere-clients is regenerated against the rm-api
  // subgraph schema that exposes `decision(id).reviewDrift`.
  const queries = arriereBackoffice.queries as unknown as {
    revenueManagement: (selection: Record<string, unknown>) => Promise<{
      decision?: {
        reviewDrift?: {
          drifted?: boolean | null
          fields?: (string | null)[] | null
          checkFailed?: boolean | null
        } | null
      } | null
    }>
  }
  const result = await queries.revenueManagement({
    __alias: 'revenueAgentsDecisionReviewDrift',
    decision: {
      __args: { id: decisionId },
      reviewDrift: { drifted: true, fields: true, checkFailed: true },
    },
  })
  const d = result?.decision?.reviewDrift
  if (!d) return null
  return {
    drifted: d.drifted ?? false,
    fields: (d.fields ?? []).filter((f): f is string => !!f),
    checkFailed: d.checkFailed ?? false,
  }
}

/** One operator-visible change inside a drifted field: a rule or a calendar
 *  day whose value moved between when the decision was built and now. A null
 *  `before`/`after` means the rule/day was absent then/now (added/removed). */
export interface DecisionDriftChange {
  scope: string
  before: string | null
  after: string | null
}

/** Granular per-field drift — which rules / which days changed, old→new. */
export interface DecisionDriftFieldDetail {
  field: string
  label: string
  changes: DecisionDriftChange[]
}

export interface DecisionDriftPreflight {
  /** At least one guarded Wheelhouse or Voyage input changed since build. */
  drifted: boolean
  /** A live-state read failed — drift is unknown (treat as "could not determine"). */
  checkFailed: boolean
  /** Coarse drifted field names — fallback when `details` is empty (e.g. the
   *  row predates the build snapshot, so old→new can't be reconstructed). */
  fields: string[]
  /** Granular per-rule / per-day changes. Empty when there is no drift OR no
   *  build snapshot to diff against. */
  details: DecisionDriftFieldDetail[]
}

/**
 * Preflight a decision's live build→now drift right before approval. The
 * backend supports ordinary decisions, cohort children, and cohort parents;
 * parent checks aggregate their pending children.
 */
export async function getDecisionDriftPreflight(
  decisionId: string,
): Promise<DecisionDriftPreflight | null> {
  // Cast until @avantstay/arriere-clients is regenerated against the rm-api
  // subgraph schema that exposes `decisionDriftPreflight`.
  const queries = arriereBackoffice.queries as unknown as {
    revenueManagement: (selection: Record<string, unknown>) => Promise<{
      decisionDriftPreflight?: {
        drifted?: boolean | null
        fields?: (string | null)[] | null
        checkFailed?: boolean | null
        details?:
          | {
              field?: string | null
              label?: string | null
              changes?:
                | {
                    scope?: string | null
                    before?: string | null
                    after?: string | null
                  }[]
                | null
            }[]
          | null
      } | null
    }>
  }
  const result = await queries.revenueManagement({
    __alias: 'revenueAgentsDecisionDriftPreflight',
    decisionDriftPreflight: {
      __args: { id: decisionId },
      drifted: true,
      fields: true,
      checkFailed: true,
      details: {
        field: true,
        label: true,
        changes: { scope: true, before: true, after: true },
      },
    },
  })
  const preflight = result?.decisionDriftPreflight
  if (!preflight) return null
  const rawDetails = preflight.details ?? []
  const details: DecisionDriftFieldDetail[] = rawDetails.map(d => ({
    field: d?.field ?? '',
    label: d?.label ?? d?.field ?? '',
    changes: (d?.changes ?? []).map(c => ({
      scope: c?.scope ?? '',
      before: c?.before ?? null,
      after: c?.after ?? null,
    })),
  }))
  return {
    drifted: preflight.drifted ?? details.length > 0,
    checkFailed: preflight.checkFailed ?? false,
    fields: (preflight.fields ?? []).filter((f): f is string => !!f),
    details,
  }
}

export function combineDecisionDriftPreflights(
  results: DecisionDriftPreflight[],
): DecisionDriftPreflight | null {
  if (results.length === 0) return null

  const detailMap = new Map<string, DecisionDriftFieldDetail>()
  for (const result of results) {
    for (const detail of result.details) {
      const current = detailMap.get(detail.field)
      if (!current) {
        detailMap.set(detail.field, { ...detail, changes: [...detail.changes] })
        continue
      }
      for (const change of detail.changes) {
        if (
          !current.changes.some(
            existing =>
              existing.scope === change.scope &&
              existing.before === change.before &&
              existing.after === change.after,
          )
        ) {
          current.changes.push(change)
        }
      }
    }
  }

  return {
    drifted: results.some(result => result.drifted),
    checkFailed: results.some(result => result.checkFailed),
    fields: [...new Set(results.flatMap(result => result.fields))],
    details: [...detailMap.values()],
  }
}

export interface StaleDecision {
  id: string
  decisionType: string
  priority: 'high' | 'medium' | 'low'
  staleDays: number
  createdAt: string
  propertyId: string
  propertyName: string
  market: string
  image_url: string
}

const STALE_DECISION_FIELDS = {
  id: true,
  parentDecisionId: true,
  decisionType: true,
  priority: true,
  createdAt: true,
  property: { id: true, name: true, market: true, imageUrl: true },
} as const

// Every in-flight status, not only the reviewable queue behind `pendingDecisionsList`:
// a decision stuck in build, validation or a builder error is exactly what the
// dashboard should surface as needing attention.
// Scheduled work is deliberately excluded: it is already owned by the dispatcher.
const PENDING_LIKE_STATUSES: RevenueManagementAutomationDecisionStatus[] = [
  RevenueManagementAutomationDecisionStatus.pending,
  RevenueManagementAutomationDecisionStatus.pendingBuild,
  RevenueManagementAutomationDecisionStatus.pendingReview,
  RevenueManagementAutomationDecisionStatus.pendingValidation,
  RevenueManagementAutomationDecisionStatus.needsHumanPayload,
  RevenueManagementAutomationDecisionStatus.builderError,
]

const DAY_MS = 1000 * 60 * 60 * 24

/** The N oldest pending-like decisions for the dashboard "Needs Attention" section. */
export async function getStaleDecisions(
  limit = 5,
  filters: RevenueAgentsFilters = {},
): Promise<StaleDecision[]> {
  const result = await arriereBackoffice.queries.revenueManagement({
    __alias: 'revenueAgentsStaleDecisions',
    decisions: {
      __args: {
        statuses: PENDING_LIKE_STATUSES,
        analystId: filters.analystId || undefined,
      },
      ...STALE_DECISION_FIELDS,
      children: STALE_DECISION_FIELDS,
    },
  })
  // Drop child rows the API also returns at the top level — they ride along on
  // `parent.children`. A child whose parent isn't in the list stays.
  const rows = result.decisions ?? []
  const parentIds = new Set(rows.filter(row => !!row.children?.length).map(row => row.id))
  const now = Date.now()
  return rows
    .filter(d => !(d.parentDecisionId && parentIds.has(d.parentDecisionId)))
    .map((d): StaleDecision => {
      const children = d.children ?? []
      const head = children[0]
      const property = d.property ?? head?.property
      const createdAt = d.createdAt ? String(d.createdAt) : new Date().toISOString()
      const created = new Date(createdAt).getTime()
      const priority =
        d.priority ?? highestPriority(children.map(c => ({ priority: c.priority ?? '' }))) ?? 'low'
      return {
        id: d.id ?? '',
        decisionType: d.decisionType ?? head?.decisionType ?? '',
        priority: priority === 'high' || priority === 'medium' ? priority : 'low',
        staleDays: Number.isFinite(created) ? Math.max(0, Math.floor((now - created) / DAY_MS)) : 0,
        createdAt,
        propertyId: property?.id ?? '',
        propertyName: property?.name ?? '',
        market: property?.market ?? '',
        image_url: property?.imageUrl ?? '',
      }
    })
    .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime())
    .slice(0, limit)
}

// ---------------------------------------------------------------------------
// Feedback follow-ups — rejected decisions whose recovery follow-up is awaiting
// the reviewer's answer (status `pending`). Reviewer-scoped (defaults to the
// authenticated reviewer), NOT property-scoped. A lean, flat shape: the queue
// card only needs identity + property + the rejection annotations, so we
// deliberately avoid REVENUE_DECISION_SELECTION and its cohort/children
// machinery.
// ---------------------------------------------------------------------------

export interface FeedbackFollowup {
  id: string
  decisionType: string
  currentValue: string
  proposedValue: string
  confidence: number
  /** The reviewer's original rejection note (the thin reason being recovered). */
  reviewFeedback: string | null
  reviewFeedbackType: string | null
  reviewedAt: string | null
  /** pending | answered | resolved | dismissed | cancelled | null */
  feedbackFollowupStatus: string | null
  /** 0–1 quality score of the original rejection note. */
  feedbackSignalScore: number | null
  /** 0–1 re-score after the follow-up (the "lift"); null until resolved. */
  feedbackSignalScorePostFollowup: number | null
  /** Named 0–1 sub-scores that make up feedbackSignalScore (BE-defined keys). */
  feedbackSignalSubscores: Record<string, number> | null
  feedbackSignalCritique: string | null
  /** Distilled reviewer-facing questions — exactly what to answer. 0-3 items. */
  feedbackDirectQuestions: string[]
  /** Whether the rejected decision can be revived (approved as-is). */
  canReviveDecision: boolean
  /** Reason revive is blocked (when canReviveDecision is false) — for a tooltip. */
  reviveBlockedReason: string | null
  /** Responsible analyst (the BackOffice user who reviewed/owns the decision). */
  analystId: string | null
  analystName: string | null
  propertyId: string
  propertyName: string
  market: string
  image_url: string
  decisionScope: string
  feedbackFollowupCovers: FeedbackFollowupCoverRef[]
}

const FEEDBACK_FOLLOWUP_SELECTION = {
  id: true,
  decisionScope: true,
  decisionType: true,
  currentValue: true,
  proposedValue: true,
  confidence: true,
  reviewFeedback: true,
  reviewFeedbackType: true,
  reviewedAt: true,
  feedbackFollowupStatus: true,
  feedbackSignalScore: true,
  feedbackSignalScorePostFollowup: true,
  feedbackSignalSubscores: true,
  feedbackSignalCritique: true,
  feedbackDirectQuestions: true,
  canReviveDecision: true,
  reviveBlockedReason: true,
  feedbackFollowupCovers: {
    id: true,
    propertyName: true,
  },
  analyst: {
    id: true,
    name: true,
  },
  property: {
    id: true,
    name: true,
    market: true,
    imageUrl: true,
  },
} as const

type RawFeedbackFollowup = {
  id?: string | null
  decisionScope?: string | null
  decisionType?: string | null
  currentValue?: string | null
  proposedValue?: string | null
  confidence?: number | null
  reviewFeedback?: string | null
  reviewFeedbackType?: unknown
  reviewedAt?: unknown
  feedbackFollowupStatus?: string | null
  feedbackSignalScore?: number | null
  feedbackSignalScorePostFollowup?: number | null
  feedbackSignalSubscores?: unknown
  feedbackSignalCritique?: string | null
  feedbackDirectQuestions?: unknown
  canReviveDecision?: boolean | null
  reviveBlockedReason?: string | null
  feedbackFollowupCovers?: FeedbackFollowupCoverRef[] | null
  analyst?: { id?: string | null; name?: string | null } | null
  property?: {
    id?: string | null
    name?: string | null
    market?: string | null
    imageUrl?: string | null
  } | null
}

// feedbackSignalSubscores is typed JSON — either an already-parsed object or a
// JSON string. Normalize to a flat { key: number } map (dropping non-numeric
// entries) so the UI can render a breakdown without knowing the keys up front.
function parseSubscores(raw: unknown): Record<string, number> | null {
  let value = raw
  if (typeof value === 'string') {
    try {
      value = JSON.parse(value)
    } catch {
      return null
    }
  }
  if (!value || typeof value !== 'object') return null
  const out: Record<string, number> = {}
  for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
    if (typeof v === 'number' && Number.isFinite(v)) out[k] = v
  }
  return Object.keys(out).length > 0 ? out : null
}

function mapFeedbackFollowup(d: RawFeedbackFollowup): FeedbackFollowup {
  return {
    id: d.id ?? '',
    decisionType: d.decisionType ?? '',
    currentValue: d.currentValue ?? '',
    proposedValue: d.proposedValue ?? '',
    confidence: d.confidence ?? 0,
    reviewFeedback: d.reviewFeedback ?? null,
    // reviewFeedbackType is typed JSON in the schema — coerce the common
    // string-tag case, otherwise drop it.
    reviewFeedbackType: typeof d.reviewFeedbackType === 'string' ? d.reviewFeedbackType : null,
    reviewedAt: d.reviewedAt ? String(d.reviewedAt) : null,
    feedbackFollowupStatus: d.feedbackFollowupStatus ?? null,
    feedbackSignalScore: d.feedbackSignalScore ?? null,
    feedbackSignalScorePostFollowup: d.feedbackSignalScorePostFollowup ?? null,
    feedbackSignalSubscores: parseSubscores(d.feedbackSignalSubscores),
    feedbackSignalCritique: d.feedbackSignalCritique ?? null,
    feedbackDirectQuestions: Array.isArray(d.feedbackDirectQuestions)
      ? d.feedbackDirectQuestions.filter((q): q is string => typeof q === 'string')
      : [],
    canReviveDecision: d.canReviveDecision ?? false,
    reviveBlockedReason: d.reviveBlockedReason ?? null,
    analystId: d.analyst?.id ?? null,
    analystName: d.analyst?.name ?? null,
    propertyId: d.property?.id ?? '',
    propertyName: d.property?.name ?? '',
    market: d.property?.market ?? '',
    image_url: d.property?.imageUrl ?? '',
    decisionScope: d.decisionScope ?? '',
    feedbackFollowupCovers: d.feedbackFollowupCovers ?? [],
  }
}

/**
 * Pending feedback follow-ups — rejected decisions whose recovery follow-up is
 * awaiting an answer. The count of this list is the "N rejections need your
 * input" badge.
 *
 * The follow-up queue is COLLABORATIVE: with no filters this returns the whole
 * RM team's pending queue (any member can pick one up). Pass `propertyId` to
 * scope to a single property's pending follow-ups (the per-property badge);
 * pass `reviewerId` to scope to one reviewer's queue. Filters AND-combine.
 *
 * Uses the silent `.raw` endpoint so partial data survives subgraph errors
 * (same rationale as getPendingDecisionsList).
 */
export async function getPendingFeedbackFollowups(
  limit = 50,
  opts: { propertyId?: string; reviewerId?: string } = {},
): Promise<FeedbackFollowup[]> {
  const { data } = await arriereBackoffice.queries.revenueManagement.raw({
    __alias: 'revenueAgentsPendingFeedbackFollowups',
    pendingFeedbackFollowups: {
      __args: {
        limit,
        propertyId: opts.propertyId || undefined,
        reviewerId: opts.reviewerId || undefined,
      },
      ...FEEDBACK_FOLLOWUP_SELECTION,
    },
  })
  return (data?.pendingFeedbackFollowups ?? []).map(mapFeedbackFollowup)
}

/**
 * Dismiss a pending feedback follow-up. The backend enforces "only the
 * rejection's owner can dismiss"; a non-owner just gets `false`. Returns whether
 * it was dismissed so the caller can refresh the queue / surface failure.
 */
export async function dismissFeedbackFollowup(id: string): Promise<boolean> {
  const result = await arriereBackoffice.mutations.revenueManagement({
    __alias: 'dismissFeedbackFollowup',
    dismissFeedbackFollowup: {
      __args: { id },
    },
  })
  return result.dismissFeedbackFollowup ?? false
}

export interface MarketSummary {
  id: string
  market: string
  analyst: string | null
  properties: PropertyDetail[]
  pace: { pace_vs_ly_90: number; avg_occ_90: number } | null
  pendingCount: number
  lastCycleDate: string | null
  decisionsStats: {
    approved: number
    approvedWithChanges: number
    pending: number
    rejected: number
  }
  exceptionCount: number
  totalRevenue: number | null
  avgOccupancy: number | null
  avgRevPar: number | null
}

/**
 * Fetches everything the property page needs in a single revenueManagement call:
 * - Property core info (header card)
 * - Latest analysis cycles (with their decisions, for the Analysis Cycles section)
 * - All decisions (for the table, activity log, and rejection feedback)
 * - Global approval stats (Approval Rate card — overall / by type / by confidence)
 */
export const getPropertyDecisions = async (propertyId: string) => {
  return arriereBackoffice.queries.revenueManagement({
    __alias: 'propertyDecisions',
    property: {
      __args: { id: propertyId },
      id: true,
      name: true,
      market: true,
      marketId: true,
      address: true,
      imageUrl: true,
      totalRevenueLtm: true,
      avgOccupancy: true,
      revPar: true,
      analysisCycles: {
        id: true,
        cycleDate: true,
        analysisSummary: true,
        situationCategory: true,
        sessionStatus: true,
        sessionDurationSeconds: true,
        sessionCostUsd: true,
        sessionTokenCount: true,
        sessionModel: true,
        sessionError: true,
        createdAt: true,
        noActionNotes: { id: true, area: true, note: true },
        flagsForHuman: { id: true, flag: true, resolved: true, resolvedAt: true },
        investigationLog: { id: true, stepOrder: true, description: true },
        decisions: {
          scheduledDispatch: {
            notBefore: true,
            status: true,
            policyId: true,
            policyName: true,
            origin: true,
            action: true,
            reviewerName: true,
            driftPolicy: true,
          },
          id: true,
          decisionType: true,
          decisionScope: true,
          priority: true,
          confidence: true,
          status: true,
          currentSetting: true,
          currentValue: true,
          proposedSetting: true,
          proposedValue: true,
          reviewedAt: true,
          reviewedBy: true,
          reviewFeedback: true,
          reviewFeedbackType: true,
          decisionLifecycle: DECISION_LIFECYCLE_FIELDS,
          createdAt: true,
          updatedAt: true,
          // V22 revive fields — needed so the Revive button on rejected
          // decisions inside cycle cards reads canReviveDecision off the row.
          canReviveDecision: true,
          reviveBlockedReason: true,
          replacesDecisionId: true,
          // Same, for the Rollback button on approved decisions in those cards.
          canRollbackDecision: true,
          rollbackBlockedReason: true,
        },
      },
      decisions: {
        scheduledDispatch: {
          notBefore: true,
          status: true,
          policyId: true,
          policyName: true,
          origin: true,
          action: true,
          reviewerName: true,
          driftPolicy: true,
        },
        id: true,
        decisionType: true,
        decisionScope: true,
        priority: true,
        confidence: true,
        status: true,
        currentSetting: true,
        currentValue: true,
        proposedSetting: true,
        proposedValue: true,
        effectiveScope: true,
        effectiveStart: true,
        effectiveEnd: true,
        observation: true,
        diagnosis: true,
        actionRationale: true,
        expectedOutcome: true,
        riskFactors: true,
        constraintsChecked: true,
        builtPayloads: true,
        builtRollbacks: true,
        change: true,
        summary: true,
        summaryChanging: true,
        summaryContext: true,
        summaryOutcomes: true,
        summaryRisks: true,
        reviewedAt: true,
        reviewedBy: true,
        reviewFeedback: true,
        reviewFeedbackType: true,
        decisionLifecycle: DECISION_LIFECYCLE_FIELDS,
        createdAt: true,
        updatedAt: true,
        cycle: {
          id: true,
          cycleDate: true,
          situationCategory: true,
        },
        // V22 revive fields — the property page's main `decisions` list
        // includes rejected rows; this is where the Revive button needs
        // its enablement signal and the lineage badge needs its source id.
        canReviveDecision: true,
        reviveBlockedReason: true,
        replacesDecisionId: true,
        // …and approved rows, where the Rollback button needs the same signal.
        canRollbackDecision: true,
        rollbackBlockedReason: true,
      },
    },
  })
}

// ---------------------------------------------------------------------------
// Property pending decisions — chatbot-lean variant of `getPropertyDecisions`.
// The AgentChatbot on the Pricing Tool page uses this to seed its thought
// bubble and route chat messages to the right decision id.
// ---------------------------------------------------------------------------

const PROPERTY_DECISION_FIELDS = {
  id: true,
  decisionType: true,
  priority: true,
  confidence: true,
  status: true,
  currentSetting: true,
  currentValue: true,
  proposedSetting: true,
  proposedValue: true,
  effectiveScope: true,
  effectiveStart: true,
  effectiveEnd: true,
  observation: true,
  diagnosis: true,
  actionRationale: true,
  expectedOutcome: true,
  riskFactors: true,
  constraintsChecked: true,
  change: true,
  summaryChanging: true,
  summaryContext: true,
  summaryOutcomes: true,
  summaryRisks: true,
  reviewedAt: true,
  reviewedBy: true,
  reviewFeedback: true,
  reviewFeedbackType: true,
  decisionLifecycle: DECISION_LIFECYCLE_FIELDS,
  createdAt: true,
  updatedAt: true,
  cycle: { id: true, cycleDate: true, situationCategory: true },
  // Queue metadata needs no Wheelhouse read and is safe on the property page.
  scheduledDispatch: {
    origin: true,
    action: true,
    reviewerName: true,
    driftPolicy: true,
    notBefore: true,
    status: true,
    policyId: true,
    policyName: true,
  },
} as const

// Per-unit rows of a cohort parent. No revive fields — see the cost note below.
const PROPERTY_DECISION_CHILD_SELECTION = {
  ...PROPERTY_DECISION_FIELDS,
  property: { id: true },
} as const

// V22 revive fields. `canReviveDecision` does a live Wheelhouse read per
// unique property in the response — only include this selection on
// screens that actually render the Revive control.
const PROPERTY_DECISION_SELECTION = {
  ...PROPERTY_DECISION_FIELDS,
  canReviveDecision: true,
  reviveBlockedReason: true,
  replacesDecisionId: true,
  cohortId: true,
  cohortType: true,
  cohortMode: true,
  totalUnits: true,
  decisionScope: true,
  parentDecisionId: true,
  children: PROPERTY_DECISION_CHILD_SELECTION,
} as const

type RawPropertyDecision = {
  scheduledDispatch?: {
    origin?: string | null
    action?: string | null
    reviewerName?: string | null
    driftPolicy?: string | null
    notBefore?: unknown
    status?: string | null
    policyId?: string | null
    policyName?: string | null
  } | null
  id?: string | null
  decisionType?: string | null
  priority?: string | null
  confidence?: number | null
  status?: string | null
  currentSetting?: string | null
  currentValue?: string | null
  proposedSetting?: string | null
  proposedValue?: string | null
  effectiveScope?: string | null
  effectiveStart?: unknown
  effectiveEnd?: unknown
  observation?: string | null
  diagnosis?: string | null
  actionRationale?: string | null
  expectedOutcome?: string | null
  riskFactors?: string | null
  constraintsChecked?: string | null
  change?: unknown
  summaryChanging?: (string | null)[] | null
  summaryContext?: (string | null)[] | null
  summaryOutcomes?: (string | null)[] | null
  summaryRisks?: (string | null)[] | null
  reviewedAt?: unknown
  reviewedBy?: string | null
  reviewFeedback?: string | null
  reviewFeedbackType?: string | string[] | null
  decisionLifecycle?: RawDecisionLifecycleEvent[] | null
  createdAt?: unknown
  updatedAt?: unknown
  cycle?: { id?: string | null; cycleDate?: unknown; situationCategory?: string | null } | null
  // V22 — only present on responses where REVIVE_FIELDS_SELECTION was
  // spread into the selection. Absent on older selections — the mapper
  // defaults each to its safe value.
  canReviveDecision?: boolean | null
  reviveBlockedReason?: string | null
  replacesDecisionId?: string | null
  cohortId?: string | null
  cohortType?: RevenueManagementAutomationCohortType | null
  cohortMode?: RevenueManagementAutomationCohortMode | null
  totalUnits?: number | null
  decisionScope?: string | null
  parentDecisionId?: string | null
  children?: RawPropertyDecision[] | null
  property?: { id?: string | null } | null
}

function normalizeFeedbackTypes(raw: string | string[] | null | undefined): string[] {
  if (Array.isArray(raw)) return raw.filter(Boolean)
  if (typeof raw === 'string' && raw) return [raw]
  return []
}

// A cohort parent's classification lives on its children, so fall back to them.
function mapPropertyDecision(d: RawPropertyDecision, propertyId: string): Decision {
  const children = (d.children ?? []).map(c => mapPropertyDecision(c, c.property?.id ?? propertyId))
  const head = children.find(c => c.property_id === propertyId) ?? children[0]
  const avgChildConfidence = children.length
    ? children.reduce((sum, c) => sum + c.confidence, 0) / children.length
    : null

  return {
    id: d.id ?? '',
    cycle_id: d.cycle?.id ?? head?.cycle_id ?? '',
    property_id: d.property?.id ?? propertyId,
    decision_type: d.decisionType ?? head?.decision_type ?? '',
    priority: d.priority ?? highestPriority(children) ?? 'low',
    confidence: d.confidence ?? avgChildConfidence ?? 0,
    status: (d.status ?? 'pending') as DecisionStatus,
    current_setting: d.currentSetting ?? '',
    current_value: d.currentValue ?? '',
    proposed_setting: d.proposedSetting ?? '',
    proposed_value: d.proposedValue ?? '',
    effective_scope: d.effectiveScope ?? '',
    effective_start: d.effectiveStart ? String(d.effectiveStart) : null,
    effective_end: d.effectiveEnd ? String(d.effectiveEnd) : null,
    observation: d.observation ?? '',
    diagnosis: d.diagnosis ?? '',
    action_rationale: d.actionRationale ?? '',
    expected_outcome: d.expectedOutcome ?? '',
    risk_factors: d.riskFactors ?? '',
    constraints_checked: d.constraintsChecked ?? '',
    built_payloads: '',
    built_rollbacks: '',
    change: d.change ?? null,
    reviewed_at: d.reviewedAt ? String(d.reviewedAt) : null,
    reviewed_by: d.reviewedBy ?? null,
    review_feedback: d.reviewFeedback ?? null,
    review_feedback_types: normalizeFeedbackTypes(d.reviewFeedbackType),
    decision_lifecycle: (d.decisionLifecycle ?? []).map(mapDecisionLifecycleEvent),
    scheduled_dispatch: mapScheduledDispatch(d.scheduledDispatch),
    final_version_id: null,
    review_session_id: null,
    created_at: d.createdAt ? String(d.createdAt) : new Date().toISOString(),
    updated_at: d.updatedAt ? String(d.updatedAt) : new Date().toISOString(),
    cycle_date: d.cycle?.cycleDate ? String(d.cycle.cycleDate) : head?.cycle_date,
    situation_category: d.cycle?.situationCategory ?? head?.situation_category,
    summary_changing: compactStrings(d.summaryChanging),
    summary_context: compactStrings(d.summaryContext),
    summary_outcomes: compactStrings(d.summaryOutcomes),
    summary_risks: compactStrings(d.summaryRisks),
    can_revive_decision: d.canReviveDecision ?? false,
    revive_blocked_reason: d.reviveBlockedReason ?? null,
    replaces_decision_id: d.replacesDecisionId ?? null,
    // Hard-stubbed, not selected: this is the chatbot-lean PENDING list, and a
    // pending decision is never rollbackable (`not_approved`). Selecting
    // canRollbackDecision here would buy a COUNT per row for a guaranteed false.
    can_rollback_decision: false,
    rollback_blocked_reason: null,
    cohort_id: d.cohortId ?? null,
    cohort_type: normalizeCohortType(d.cohortType),
    cohort_mode: normalizeCohortMode(d.cohortMode),
    unit_count: d.totalUnits ?? null,
    decision_scope: d.decisionScope ?? '',
    parent_decision_id: d.parentDecisionId ?? null,
    children,
  }
}

const PROPERTY_PENDING_PAGE_SIZE = 100

/**
 * Returns the pending decisions for a given property, mapped into the shared
 * `Decision` shape. Used by the Pricing Tool's AgentChatbot.
 *
 * Reads every page of the paginated query so the pending-status whitelist is
 * applied server-side instead of client-filtering on a single literal status.
 */
export async function getPropertyPendingDecisions(propertyId: string): Promise<Decision[]> {
  const fetchPage = (page: number) =>
    arriereBackoffice.queries.revenueManagement({
      __alias: 'revenueAgentsPropertyPendingDecisions',
      pendingDecisionsPage: {
        __args: { propertyId, pagination: { page, size: PROPERTY_PENDING_PAGE_SIZE } },
        pages: true,
        results: PROPERTY_DECISION_SELECTION,
      },
    })

  const first = await fetchPage(1)
  const pages = first.pendingDecisionsPage?.pages ?? 1
  const rest = await Promise.all(
    Array.from({ length: Math.max(0, pages - 1) }, (_, i) => fetchPage(i + 2)),
  )
  const decisions = [first, ...rest]
    .flatMap(page => page.pendingDecisionsPage?.results ?? [])
    .map(d => mapPropertyDecision(d as RawPropertyDecision, propertyId))
  // Drop child rows the API also returns at the top level — they ride along on
  // `parent.children`. A child whose parent isn't in the list stays.
  const parentIds = new Set(decisions.filter(d => !!d.children?.length).map(d => d.id))
  return decisions.filter(d => !(d.parent_decision_id && parentIds.has(d.parent_decision_id)))
}

/**
 * Returns just the pending-decisions count for a property using the backend
 * aggregate `property.decisionCounts.totalPending`, avoiding the full
 * decisions payload that `getPropertyPendingDecisions` fetches.
 */
export async function getPropertyPendingDecisionsCount(propertyId: string): Promise<number> {
  const result = await arriereBackoffice.queries.revenueManagement({
    __alias: 'revenueAgentsPropertyPendingDecisionsCount',
    property: {
      __args: { id: propertyId },
      decisionCounts: {
        totalPending: true,
      },
    },
  })
  return result.property?.decisionCounts?.totalPending ?? 0
}

/**
 * Loads the full markets overview snapshot (every market with its aggregated
 * stats + decision counts + nested properties) plus global totals in a single
 * revenueManagement call. Powers the dashboard markets table and overview
 * cards without further per-market round-trips.
 */
export async function getMarketsOverview(filters: RevenueAgentsFilters = {}) {
  const result = await arriereBackoffice.queries.revenueManagement({
    __alias: 'revenueAgentsMarketsOverview',
    marketsOverview: {
      __args: {
        analystId: filters.analystId || undefined,
      },
      markets: {
        marketId: true,
        canonicalMarketId: true,
        marketName: true,
        propertyCount: true,
        ltmRevenue: true,
        avgRevpar: true,
        lastCycleAt: true,
        decisionCounts: {
          approved: true,
          approvedWithChanges: true,
          pending: true,
          pendingBuild: true,
          pendingReview: true,
          pendingValidation: true,
          needsHumanPayload: true,
          builderError: true,
          rejected: true,
          rejectedGuardrail: true,
          rejectedStale: true,
          unresolvable: true,
          total: true,
          totalPending: true,
          totalRejected: true,
        },
        properties: {
          id: true,
          name: true,
          market: true,
          marketId: true,
          address: true,
          imageUrl: true,
          totalRevenueLtm: true,
          avgOccupancy: true,
          revPar: true,
          lastCycleAt: true,
          decisionCounts: {
            approved: true,
            pending: true,
            pendingBuild: true,
            pendingReview: true,
            pendingValidation: true,
            needsHumanPayload: true,
            builderError: true,
            rejected: true,
            rejectedGuardrail: true,
            rejectedStale: true,
            unresolvable: true,
            total: true,
            totalPending: true,
            totalRejected: true,
          },
        },
      },
      totals: {
        marketCount: true,
        propertyCount: true,
        decisionCounts: {
          approved: true,
          approvedWithChanges: true,
          pending: true,
          pendingBuild: true,
          pendingReview: true,
          pendingValidation: true,
          needsHumanPayload: true,
          builderError: true,
          rejected: true,
          rejectedGuardrail: true,
          rejectedStale: true,
          unresolvable: true,
          total: true,
          totalPending: true,
          totalRejected: true,
        },
      },
    },
  })

  return result.marketsOverview ?? { markets: [], totals: null }
}

export type MarketsOverview = Awaited<ReturnType<typeof getMarketsOverview>>

// Slim variant of `getMarketsOverview` selecting only the pending-decision count per property.
export async function getPendingDecisionCountsOverview() {
  const result = await arriereBackoffice.queries.revenueManagement({
    __alias: 'pricingToolPendingDecisionCounts',
    marketsOverview: {
      markets: {
        properties: {
          id: true,
          decisionCounts: {
            totalPending: true,
          },
        },
      },
    },
  })

  return result.marketsOverview ?? { markets: [] }
}

export type PendingDecisionCountsOverview = Awaited<
  ReturnType<typeof getPendingDecisionCountsOverview>
>

// ---------------------------------------------------------------------------
// Active markets list — groups active homes by region. Downstream, this feed
// is the source for property thumbnails + the fallback market list when
// `marketsOverview` has no data. We only populate identity fields here and
// leave numeric fields at zero defaults; pending counts are overlaid from
// `pendingDecisions` in RevenueAgentsDataStore.
// ---------------------------------------------------------------------------

interface HomeAddressParts {
  address?: string | null
  city?: string | null
  zipCode?: string | null
  state?: { name?: string | null } | null
}

function composeAddressLine(address: HomeAddressParts | null | undefined): string {
  if (!address) return ''
  return [address.address, address.city, address.state?.name, address.zipCode]
    .filter(Boolean)
    .join(' ･ ')
}

function emptyProperty(h: {
  id: string
  name: string
  region?: { id?: string | null; name?: string | null } | null
  address?: HomeAddressParts | null
  thumbnail?: { url?: string | null } | null
}): PropertyDetail {
  return {
    id: h.id,
    voyagePropertyId: h.id,
    name: h.name,
    market: h.region?.name ?? '',
    marketId: h.region?.id ?? null,
    address: composeAddressLine(h.address),
    image_url: h.thumbnail?.url ?? '',
    total_revenue_ltm: 0,
    avg_occupancy: 0,
    rev_par: 0,
    analyst: null,
    pending_decisions: [],
    decisionsStats: { approved: 0, approvedWithChanges: 0, pending: 0, rejected: 0 },
    lastCycleDate: null,
  }
}

export async function getActiveMarkets(): Promise<MarketSummary[]> {
  const result = await arriereBackoffice.queries.homes({
    __alias: 'revenueAgentHomes',
    __args: { onlyActive: true, pagination: { page: 1, size: 9000 } },
    results: {
      id: true,
      name: true,
      thumbnail: { url: true },
      region: { id: true, name: true },
      address: { address: true, city: true, zipCode: true, state: { name: true } },
    },
  })

  const byRegion = new Map<string, typeof result.results>()
  for (const h of result.results) {
    const regionId = h.region?.id ?? 'unknown'
    if (!byRegion.has(regionId)) byRegion.set(regionId, [])
    byRegion.get(regionId)!.push(h)
  }

  return Array.from(byRegion.entries()).map(([regionId, homes]) => ({
    id: regionId,
    market: homes[0].region?.name ?? regionId,
    analyst: null,
    properties: homes.map(emptyProperty),
    pace: null,
    pendingCount: 0,
    lastCycleDate: null,
    decisionsStats: { approved: 0, approvedWithChanges: 0, pending: 0, rejected: 0 },
    exceptionCount: 0,
    totalRevenue: null,
    avgOccupancy: null,
    avgRevPar: null,
  }))
}

export interface PropertyCard {
  id: string
  name: string
  imageUrl: string
}

/** Batch lookup by id, including offboarded homes that `getActiveMarkets` omits. */
export async function getPropertyCards(homeIds: string[]): Promise<PropertyCard[]> {
  if (homeIds.length === 0) return []
  const result = await arriereBackoffice.queries.homes({
    __alias: 'revenueAgentsPropertyCards',
    __args: {
      homeIds,
      withOffboarded: true,
      pagination: { page: 1, size: homeIds.length },
    },
    results: {
      id: true,
      name: true,
      thumbnail: { url: true },
    },
  })

  return (result.results ?? []).map(home => ({
    id: home.id,
    name: home.name,
    imageUrl: home.thumbnail?.url ?? '',
  }))
}

export interface RevenueAnalyst {
  id: string
  name: string
}

export async function getRevenueAnalysts(): Promise<RevenueAnalyst[]> {
  const analysts = await revenueTicketsModule.getTicketDomainAnalysts('RM')
  return analysts.map(({ id, name }) => ({ id, name }))
}

// Analyst activity history, summaries, and recorded event details.

export type ActivityFilters = NonNullable<
  NonNullable<RevenueManagementQueriesSelection['analystActivity']>['__args']
>

const ANALYST_ACTIVITY_ROW_FIELDS = {
  id: true,
  occurredAt: true,
  actorId: true,
  actorName: true,
  actorKnown: true,
  actorSurface: true,
  actionType: true,
  reason: true,
  decisionId: true,
  resultDecisionId: true,
  parentDecisionId: true,
  decisionSummary: true,
  summaryIsHistorical: true,
  historicalDetailsComplete: true,
  legacyUngrouped: true,
  queued: true,
  requestOnly: true,
  affectedMemberCount: true,
} as const

const ANALYST_ACTIVITY_CURRENT_DECISION_FIELDS = {
  id: true,
  status: true,
  proposedValue: true,
  executionTasks: { id: true, status: true, executionError: true },
  decisionLifecycle: {
    id: true,
    eventType: true,
    occurredAt: true,
    reason: true,
    eventPayload: true,
  },
} as const

export async function getActivityIdentity(analystId: string) {
  const { data, errors } = await arriereBackoffice.queries.ticketDomain.raw({
    __alias: 'analystActivityIdentity',
    __args: { code: 'RM' },
    assigneeOptions: { id: true, name: true, user: { id: true } },
  })
  if (errors?.length || !data) throw new Error('Unable to load analyst identity')
  const analyst = data.assigneeOptions?.find(option => option.id === analystId)
  // The portfolio assignee ID is not an activity actor ID. Use the explicit account link.
  return {
    actorId: analyst?.user?.id ?? null,
    name: analyst?.name ?? 'Selected analyst',
  }
}

export async function getAnalystActivity(filters: ActivityFilters) {
  const { data, errors } = await arriereBackoffice.queries.revenueManagement.raw({
    __alias: 'analystActivityHistory',
    analystActivity: {
      __args: filters,
      total: true,
      pages: true,
      currentPage: true,
      nextPage: true,
      rows: ANALYST_ACTIVITY_ROW_FIELDS,
    },
  })
  if (errors?.length || !data?.analystActivity) throw new Error('Unable to load analyst activity')
  return data.analystActivity
}

export type ActivityPage = Awaited<ReturnType<typeof getAnalystActivity>>
export type ActivityRow = NonNullable<ActivityPage['rows']>[number]

export async function getActivitySummary(actorId: string, occurredFrom?: string) {
  return Promise.all(
    Object.values(RevenueManagementAnalystActionType).map(async actionType => {
      const page = await getAnalystActivity({
        actorIds: [actorId],
        actionTypes: [actionType],
        occurredFrom,
        pagination: { page: 1, size: 1 },
        sortOrder: RevenueManagementAutomationSortOrder.desc,
      })
      return { actionType, total: page.total ?? 0, latest: page.rows?.[0] }
    }),
  )
}

export async function getActivityDetail(row: ActivityRow, memberPage: number) {
  // Pin the event's target and timestamp so new activity cannot shift its detail lookup.
  // Multiple actions can share a transaction timestamp, so still match the exact event ID.
  let page = 1
  while (true) {
    const { data, errors } = await arriereBackoffice.queries.revenueManagement.raw({
      __alias: 'analystActivityDetail',
      analystActivity: {
        __args: {
          decisionId: row.decisionId,
          actionTypes: row.actionType ? [row.actionType] : undefined,
          occurredFrom: row.occurredAt,
          occurredTo: row.occurredAt
            ? new Date(new Date(row.occurredAt).getTime() + 1).toISOString()
            : undefined,
          pagination: { page, size: 100 },
        },
        nextPage: true,
        rows: {
          id: true,
          detail: { before: true, after: true, actionDetail: true },
          currentDecision: ANALYST_ACTIVITY_CURRENT_DECISION_FIELDS,
          affectedMembers: {
            __args: { pagination: { page: memberPage, size: 10 } },
            total: true,
            pages: true,
            rows: {
              decisionId: true,
              resultDecisionId: true,
              before: true,
              after: true,
              currentDecision: ANALYST_ACTIVITY_CURRENT_DECISION_FIELDS,
            },
          },
        },
      },
    })
    if (errors?.length || !data?.analystActivity) throw new Error('Unable to load activity details')
    const found = data.analystActivity.rows?.find(entry => entry.id === row.id)
    if (found) return found
    if (!data.analystActivity.nextPage) return null
    page = data.analystActivity.nextPage
  }
}

export type ActivityDetail = NonNullable<Awaited<ReturnType<typeof getActivityDetail>>>

export interface RevenueAgentsFilters {
  analystId?: string
  marketId?: string
  decisionType?: RevenueManagementAutomationDecisionType
  sinceDays?: number
}

/**
 * Loads the aggregated Learning-page analytics (overview totals, weekly
 * approval trend, per-decision-type breakdown, feedback distribution, and
 * recent rejections) from the revenue management API in one call. The
 * response shape is kept close to the GraphQL schema; `mapLearningData`
 * reshapes it into the `LearningStats` shape consumed by `LearningView`.
 */
export async function getLearningData(filters: RevenueAgentsFilters = {}) {
  const result = await arriereBackoffice.queries.revenueManagement({
    __alias: 'revenueAgentsLearningData',
    learningData: {
      __args: {
        analystId: filters.analystId || undefined,
        marketId: filters.marketId || undefined,
        decisionType: filters.decisionType ? [filters.decisionType] : undefined,
        sinceDays: filters.sinceDays || undefined,
      },
      overview: {
        total: true,
        approved: true,
        approvedWithChanges: true,
        rejected: true,
        pending: true,
      },
      rejectionsInPeriod: true,
      promotionReadyCount: true,
      byDecisionType: {
        decisionType: true,
        total: true,
        approved: true,
        approvedWithChanges: true,
        rejected: true,
        approvalRate: true,
        avgConfidence: true,
        commonFeedbackType: true,
      },
      feedbackDist: {
        reviewFeedbackType: true,
        count: true,
      },
      byProperty: {
        propertyId: true,
        propertyName: true,
        market: true,
        total: true,
        approved: true,
        approvedWithChanges: true,
        rejected: true,
        approvalRate: true,
        commonFeedbackType: true,
        rejectionCount: true,
      },
    },
  })

  return result.learningData ?? null
}

export type LearningDataResponse = Awaited<ReturnType<typeof getLearningData>>

/**
 * Loads only the weekly approval trend for the Learning page. The backend
 * always returns the last 8 weeks regardless of `sinceDays`, so this is keyed
 * on the scope filters (analyst, market, decision type) and stays untouched
 * when the period selector changes.
 */
export async function getLearningWeeklyTrend(filters: RevenueAgentsFilters = {}) {
  const result = await arriereBackoffice.queries.revenueManagement({
    __alias: 'revenueAgentsLearningWeeklyTrend',
    learningData: {
      __args: {
        analystId: filters.analystId || undefined,
        marketId: filters.marketId || undefined,
        decisionType: filters.decisionType ? [filters.decisionType] : undefined,
      },
      weeklyTrend: {
        label: true,
        rate: true,
        total: true,
        approved: true,
        approvedWithChanges: true,
        rejected: true,
      },
    },
  })

  return result.learningData?.weeklyTrend ?? null
}

export type LearningWeeklyTrendResponse = Awaited<ReturnType<typeof getLearningWeeklyTrend>>

export const WORKFLOW_REJECTION_NOTES_PAGE_SIZE = 10

export type WorkflowRunOutcome = `${RevenueManagementWorkflowRunOutcome}`

export interface WorkflowDecisionOutcome {
  workflowId: string
  workflowName: string
  market: string
  weekStart: string
  aiJudgment: boolean
  created: number
  approved: number
  partial: number
  rejected: number
  pending: number
}

export interface WorkflowRejectionReason {
  workflowId: string
  workflowName: string
  market: string
  weekStart: string
  aiJudgment: boolean
  reason: string
  count: number
}

export interface WorkflowRejectionNote {
  id: string
  workflowId: string
  workflowName: string
  market: string
  aiJudgment: boolean
  reasons: string[]
  note: string | null
  reviewer: string | null
  rejectedAt: string | null
  createdAt: string | null
  propertyName: string | null
  homes: number | null
}

export interface WorkflowRejectionNotesQuery {
  sinceWeeks: number
  market: string | null
  workflowIds: string[] | null
  reason: string | null
}

export interface WorkflowRejectionNotesPage {
  results: WorkflowRejectionNote[]
  hasMore: boolean
}

export interface WorkflowTimelineRun {
  id: string
  runAt: string
  scheduledFor: string | null
  triggeredBy: string
  status: WorkflowRunStatus
  outcome: WorkflowRunOutcome
  attempt: number
  failureKind: string | null
  error: string | null
  cohortSize: number | null
  decisions: number
  homes: number
  pendingReview: number
  approved: number
  autoApproved: number
  rejected: number
  partial: number
}

export interface WorkflowTimelineWorkflow {
  workflowId: string
  name: string
  status: WorkflowStatus
  adjustmentType: WorkflowAdjustmentType | null
  market: string | null
  routing: WorkflowRouting | null
  runs: WorkflowTimelineRun[]
  upcoming: string[]
}

export interface WorkflowTimelineQuery {
  from: Date
  to: Date
  analystId?: string | null
  marketId?: string | null
}

function toIso(value: unknown): string | null {
  if (value == null) return null
  const date = new Date(value as string | Date)
  return Number.isNaN(date.getTime()) ? null : date.toISOString()
}

const toWeekStart = (value: unknown) => toIso(value)?.slice(0, 10) ?? null

export async function getWorkflowDecisionOutcomes(
  sinceWeeks: number,
): Promise<WorkflowDecisionOutcome[]> {
  const result = await arriereBackoffice.queries.revenueManagement({
    __alias: 'revenueAgentsWorkflowDecisionOutcomes',
    workflowDecisionOutcomes: {
      __args: { sinceWeeks },
      workflowId: true,
      workflowName: true,
      market: true,
      weekStart: true,
      aiJudgment: true,
      created: true,
      approved: true,
      partial: true,
      rejected: true,
      pending: true,
    },
  })

  return (result.workflowDecisionOutcomes ?? []).flatMap(row => {
    const weekStart = toWeekStart(row?.weekStart)
    if (!row?.workflowId || !weekStart) return []
    return [
      {
        workflowId: row.workflowId,
        workflowName: row.workflowName?.trim() || 'Untitled workflow',
        market: row.market?.trim() ?? '',
        weekStart,
        aiJudgment: row.aiJudgment === true,
        created: row.created ?? 0,
        approved: row.approved ?? 0,
        partial: row.partial ?? 0,
        rejected: row.rejected ?? 0,
        pending: row.pending ?? 0,
      },
    ]
  })
}

export async function getWorkflowRejectionReasons(
  sinceWeeks: number,
): Promise<WorkflowRejectionReason[]> {
  const result = await arriereBackoffice.queries.revenueManagement({
    __alias: 'revenueAgentsWorkflowRejectionReasons',
    workflowRejectionReasons: {
      __args: { sinceWeeks },
      workflowId: true,
      workflowName: true,
      market: true,
      weekStart: true,
      aiJudgment: true,
      reason: true,
      count: true,
    },
  })

  return (result.workflowRejectionReasons ?? []).flatMap(row => {
    const weekStart = toWeekStart(row?.weekStart)
    if (!row?.workflowId || !row.reason || !weekStart) return []
    return [
      {
        workflowId: row.workflowId,
        workflowName: row.workflowName?.trim() || 'Untitled workflow',
        market: row.market?.trim() ?? '',
        weekStart,
        aiJudgment: row.aiJudgment === true,
        reason: row.reason,
        count: row.count ?? 0,
      },
    ]
  })
}

export async function getWorkflowRejectionNotes(
  query: WorkflowRejectionNotesQuery,
  page: number,
): Promise<WorkflowRejectionNotesPage> {
  const result = await arriereBackoffice.queries.revenueManagement({
    __alias: 'revenueAgentsWorkflowRejectionNotes',
    workflowRejectionNotes: {
      __args: {
        sinceWeeks: query.sinceWeeks,
        workflowIds: query.workflowIds ?? undefined,
        reason: query.reason ?? undefined,
        market: query.market ?? undefined,
        pagination: { page, size: WORKFLOW_REJECTION_NOTES_PAGE_SIZE },
      },
      results: {
        decisionId: true,
        workflowId: true,
        workflowName: true,
        market: true,
        aiJudgment: true,
        reasons: true,
        note: true,
        reviewer: true,
        rejectedAt: true,
        createdAt: true,
        propertyName: true,
        homes: true,
      },
      hasMore: true,
    },
  })

  const notes = result.workflowRejectionNotes
  return {
    results: (notes?.results ?? []).flatMap(note => {
      if (!note?.decisionId || !note.workflowId) return []
      return [
        {
          id: note.decisionId,
          workflowId: note.workflowId,
          workflowName: note.workflowName?.trim() || 'Untitled workflow',
          market: note.market?.trim() ?? '',
          aiJudgment: note.aiJudgment === true,
          reasons: note.reasons ?? [],
          note: note.note?.trim() || null,
          reviewer: note.reviewer ?? null,
          rejectedAt: toIso(note.rejectedAt),
          createdAt: toIso(note.createdAt),
          propertyName: note.propertyName ?? null,
          homes: note.homes ?? null,
        },
      ]
    }),
    hasMore: notes?.hasMore ?? false,
  }
}

export async function getWorkflowExecutionTimeline({
  from,
  to,
  analystId,
  marketId,
}: WorkflowTimelineQuery): Promise<WorkflowTimelineWorkflow[]> {
  const result = await arriereBackoffice.queries.revenueManagement({
    __alias: 'revenueAgentsWorkflowExecutionTimeline',
    workflowExecutionTimeline: {
      __args: {
        from: from.toISOString(),
        to: to.toISOString(),
        analystId: analystId ?? undefined,
        marketId: marketId ?? undefined,
      },
      workflows: {
        workflowId: true,
        name: true,
        status: true,
        adjustmentType: true,
        market: true,
        routing: true,
        upcoming: true,
        runs: {
          id: true,
          runAt: true,
          scheduledFor: true,
          triggeredBy: true,
          status: true,
          outcome: true,
          attempt: true,
          failureKind: true,
          error: true,
          cohortSize: true,
          decisions: true,
          homes: true,
          pendingReview: true,
          approved: true,
          autoApproved: true,
          rejected: true,
          partial: true,
        },
      },
    },
  })

  return (result.workflowExecutionTimeline?.workflows ?? []).flatMap(workflow => {
    if (!workflow?.workflowId || !workflow.status) return []
    return [
      {
        workflowId: workflow.workflowId,
        name: workflow.name?.trim() || 'Untitled workflow',
        status: workflow.status as WorkflowStatus,
        adjustmentType: (workflow.adjustmentType ?? null) as WorkflowAdjustmentType | null,
        market: workflow.market ?? null,
        routing: (workflow.routing ?? null) as WorkflowRouting | null,
        upcoming: (workflow.upcoming ?? []).flatMap(fire => toIso(fire) ?? []),
        runs: (workflow.runs ?? []).flatMap(run => {
          const runAt = toIso(run?.runAt)
          if (!run?.id || !runAt || !run.status || !run.outcome) return []
          return [
            {
              id: run.id,
              runAt,
              scheduledFor: toIso(run.scheduledFor),
              triggeredBy: run.triggeredBy ?? '',
              status: run.status as WorkflowRunStatus,
              outcome: run.outcome as WorkflowRunOutcome,
              attempt: run.attempt ?? 1,
              failureKind: run.failureKind ?? null,
              error: run.error ?? null,
              cohortSize: run.cohortSize ?? null,
              decisions: run.decisions ?? 0,
              homes: run.homes ?? 0,
              pendingReview: run.pendingReview ?? 0,
              approved: run.approved ?? 0,
              autoApproved: run.autoApproved ?? 0,
              rejected: run.rejected ?? 0,
              partial: run.partial ?? 0,
            },
          ]
        }),
      },
    ]
  })
}

export interface AutoExecutionCalendarEntry {
  id: string
  kind: AutoExecutionCalendarEntryKind
  at: string
  runAt: string | null
  isEstimate: boolean
  timeBasis: AutoExecutionCalendarTimeBasis | null
  mode: AutoExecutionCalendarMode | null
  modeReason: AutoExecutionCalendarModeReason | null
  workflowId: string | null
  workflowName: string | null
  adjustmentType: WorkflowAdjustmentType | null
  policyName: string | null
  memberCount: number | null
  memberSource: AutoExecutionCalendarMemberSource | null
  decisionId: string | null
  status: AutoExecutionCalendarQueueStatus | null
  action: AutoExecutionDispatchAction | null
  propertyName: string | null
  magnitudeSummary: string | null
  estRevenueDelta: number | null
  runInProgress: boolean
  lastRunProducedDecisions: boolean | null
  autoExecution: AutoExecutionState | null
}

interface AutoExecutionCalendarQuery {
  periodFrom: Date
  periodTo: Date
  analystId?: string | null
  marketId?: string | null
  autoExecutingOnly: boolean
}

export const AUTO_EXECUTION_MODE_LABELS: Record<AutoExecutionCalendarMode, string> = {
  [AutoExecutionCalendarMode.auto]: 'Executes automatically',
  [AutoExecutionCalendarMode.conditional]: 'Partially automatic',
  [AutoExecutionCalendarMode.review]: 'Needs reviewer approval',
}

export const AUTO_EXECUTION_MODE_REASONS: Record<AutoExecutionCalendarModeReason, string> = {
  [AutoExecutionCalendarModeReason.notActive]: 'This workflow is not active',
  [AutoExecutionCalendarModeReason.notOptedIn]: 'Auto-execution is off for this workflow',
  [AutoExecutionCalendarModeReason.requiresHumanIdentity]: 'Fee changes always need a reviewer',
  [AutoExecutionCalendarModeReason.noEnabledPolicy]: 'No auto-execution policy covers it',
  [AutoExecutionCalendarModeReason.noPolicyMatches]: 'No auto-execution policy covers it',
  [AutoExecutionCalendarModeReason.policyNarrowsPerDecision]:
    'Only some listings are covered by the policy',
  [AutoExecutionCalendarModeReason.scheduleInvalid]:
    'The policy’s fire schedule is invalid, so it would be held',
}

export const AUTO_EXECUTION_MEMBER_SOURCES: Record<AutoExecutionCalendarMemberSource, string> = {
  [AutoExecutionCalendarMemberSource.decision]: 'Exact decision membership',
  [AutoExecutionCalendarMemberSource.staticList]: 'Workflow’s fixed listing list',
  [AutoExecutionCalendarMemberSource.lastRun]: 'Last run’s listings; the next run may differ',
  [AutoExecutionCalendarMemberSource.unknown]: 'Listings are unknown until the workflow runs',
}

export const AUTO_EXECUTION_TIME_BASES: Record<AutoExecutionCalendarTimeBasis, string> = {
  [AutoExecutionCalendarTimeBasis.scheduled]: 'Scheduled',
  [AutoExecutionCalendarTimeBasis.dispatched]: 'Dispatched',
  [AutoExecutionCalendarTimeBasis.overdue]: 'Due; waiting for the dispatcher',
  [AutoExecutionCalendarTimeBasis.inFlight]: 'Dispatch in progress',
  [AutoExecutionCalendarTimeBasis.waitingCapacity]: 'Waiting for capacity',
  [AutoExecutionCalendarTimeBasis.retry]: 'Waiting for a retry',
  [AutoExecutionCalendarTimeBasis.indeterminate]: 'Outcome unconfirmed',
  [AutoExecutionCalendarTimeBasis.projected]: 'Future workflow run',
  [AutoExecutionCalendarTimeBasis.projectedOverdue]: 'Missed run; the scheduler will replay it',
  [AutoExecutionCalendarTimeBasis.projectedRunning]:
    'Run in progress; decisions queue when it finishes',
  [AutoExecutionCalendarTimeBasis.projectedRetry]: 'Run failed; the scheduler will retry it',
}

async function getAutoExecutionCalendar({
  periodFrom,
  periodTo,
  analystId,
  marketId,
  autoExecutingOnly,
}: AutoExecutionCalendarQuery): Promise<AutoExecutionCalendarEntry[]> {
  const { data, errors } = await arriereBackoffice.queries.revenueManagement.raw({
    __alias: 'revenueAgentsAutoExecutionCalendar',
    autoExecutionCalendar: {
      __args: {
        periodFrom: periodFrom.toISOString(),
        periodTo: periodTo.toISOString(),
        analystId: analystId ?? undefined,
        marketId: marketId ?? undefined,
        autoExecutingOnly,
      },
      entries: {
        id: true,
        kind: true,
        at: true,
        runAt: true,
        isEstimate: true,
        timeBasis: true,
        mode: true,
        modeReason: true,
        workflowId: true,
        workflowName: true,
        adjustmentType: true,
        policyName: true,
        memberCount: true,
        memberSource: true,
        decisionId: true,
        status: true,
        action: true,
        propertyName: true,
        magnitudeSummary: true,
        estRevenueDelta: true,
        runInProgress: true,
        lastRunProducedDecisions: true,
        autoExecution: AUTO_EXECUTION_STATE_FIELDS,
      },
    },
  })

  if (errors?.length) throw new Error(errors[errors.length - 1].message)
  if (!data?.autoExecutionCalendar) throw new Error('Auto-execution calendar is unavailable.')

  return (data.autoExecutionCalendar.entries ?? []).flatMap(entry => {
    const at = toIso(entry?.at)
    if (!entry?.id || !entry.kind || !at) return []
    return [
      {
        id: entry.id,
        kind: entry.kind,
        at,
        runAt: toIso(entry.runAt),
        isEstimate: entry.isEstimate ?? false,
        timeBasis: entry.timeBasis ?? null,
        mode: entry.mode ?? null,
        modeReason: entry.modeReason ?? null,
        workflowId: entry.workflowId ?? null,
        workflowName: entry.workflowName ?? null,
        adjustmentType: entry.adjustmentType ?? null,
        policyName: entry.policyName ?? null,
        memberCount: entry.memberCount ?? null,
        memberSource: entry.memberSource ?? null,
        decisionId: entry.decisionId ?? null,
        status: entry.status ?? null,
        action: entry.action ?? null,
        propertyName: entry.propertyName ?? null,
        magnitudeSummary: entry.magnitudeSummary ?? null,
        estRevenueDelta: entry.estRevenueDelta ?? null,
        runInProgress: entry.runInProgress ?? false,
        lastRunProducedDecisions: entry.lastRunProducedDecisions ?? null,
        autoExecution: mapAutoExecutionState(entry.autoExecution),
      },
    ]
  })
}

export interface WorkflowActivityFire {
  key: string
  at: string
  runAt: string
  mode: AutoExecutionCalendarMode | null
  modeReason: AutoExecutionCalendarModeReason | null
  policyName: string | null
  memberCount: number | null
  memberSource: AutoExecutionCalendarMemberSource | null
  isEstimate: boolean
  runInProgress: boolean
  lastRunProducedDecisions: boolean | null
}

export interface WorkflowActivityDispatch {
  id: string
  kind: AutoExecutionCalendarEntryKind
  at: string
  isEstimate: boolean
  timeBasis: AutoExecutionCalendarTimeBasis | null
  status: AutoExecutionCalendarQueueStatus | null
  action: AutoExecutionDispatchAction | null
  policyName: string | null
  memberCount: number | null
  memberSource: AutoExecutionCalendarMemberSource | null
  magnitudeSummary: string | null
  estRevenueDelta: number | null
  propertyName: string | null
  decisionId: string | null
}

export interface WorkflowActivity {
  workflowId: string | null
  name: string
  status: WorkflowStatus | null
  adjustmentType: WorkflowAdjustmentType | null
  routing: WorkflowRouting | null
  autoExecution: AutoExecutionState | null
  runs: WorkflowTimelineRun[]
  fires: WorkflowActivityFire[]
  dispatches: WorkflowActivityDispatch[]
}

const ANALYST_ACTIVITY_NAME = 'Analyst decisions'

function workflowActivity(workflow: WorkflowTimelineWorkflow): WorkflowActivity {
  return {
    workflowId: workflow.workflowId,
    name: workflow.name,
    status: workflow.status,
    adjustmentType: workflow.adjustmentType,
    routing: workflow.routing,
    autoExecution: null,
    runs: workflow.runs,
    fires: workflow.upcoming.map(at => ({
      key: `${workflow.workflowId}:${at}`,
      at,
      runAt: at,
      mode: null,
      modeReason: null,
      policyName: null,
      memberCount: null,
      memberSource: null,
      isEstimate: false,
      runInProgress: false,
      lastRunProducedDecisions: null,
    })),
    dispatches: [],
  }
}

function entryActivity(entry: AutoExecutionCalendarEntry): WorkflowActivity {
  return {
    workflowId: entry.workflowId,
    name:
      entry.workflowId == null
        ? ANALYST_ACTIVITY_NAME
        : (entry.workflowName ?? 'Untitled workflow'),
    status: null,
    adjustmentType: entry.adjustmentType,
    routing: null,
    autoExecution: null,
    runs: [],
    fires: [],
    dispatches: [],
  }
}

function projectedFire(entry: AutoExecutionCalendarEntry): WorkflowActivityFire {
  return {
    key: entry.id,
    at: entry.at,
    runAt: entry.runAt ?? entry.at,
    mode: entry.mode,
    modeReason: entry.modeReason,
    policyName: entry.policyName,
    memberCount: entry.memberCount,
    memberSource: entry.memberSource,
    isEstimate: entry.isEstimate,
    runInProgress: entry.runInProgress,
    lastRunProducedDecisions: entry.lastRunProducedDecisions,
  }
}

function entryDispatch(entry: AutoExecutionCalendarEntry): WorkflowActivityDispatch {
  return {
    id: entry.id,
    kind: entry.kind,
    at: entry.at,
    isEstimate: entry.isEstimate,
    timeBasis: entry.timeBasis,
    status: entry.status,
    action: entry.action,
    policyName: entry.policyName,
    memberCount: entry.memberCount,
    memberSource: entry.memberSource,
    magnitudeSummary: entry.magnitudeSummary,
    estRevenueDelta: entry.estRevenueDelta,
    propertyName: entry.propertyName,
    decisionId: entry.decisionId,
  }
}

interface MergeWorkflowActivityParams {
  workflows: WorkflowTimelineWorkflow[]
  entries: AutoExecutionCalendarEntry[]
}

export function mergeWorkflowActivity({
  workflows,
  entries,
}: MergeWorkflowActivityParams): WorkflowActivity[] {
  const activities = new Map<string | null, WorkflowActivity>()
  workflows.forEach(workflow => activities.set(workflow.workflowId, workflowActivity(workflow)))
  entries.forEach(entry => {
    const activity = activities.get(entry.workflowId) ?? entryActivity(entry)
    activities.set(entry.workflowId, activity)
    if (entry.kind !== AutoExecutionCalendarEntryKind.projected) {
      activity.dispatches.push(entryDispatch(entry))
      return
    }
    if (activity.autoExecution == null) activity.autoExecution = entry.autoExecution
    const fire = projectedFire(entry)
    const matched = activity.fires.some(candidate => candidate.runAt === fire.runAt)
    activity.fires = matched
      ? activity.fires.map(candidate =>
          candidate.runAt === fire.runAt ? { ...fire, key: candidate.key } : candidate,
        )
      : [...activity.fires, fire]
  })
  return [...activities.values()]
}

export async function getWorkflowActivity(
  query: WorkflowTimelineQuery,
): Promise<WorkflowActivity[]> {
  const [workflows, entries] = await Promise.all([
    getWorkflowExecutionTimeline(query),
    getAutoExecutionCalendar({
      periodFrom: query.from,
      periodTo: query.to,
      analystId: query.analystId,
      marketId: query.marketId,
      autoExecutingOnly: false,
    }),
  ])
  return mergeWorkflowActivity({ workflows, entries })
}

// ---------------------------------------------------------------------------
// Execution attribution — weekly human vs AI share of executed revenue actions.
// Backend: revenueManagement.executionAttribution (PRI-1401; counting unit
// changed by PRI-1575). Owned here so the Learning-page charts import the
// shape from a single source of truth.
//
// One action = one pricing entity (a property, or a hotel room type) × one
// lever × one day where the setting actually changed — the SAME unit for both
// actor types. Per-channel spreads, same-day re-edits, and no-op re-saves
// collapse on both sides. Scope caveat: the analyst/market/decisionType
// filters resolve through home ids, so filtered views cover property-mapped
// activity only — hotel room-type actions appear in the unfiltered series.
// ---------------------------------------------------------------------------

export interface ExecutionAttributionWeek {
  /** Monday of the ISO week, YYYY-MM-DD. */
  week: string
  /** Data-floor first week or the in-progress current week. */
  partial: boolean
  /** Human actions (entity × lever × day) minus human-applied AI decisions. */
  humanOriginal: number
  /** Pipeline-dispatched actions, on the same entity × lever × day unit. */
  aiExecuted: number
  /** Human applied a rejected AI decision verbatim (reconciler exact match). */
  aiAppliedExact: number
  /** Human applied a rejected AI decision with a different value (similar match). */
  aiAppliedModified: number
  /** aiExecuted + aiAppliedExact + aiAppliedModified. */
  aiAttributable: number
  /** AI-attributable share of the week's actions, 0–100. */
  aiSharePct: number
  totalActions: number
  analystsOwningHomes: number
  actionsPerAnalyst: number | null
  approvedViaDashboard: number
  approvedViaRemy: number
}

export interface ExecutionAttributionData {
  weeks: ExecutionAttributionWeek[]
  dataFloor: string
  dataThrough: string
  modifiedMissingActionDate: number
  surfaceSince: string | null
}

/**
 * Loose shape of the raw `executionAttribution` field as the generated arriere
 * client models it — every selected field is optional/nullable. `normalize`
 * defaults it into the strict `ExecutionAttributionData` the UI consumes, so
 * activating the real query is type-safe (see getExecutionAttribution).
 */
interface RawExecutionAttribution {
  dataFloor?: string | null
  dataThrough?: string | null
  modifiedMissingActionDate?: number | null
  surfaceSince?: string | null
  weeks?:
    | ({
        week?: string | null
        partial?: boolean | null
        humanOriginal?: number | null
        aiExecuted?: number | null
        aiAppliedExact?: number | null
        aiAppliedModified?: number | null
        aiAttributable?: number | null
        aiSharePct?: number | null
        totalActions?: number | null
        analystsOwningHomes?: number | null
        actionsPerAnalyst?: number | null
        approvedViaDashboard?: number | null
        approvedViaRemy?: number | null
      } | null)[]
    | null
}

export function normalizeExecutionAttribution(
  raw: RawExecutionAttribution,
): ExecutionAttributionData {
  return {
    dataFloor: raw.dataFloor ?? '',
    dataThrough: raw.dataThrough ?? '',
    modifiedMissingActionDate: raw.modifiedMissingActionDate ?? 0,
    surfaceSince: raw.surfaceSince ?? null,
    weeks: (raw.weeks ?? []).filter(Boolean).map(w => ({
      week: w?.week ?? '',
      partial: w?.partial ?? false,
      humanOriginal: w?.humanOriginal ?? 0,
      aiExecuted: w?.aiExecuted ?? 0,
      aiAppliedExact: w?.aiAppliedExact ?? 0,
      aiAppliedModified: w?.aiAppliedModified ?? 0,
      aiAttributable: w?.aiAttributable ?? 0,
      aiSharePct: w?.aiSharePct ?? 0,
      totalActions: w?.totalActions ?? 0,
      analystsOwningHomes: w?.analystsOwningHomes ?? 0,
      actionsPerAnalyst: w?.actionsPerAnalyst ?? null,
      approvedViaDashboard: w?.approvedViaDashboard ?? 0,
      approvedViaRemy: w?.approvedViaRemy ?? 0,
    })),
  }
}

/**
 * Weekly execution-attribution series for the Learning page.
 *
 * `normalizeExecutionAttribution` defaults the client's optional/nullable
 * fields into the strict shape the charts consume. Returns `null` when the
 * backend has no attribution payload — the Learning-page section stays hidden.
 */
export async function getExecutionAttribution(
  filters: RevenueAgentsFilters = {},
): Promise<ExecutionAttributionData | null> {
  const result = await arriereBackoffice.queries.revenueManagement({
    __alias: 'revenueAgentsExecutionAttribution',
    executionAttribution: {
      __args: {
        analystId: filters.analystId || undefined,
        marketId: filters.marketId || undefined,
        decisionType: filters.decisionType ? [filters.decisionType] : undefined,
      },
      dataFloor: true,
      dataThrough: true,
      modifiedMissingActionDate: true,
      surfaceSince: true,
      weeks: {
        week: true,
        partial: true,
        humanOriginal: true,
        aiExecuted: true,
        aiAppliedExact: true,
        aiAppliedModified: true,
        aiAttributable: true,
        aiSharePct: true,
        totalActions: true,
        analystsOwningHomes: true,
        actionsPerAnalyst: true,
        approvedViaDashboard: true,
        approvedViaRemy: true,
      },
    },
  })

  return result.executionAttribution
    ? normalizeExecutionAttribution(result.executionAttribution)
    : null
}

export async function getRecentRejectionsPage(
  filters: RevenueAgentsFilters = {},
  page: number,
  size: number,
) {
  const result = await arriereBackoffice.queries.revenueManagement({
    __alias: 'revenueAgentsRecentRejections',
    recentRejections: {
      __args: {
        analystId: filters.analystId || undefined,
        marketId: filters.marketId || undefined,
        decisionType: filters.decisionType ? [filters.decisionType] : undefined,
        sinceDays: filters.sinceDays || undefined,
        pagination: { page, size },
      },
      pages: true,
      total: true,
      results: {
        id: true,
        confidence: true,
        currentValue: true,
        decisionType: true,
        market: true,
        observation: true,
        propertyId: true,
        propertyName: true,
        proposedValue: true,
        reviewFeedback: true,
        reviewFeedbackType: true,
        reviewedAt: true,
        reviewedBy: true,
      },
    },
  })

  return result.recentRejections ?? null
}

export type RecentRejectionsPageResponse = Awaited<ReturnType<typeof getRecentRejectionsPage>>

// ---------------------------------------------------------------------------
// Changes page — change-log entries + analysis cycles.
// ---------------------------------------------------------------------------

export type ChangeScope = 'global' | 'market' | 'property'

export type ChangeSourceType =
  | 'doc'
  | 'script'
  | 'dataset'
  | 'methodology'
  | 'data_correction'
  | 'other'

export interface ChangeLogEntryStats {
  approved: number
  rejected: number
  pending: number
  total: number
}

export interface ChangeLogEntry {
  id: string
  scope: ChangeScope
  market: string | null
  property_id: string | null
  summary: string
  details: string | null
  source_type: ChangeSourceType
  effective_at: string
  created_at: string
  /**
   * Optional aggregate of decision outcomes for the window leading up to this
   * change. The backend only populates this on a subset of entries — callers
   * must guard on `decision_stats?.total`.
   */
  decision_stats: ChangeLogEntryStats | null
}

export interface CycleTimelineRow {
  cycle_id: string
  property_id: string
  property_name: string
  market: string
  started_at: string
  completed_at: string | null
  session_status: string | null
  decision_count: number
}

export interface ChangesPageFilters {
  scope?: ChangeScope
  market?: string
  sourceType?: ChangeSourceType
  since?: string
  until?: string
  hideCycles?: boolean
}

export interface ChangesPageData {
  entries: ChangeLogEntry[]
  cycles: CycleTimelineRow[]
  decisionsProgress: DecisionsProgressPoint[]
}

const SCOPE_TO_API = {
  global: RevenueManagementAutomationChangeLogScope.global,
  market: RevenueManagementAutomationChangeLogScope.market,
  property: RevenueManagementAutomationChangeLogScope.property,
} satisfies Record<ChangeScope, RevenueManagementAutomationChangeLogScope>

const SCOPE_FROM_API: Record<RevenueManagementAutomationChangeLogScope, ChangeScope> = {
  [RevenueManagementAutomationChangeLogScope.global]: 'global',
  [RevenueManagementAutomationChangeLogScope.market]: 'market',
  [RevenueManagementAutomationChangeLogScope.property]: 'property',
}

const SOURCE_TYPE_TO_API = {
  doc: RevenueManagementAutomationChangeLogSourceType.doc,
  script: RevenueManagementAutomationChangeLogSourceType.script,
  dataset: RevenueManagementAutomationChangeLogSourceType.dataset,
  methodology: RevenueManagementAutomationChangeLogSourceType.methodology,
  data_correction: RevenueManagementAutomationChangeLogSourceType.dataCorrection,
  other: RevenueManagementAutomationChangeLogSourceType.other,
} satisfies Record<ChangeSourceType, RevenueManagementAutomationChangeLogSourceType>

const SOURCE_TYPE_FROM_API: Record<
  RevenueManagementAutomationChangeLogSourceType,
  ChangeSourceType
> = {
  [RevenueManagementAutomationChangeLogSourceType.doc]: 'doc',
  [RevenueManagementAutomationChangeLogSourceType.script]: 'script',
  [RevenueManagementAutomationChangeLogSourceType.dataset]: 'dataset',
  [RevenueManagementAutomationChangeLogSourceType.methodology]: 'methodology',
  [RevenueManagementAutomationChangeLogSourceType.dataCorrection]: 'data_correction',
  [RevenueManagementAutomationChangeLogSourceType.other]: 'other',
}

// Only produce a completion timestamp when the cycle is terminal. Running
// cycles → null so the UI doesn't show a projected end time.
function deriveCompletedAt(
  startedAt: string | undefined,
  durationSeconds: number | undefined,
  status: string | undefined,
): string | null {
  if (!startedAt || durationSeconds == null || !status) return null
  const terminal = status === 'completed' || status === 'failed'
  if (!terminal) return null
  const start = Date.parse(startedAt)
  if (Number.isNaN(start)) return null
  return new Date(start + durationSeconds * 1000).toISOString()
}

export interface DecisionsProgressPoint {
  timestamp: string
  approved: number
  rejected: number
  pending: number
  total: number
  approvalRate: number
}

/**
 * Build the line-chart series from already-mapped `entries[]`. We keep one
 * snapshot per day (the first entry encountered — entries arrive sorted DESC
 * by `effective_at`, so this is the latest change of that day). Summing
 * across changes within a day risked double-counting the same decisions when
 * the backend's per-entry stats windows overlap.
 *
 * `approvalRate = approved / (approved + rejected)` so pending decisions
 * don't dilute the rate.
 *
 * Days whose representative window had no decided decisions (`approved +
 * rejected === 0`) are omitted entirely rather than plotted at 0% — a window
 * with only pending decisions (or no decisions at all) carries no approval
 * signal, so the chart skips that date instead of drawing a misleading
 * drop-to-zero point.
 */
function aggregateDecisionsProgress(entries: ChangeLogEntry[]): DecisionsProgressPoint[] {
  const byDate = new Map<string, ChangeLogEntryStats>()
  for (const e of entries) {
    if (!e.decision_stats) continue
    const dateKey = e.effective_at.slice(0, 10)
    if (!dateKey || byDate.has(dateKey)) continue
    byDate.set(dateKey, e.decision_stats)
  }

  return [...byDate.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .filter(([, stats]) => stats.approved + stats.rejected > 0)
    .map(([dateKey, stats]) => {
      const decided = stats.approved + stats.rejected
      const approvalRate = Math.round((stats.approved / decided) * 100)
      return {
        // `dateKey` is just the day; pin to UTC midnight so the tooltip's
        // date formatter has a real ISO timestamp to chew on.
        timestamp: `${dateKey}T00:00:00Z`,
        approved: stats.approved,
        rejected: stats.rejected,
        pending: stats.pending,
        total: stats.total,
        approvalRate,
      }
    })
}

/**
 * Loads change-log entries and (optionally) analysis cycles for the Changes
 * page via the `revenueManagement.changesTimeline` query. The server groups
 * results by day; we flatten them into the `{ entries, cycles, decisionsProgress }`
 * shape `ChangesView` expects.
 */
export async function getChangesPageData(
  filters: ChangesPageFilters = {},
): Promise<ChangesPageData> {
  const result = await arriereBackoffice.queries.revenueManagement({
    __alias: 'revenueAgentsChangesTimeline',
    changesTimeline: {
      __args: {
        includeCycles: !filters.hideCycles,
        market: filters.market,
        scope: filters.scope ? SCOPE_TO_API[filters.scope] : undefined,
        sourceType: filters.sourceType ? SOURCE_TYPE_TO_API[filters.sourceType] : undefined,
        since: filters.since,
        until: filters.until,
      },
      date: true,
      changes: {
        id: true,
        summary: true,
        details: true,
        scope: true,
        market: true,
        propertyId: true,
        category: true,
        effectiveAt: true,
        decisionStats: {
          approved: true,
          rejected: true,
          pending: true,
          total: true,
        },
      },
      markets: {
        market: true,
        properties: {
          cycleId: true,
          market: true,
          startedAt: true,
          analysisDurationSeconds: true,
          analysisStatus: true,
          decisionCount: true,
          property: { id: true, name: true },
        },
      },
    },
  })

  const days = result.changesTimeline ?? []

  const entries: ChangeLogEntry[] = days
    .flatMap(d => d.changes ?? [])
    .map(c => {
      const stats = c.decisionStats
      const decision_stats: ChangeLogEntryStats | null =
        stats && typeof stats.total === 'number'
          ? {
              approved: stats.approved ?? 0,
              rejected: stats.rejected ?? 0,
              pending: stats.pending ?? 0,
              total: stats.total,
            }
          : null
      return {
        id: c.id ?? '',
        scope: c.scope ? SCOPE_FROM_API[c.scope] : 'global',
        market: c.market ?? null,
        property_id: c.propertyId ?? null,
        summary: c.summary ?? '',
        details: c.details ?? null,
        source_type: c.category ? SOURCE_TYPE_FROM_API[c.category] : 'other',
        effective_at: c.effectiveAt ? String(c.effectiveAt) : '',
        // API only surfaces effectiveAt; created_at is unused by the UI.
        created_at: c.effectiveAt ? String(c.effectiveAt) : '',
        decision_stats,
      }
    })
    .sort((a, b) => b.effective_at.localeCompare(a.effective_at))

  const decisionsProgress = aggregateDecisionsProgress(entries)

  const cycles: CycleTimelineRow[] = days
    .flatMap(d => d.markets ?? [])
    .flatMap(m => m.properties ?? [])
    .map(c => {
      const startedAt = c.startedAt ? String(c.startedAt) : ''
      return {
        cycle_id: c.cycleId ?? '',
        property_id: c.property?.id ?? '',
        property_name: c.property?.name ?? '',
        market: c.market ?? '',
        started_at: startedAt,
        completed_at: deriveCompletedAt(
          startedAt || undefined,
          c.analysisDurationSeconds ?? undefined,
          c.analysisStatus ?? undefined,
        ),
        session_status: c.analysisStatus ?? null,
        decision_count: c.decisionCount ?? 0,
      }
    })
    .sort((a, b) => b.started_at.localeCompare(a.started_at))

  return { entries, cycles, decisionsProgress }
}

// ---------------------------------------------------------------------------
// Workflows (RevenueManagementQueries.workflows + lifecycle mutations)
// ---------------------------------------------------------------------------

export type WorkflowStatus = `${RevenueManagementWorkflowStatus}`
export type WorkflowRunStatus = `${RevenueManagementWorkflowRunStatus}`
export type WorkflowAdjustmentType = `${RevenueManagementWorkflowAdjustmentType}`
export type WorkflowCohortType = `${RevenueManagementWorkflowCohortType}`
export type WorkflowExclusionType = `${RevenueManagementWorkflowExclusionType}`
export type WorkflowFloorMode = `${RevenueManagementWorkflowFloorMode}`
export type WorkflowTriggerType = `${RevenueManagementWorkflowTriggerType}`
export type WorkflowScheduleCadence = `${RevenueManagementWorkflowScheduleCadence}`
export type WorkflowScopeKind = `${RevenueManagementWorkflowScopeKind}`

/** Input for `createWorkflow` / `updateWorkflow` / `compileWorkflowPolicy`. */
export type CreateWorkflowControls = RevenueManagementWorkflowControlsInput

export interface WorkflowSummary {
  id: string
  name: string
  status: WorkflowStatus
  scopeSummary: string
  cadence: string
  rawCron: string | null
  timezone: string | null
  adjustmentType: WorkflowAdjustmentType | null
  aiReviewEnabled: boolean
  approvalRate: number | null
  decisionsCreated: number
  lastRunAt: string | null
  lastRunStatus: WorkflowRunStatus | null
  createdAt: string | null
  createdBy: string | null
  autoExecution: AutoExecutionState | null
}

export interface WorkflowTier {
  discountPct: number | null
  maxDaysOut: number | null
}

export interface WorkflowFloor {
  mode: WorkflowFloorMode | null
}

/** Which axis a metric band buckets a property by. */
export type WorkflowPropertyMetric = `${RevenueManagementWorkflowPropertyMetric}`

/** Discount % split by day-of-week class (weekend = Fri+Sat, weekday = Sun–Thu). */
export interface WorkflowDowDiscounts {
  weekend: number | null
  weekday: number | null
  defaultNights: number | null
}

/** One occupancy band inside a lead-time rule (inclusive % bounds). */
export interface WorkflowOccupancyRange {
  minPct: number | null
  maxPct: number | null
  discounts: WorkflowDowDiscounts | null
}

/** One band on a generic property-metric axis inside a lead-time rule. */
export interface WorkflowMetricRange {
  metric: WorkflowPropertyMetric | null
  min: number | null
  max: number | null
  discounts: WorkflowDowDiscounts | null
}

/**
 * One lead-time rule: nights within `maxDaysOut` resolve through occupancyRanges,
 * metricRanges, or a lead-time-wide `discounts` block (exactly one is set).
 */
export interface WorkflowRule {
  maxDaysOut: number | null
  occupancyRanges: WorkflowOccupancyRange[]
  metricRanges: WorkflowMetricRange[]
  discounts: WorkflowDowDiscounts | null
}

export interface WorkflowAdjustment {
  type: WorkflowAdjustmentType | null
  basis: string | null
  source: string | null
  /** Voyage fee keys an `authored_fees` workflow targets (e.g. `cleaning`,
   *  `marginal_cleaning`). Empty for non-fee adjustments. */
  fees: string[]
  floor: WorkflowFloor | null
  tiers: WorkflowTier[]
  /** Nested discount matrix (lead-time × occupancy/metric × day-of-week). Empty
   *  for a flat-tier workflow. */
  rules: WorkflowRule[]
}

export interface WorkflowCohort {
  type: WorkflowCohortType | null
  market: string | null
  bedrooms: number | null
  cohortIds: string[]
  propertyIds: string[]
}

export interface WorkflowExclusion {
  type: WorkflowExclusionType | null
  label: string | null
  fingerprint: string | null
  propertyIds: string[]
  requiredColumns: string[]
  sql: string | null
}

export interface WorkflowTrigger {
  type: WorkflowTriggerType | null
  expr: string | null
  timezone: string | null
}

export interface WorkflowAiReview {
  enabled: boolean
}

export interface WorkflowPolicy {
  name: string | null
  horizonDays: number | null
  routing: string | null
  emits: string[]
  adjustment: WorkflowAdjustment | null
  aiReview: WorkflowAiReview | null
  cohort: WorkflowCohort | null
  exclusions: WorkflowExclusion[]
  trigger: WorkflowTrigger | null
}

/**
 * Whether a workflow's decisions can execute unattended, resolved server-side.
 *
 * Both switches have to be on — the workflow's own routing AND an enabled policy
 * whose scope claims it — and the client deliberately does not work this out:
 * a policy narrows by property and market, which a workflow does not have, so a
 * second matcher here would drift from the one that actually gates dispatch.
 */
export interface AutoExecutionState {
  /** `auto_approve` | `pending_review`. */
  routing: string | null
  /** True only when a policy claims the whole workflow. Render as "on". */
  eligible: boolean
  /** A policy matches but narrows per decision. Render as "partial", NEVER as
   *  on — and expect it often, since most policies scope by property. */
  conditional: boolean
  policyId: string | null
  /** The policy that WOULD fire — the most specific match (properties > workflow
   *  set > market > lever only), the same order the enqueue path uses. */
  policyName: string | null
  /** Why it is not simply on. A bare String on the wire, so treat the set as
   *  open: `not_active`, `not_opted_in`, `requires_human_identity`,
   *  `no_enabled_policy`, `no_policy_matches`, `policy_narrows_per_decision`. */
  reason: string | null
}

const AUTO_EXECUTION_STATE_FIELDS = {
  routing: true,
  eligible: true,
  conditional: true,
  policyId: true,
  policyName: true,
  reason: true,
} as const

function mapAutoExecutionState(
  raw:
    | {
        routing?: string | null
        eligible?: boolean | null
        conditional?: boolean | null
        policyId?: string | null
        policyName?: string | null
        reason?: string | null
      }
    | null
    | undefined,
): AutoExecutionState | null {
  if (!raw) return null
  return {
    routing: raw.routing ?? null,
    // The SDL prints these nullable but the resolver always sets them; treating
    // absence as false keeps "on" something the server has to assert.
    eligible: raw.eligible === true,
    conditional: raw.conditional === true,
    policyId: raw.policyId ?? null,
    policyName: raw.policyName ?? null,
    reason: raw.reason ?? null,
  }
}

/** Full workflow row (list summary fields + nested compiled policy). */
export interface WorkflowDetail extends WorkflowSummary {
  createdByReviewerId: string | null
  updatedAt: string | null
  /** The B7 authoring chat that produced this workflow (null for composer/CLI). */
  authoringSessionId: string | null
  /** The workflow this one was duplicated/cloned from, if any. */
  baseWorkflowId: string | null
  /** The multi-market fan-out set this workflow was created with; null if solo.
   *  Distinct from baseWorkflowId (cloned-from ancestry). */
  workflowSetId: string | null
  /** Sibling workflows created together in the same fan-out (excludes self);
   *  empty when this workflow isn't part of a set. */
  relatedWorkflows: WorkflowSummary[]
  policy: WorkflowPolicy | null
}

// ── Workflow Sets ────────────────────────────────────────────────────────────
// A set is the first-class entity grouping the single-market workflows of a
// multi-market fan-out. The set's own `status` is a retired/live marker; the
// per-market state lives on the members (see `memberStatusRollup`).

/** The set's own live/retired marker — distinct from its members' statuses. */
export type WorkflowSetStatus = `${RevenueManagementWorkflowSetStatus}`

/** Per-member status counts + a ready-to-render summary (e.g. "2 active · 1 paused"). */
export interface WorkflowSetMemberStatusRollup {
  active: number
  paused: number
  draft: number
  archived: number
  label: string | null
}

/** Aggregate decision metrics for the set. `approvalRate` is the workflow-product
 *  rate (not the agent-learning series). */
export interface WorkflowSetRollup {
  memberCount: number
  activeMemberCount: number
  decisionsCreated: number
  approved: number
  rejected: number
  approvalRate: number | null
}

/** A multi-market fan-out set: one member workflow per market. */
export interface WorkflowSet {
  id: string
  name: string
  /** The set's own retired/live marker; use `memberStatusRollup` for per-market state. */
  status: WorkflowSetStatus | null
  createdBy: string | null
  createdByReviewerId: string | null
  createdAt: string | null
  updatedAt: string | null
  /** One per market, creation order, incl. archived. Same shape the flat list uses. */
  members: WorkflowSummary[]
  memberStatusRollup: WorkflowSetMemberStatusRollup | null
  rollup: WorkflowSetRollup | null
}

/**
 * One per-member result from a set operation (activate/pause/archive/run/edit).
 * Partial success is the NORMAL case — a market the operator doesn't own fails
 * while the rest succeed, so callers must render the per-member breakdown rather
 * than a blanket success. `runRequestId` is only set by `runWorkflowSetNow`.
 */
export interface WorkflowSetMemberResult {
  workflowId: string | null
  market: string | null
  name: string | null
  ok: boolean
  error: string | null
  runRequestId: string | null
}

const WORKFLOW_SUMMARY_FIELDS = {
  id: true,
  name: true,
  status: true,
  scopeSummary: true,
  cadence: true,
  rawCron: true,
  timezone: true,
  adjustmentType: true,
  aiReviewEnabled: true,
  approvalRate: true,
  decisionsCreated: true,
  lastRunAt: true,
  lastRunStatus: true,
  createdAt: true,
  createdBy: true,
  autoExecution: AUTO_EXECUTION_STATE_FIELDS,
} as const

const WORKFLOW_DETAIL_FIELDS = {
  ...WORKFLOW_SUMMARY_FIELDS,
  createdByReviewerId: true,
  updatedAt: true,
  authoringSessionId: true,
  baseWorkflowId: true,
  workflowSetId: true,
  relatedWorkflows: WORKFLOW_SUMMARY_FIELDS,
  policy: {
    name: true,
    horizonDays: true,
    routing: true,
    emits: true,
    adjustment: {
      type: true,
      basis: true,
      source: true,
      fees: true,
      floor: { mode: true },
      tiers: {
        discountPct: true,
        maxDaysOut: true,
      },
      rules: {
        maxDaysOut: true,
        discounts: { weekend: true, weekday: true, defaultNights: true },
        occupancyRanges: {
          minPct: true,
          maxPct: true,
          discounts: { weekend: true, weekday: true, defaultNights: true },
        },
        metricRanges: {
          metric: true,
          min: true,
          max: true,
          discounts: { weekend: true, weekday: true, defaultNights: true },
        },
      },
    },
    aiReview: {
      enabled: true,
    },
    cohort: {
      type: true,
      market: true,
      bedrooms: true,
      cohortIds: true,
      propertyIds: true,
    },
    exclusions: {
      type: true,
      label: true,
      fingerprint: true,
      propertyIds: true,
      requiredColumns: true,
      sql: true,
    },
    trigger: {
      type: true,
      expr: true,
      timezone: true,
    },
  },
} as const

const WORKFLOW_SET_FIELDS = {
  id: true,
  name: true,
  status: true,
  createdBy: true,
  createdByReviewerId: true,
  createdAt: true,
  updatedAt: true,
  members: WORKFLOW_SUMMARY_FIELDS,
  memberStatusRollup: {
    active: true,
    paused: true,
    draft: true,
    archived: true,
    label: true,
  },
  rollup: {
    memberCount: true,
    activeMemberCount: true,
    decisionsCreated: true,
    approved: true,
    rejected: true,
    approvalRate: true,
  },
} as const

const WORKFLOW_SET_MEMBER_RESULT_FIELDS = {
  workflowId: true,
  market: true,
  name: true,
  ok: true,
  error: true,
  runRequestId: true,
} as const

type RawWorkflowSummary = {
  id?: string | null
  name?: string | null
  status?: RevenueManagementWorkflowStatus | null
  scopeSummary?: string | null
  cadence?: string | null
  rawCron?: string | null
  timezone?: string | null
  adjustmentType?: RevenueManagementWorkflowAdjustmentType | null
  aiReviewEnabled?: boolean | null
  approvalRate?: number | null
  decisionsCreated?: number | null
  lastRunAt?: unknown
  lastRunStatus?: RevenueManagementWorkflowRunStatus | null
  createdAt?: unknown
  createdBy?: string | null
  autoExecution?: Parameters<typeof mapAutoExecutionState>[0]
}

type RawDowDiscounts = {
  weekend?: number | null
  weekday?: number | null
  defaultNights?: number | null
}

type RawWorkflowDetail = RawWorkflowSummary & {
  createdByReviewerId?: string | null
  updatedAt?: unknown
  authoringSessionId?: string | null
  baseWorkflowId?: string | null
  workflowSetId?: string | null
  relatedWorkflows?: Array<RawWorkflowSummary | null> | null
  policy?: {
    name?: string | null
    horizonDays?: number | null
    routing?: string | null
    emits?: (string | null)[] | null
    adjustment?: {
      type?: RevenueManagementWorkflowAdjustmentType | null
      basis?: string | null
      source?: string | null
      fees?: (string | null)[] | null
      floor?: { mode?: RevenueManagementWorkflowFloorMode | null } | null
      tiers?: Array<{
        discountPct?: number | null
        maxDaysOut?: number | null
      } | null> | null
      rules?: Array<{
        maxDaysOut?: number | null
        discounts?: RawDowDiscounts | null
        occupancyRanges?: Array<{
          minPct?: number | null
          maxPct?: number | null
          discounts?: RawDowDiscounts | null
        } | null> | null
        metricRanges?: Array<{
          metric?: RevenueManagementWorkflowPropertyMetric | null
          min?: number | null
          max?: number | null
          discounts?: RawDowDiscounts | null
        } | null> | null
      } | null> | null
    } | null
    aiReview?: { enabled?: boolean | null } | null
    cohort?: {
      type?: RevenueManagementWorkflowCohortType | null
      market?: string | null
      bedrooms?: number | null
      cohortIds?: (string | null)[] | null
      propertyIds?: (string | null)[] | null
    } | null
    exclusions?: Array<{
      type?: RevenueManagementWorkflowExclusionType | null
      label?: string | null
      fingerprint?: string | null
      propertyIds?: (string | null)[] | null
      requiredColumns?: (string | null)[] | null
      sql?: string | null
    } | null> | null
    trigger?: {
      type?: RevenueManagementWorkflowTriggerType | null
      expr?: string | null
      timezone?: string | null
    } | null
  } | null
}

function mapWorkflowSummary(raw: RawWorkflowSummary | null | undefined): WorkflowSummary | null {
  if (!raw?.id) return null
  return {
    id: raw.id,
    name: raw.name ?? 'Untitled workflow',
    status: (raw.status ?? RevenueManagementWorkflowStatus.draft) as WorkflowStatus,
    scopeSummary: raw.scopeSummary ?? '',
    cadence: raw.cadence ?? '',
    rawCron: raw.rawCron?.trim() || null,
    timezone: raw.timezone?.trim() || null,
    adjustmentType: (raw.adjustmentType ?? null) as WorkflowAdjustmentType | null,
    aiReviewEnabled: !!raw.aiReviewEnabled,
    approvalRate: typeof raw.approvalRate === 'number' ? raw.approvalRate : null,
    decisionsCreated: raw.decisionsCreated ?? 0,
    lastRunAt: raw.lastRunAt ? String(raw.lastRunAt) : null,
    lastRunStatus: (raw.lastRunStatus ?? null) as WorkflowRunStatus | null,
    createdAt: raw.createdAt ? String(raw.createdAt) : null,
    createdBy: raw.createdBy ?? null,
    autoExecution: mapAutoExecutionState(raw.autoExecution),
  }
}

type RawWorkflowSetMemberStatusRollup = {
  active?: number | null
  paused?: number | null
  draft?: number | null
  archived?: number | null
  label?: string | null
}

type RawWorkflowSetRollup = {
  memberCount?: number | null
  activeMemberCount?: number | null
  decisionsCreated?: number | null
  approved?: number | null
  rejected?: number | null
  approvalRate?: number | null
}

type RawWorkflowSet = {
  id?: string | null
  name?: string | null
  status?: RevenueManagementWorkflowSetStatus | null
  createdBy?: string | null
  createdByReviewerId?: string | null
  createdAt?: unknown
  updatedAt?: unknown
  members?: Array<RawWorkflowSummary | null> | null
  memberStatusRollup?: RawWorkflowSetMemberStatusRollup | null
  rollup?: RawWorkflowSetRollup | null
}

type RawWorkflowSetMemberResult = {
  workflowId?: string | null
  market?: string | null
  name?: string | null
  ok?: boolean | null
  error?: string | null
  runRequestId?: string | null
}

function mapWorkflowSetMemberStatusRollup(
  raw: RawWorkflowSetMemberStatusRollup | null | undefined,
): WorkflowSetMemberStatusRollup | null {
  if (!raw) return null
  return {
    active: raw.active ?? 0,
    paused: raw.paused ?? 0,
    draft: raw.draft ?? 0,
    archived: raw.archived ?? 0,
    label: raw.label?.trim() || null,
  }
}

function mapWorkflowSetRollup(
  raw: RawWorkflowSetRollup | null | undefined,
): WorkflowSetRollup | null {
  if (!raw) return null
  return {
    memberCount: raw.memberCount ?? 0,
    activeMemberCount: raw.activeMemberCount ?? 0,
    decisionsCreated: raw.decisionsCreated ?? 0,
    approved: raw.approved ?? 0,
    rejected: raw.rejected ?? 0,
    approvalRate: typeof raw.approvalRate === 'number' ? raw.approvalRate : null,
  }
}

function mapWorkflowSet(raw: RawWorkflowSet | null | undefined): WorkflowSet | null {
  if (!raw?.id) return null
  return {
    id: raw.id,
    name: raw.name?.trim() || 'Untitled set',
    status: (raw.status ?? null) as WorkflowSetStatus | null,
    createdBy: raw.createdBy ?? null,
    createdByReviewerId: raw.createdByReviewerId ?? null,
    createdAt: raw.createdAt ? String(raw.createdAt) : null,
    updatedAt: raw.updatedAt ? String(raw.updatedAt) : null,
    members: (raw.members ?? []).map(mapWorkflowSummary).filter((m): m is WorkflowSummary => !!m),
    memberStatusRollup: mapWorkflowSetMemberStatusRollup(raw.memberStatusRollup),
    rollup: mapWorkflowSetRollup(raw.rollup),
  }
}

function mapWorkflowSetMemberResult(
  raw: RawWorkflowSetMemberResult | null | undefined,
): WorkflowSetMemberResult {
  return {
    workflowId: raw?.workflowId ?? null,
    market: raw?.market ?? null,
    name: raw?.name?.trim() || null,
    ok: !!raw?.ok,
    error: raw?.error ?? null,
    runRequestId: raw?.runRequestId ?? null,
  }
}

function mapDowDiscounts(raw: RawDowDiscounts | null | undefined): WorkflowDowDiscounts | null {
  if (!raw) return null
  return {
    weekend: typeof raw.weekend === 'number' ? raw.weekend : null,
    weekday: typeof raw.weekday === 'number' ? raw.weekday : null,
    defaultNights: typeof raw.defaultNights === 'number' ? raw.defaultNights : null,
  }
}

function mapWorkflowRules(
  raw: NonNullable<NonNullable<RawWorkflowDetail['policy']>['adjustment']>['rules'],
): WorkflowRule[] {
  return (raw ?? [])
    .filter((r): r is NonNullable<typeof r> => !!r)
    .map(r => ({
      maxDaysOut: typeof r.maxDaysOut === 'number' ? r.maxDaysOut : null,
      discounts: mapDowDiscounts(r.discounts),
      occupancyRanges: (r.occupancyRanges ?? [])
        .filter((o): o is NonNullable<typeof o> => !!o)
        .map(o => ({
          minPct: typeof o.minPct === 'number' ? o.minPct : null,
          maxPct: typeof o.maxPct === 'number' ? o.maxPct : null,
          discounts: mapDowDiscounts(o.discounts),
        })),
      metricRanges: (r.metricRanges ?? [])
        .filter((m): m is NonNullable<typeof m> => !!m)
        .map(m => ({
          metric: (m.metric ?? null) as WorkflowPropertyMetric | null,
          min: typeof m.min === 'number' ? m.min : null,
          max: typeof m.max === 'number' ? m.max : null,
          discounts: mapDowDiscounts(m.discounts),
        })),
    }))
}

function mapWorkflowPolicy(raw: NonNullable<RawWorkflowDetail['policy']>): WorkflowPolicy {
  const adjustment = raw.adjustment
    ? {
        type: (raw.adjustment.type ?? null) as WorkflowAdjustmentType | null,
        basis: raw.adjustment.basis ?? null,
        source: raw.adjustment.source ?? null,
        fees: compactStrings(raw.adjustment.fees),
        floor: raw.adjustment.floor
          ? { mode: (raw.adjustment.floor.mode ?? null) as WorkflowFloorMode | null }
          : null,
        tiers: (raw.adjustment.tiers ?? [])
          .filter((t): t is NonNullable<typeof t> => !!t)
          .map(t => ({
            discountPct: typeof t.discountPct === 'number' ? t.discountPct : null,
            maxDaysOut: typeof t.maxDaysOut === 'number' ? t.maxDaysOut : null,
          })),
        rules: mapWorkflowRules(raw.adjustment.rules),
      }
    : null

  const cohort = raw.cohort
    ? {
        type: (raw.cohort.type ?? null) as WorkflowCohortType | null,
        market: raw.cohort.market ?? null,
        bedrooms: typeof raw.cohort.bedrooms === 'number' ? raw.cohort.bedrooms : null,
        cohortIds: compactStrings(raw.cohort.cohortIds),
        propertyIds: compactStrings(raw.cohort.propertyIds),
      }
    : null

  const exclusions = (raw.exclusions ?? [])
    .filter((e): e is NonNullable<typeof e> => !!e)
    .map(e => ({
      type: (e.type ?? null) as WorkflowExclusionType | null,
      label: e.label ?? null,
      fingerprint: e.fingerprint ?? null,
      propertyIds: compactStrings(e.propertyIds),
      requiredColumns: compactStrings(e.requiredColumns),
      sql: e.sql ?? null,
    }))

  const trigger = raw.trigger
    ? {
        type: (raw.trigger.type ?? null) as WorkflowTriggerType | null,
        expr: raw.trigger.expr ?? null,
        timezone: raw.trigger.timezone ?? null,
      }
    : null

  return {
    name: raw.name ?? null,
    horizonDays: typeof raw.horizonDays === 'number' ? raw.horizonDays : null,
    routing: raw.routing ?? null,
    emits: compactStrings(raw.emits),
    adjustment,
    aiReview: raw.aiReview ? { enabled: !!raw.aiReview.enabled } : null,
    cohort,
    exclusions,
    trigger,
  }
}

function mapWorkflowDetail(raw: RawWorkflowDetail | null | undefined): WorkflowDetail | null {
  const summary = mapWorkflowSummary(raw)
  if (!summary) return null
  return {
    ...summary,
    createdByReviewerId: raw?.createdByReviewerId ?? null,
    updatedAt: raw?.updatedAt ? String(raw.updatedAt) : null,
    authoringSessionId: raw?.authoringSessionId ?? null,
    baseWorkflowId: raw?.baseWorkflowId ?? null,
    workflowSetId: raw?.workflowSetId ?? null,
    relatedWorkflows: (raw?.relatedWorkflows ?? [])
      .map(mapWorkflowSummary)
      .filter((wf): wf is WorkflowSummary => !!wf),
    policy: raw?.policy ? mapWorkflowPolicy(raw.policy) : null,
  }
}

/**
 * Lists all revenue-management workflows (summaries) for the Workflows page.
 */
export async function getWorkflows(opts?: {
  /** Hide workflows that belong to a set — the set is rendered as one parent row
   *  instead (mirrors how cohort_child decision rows are hidden). */
  excludeSetMembers?: boolean
}): Promise<WorkflowSummary[]> {
  const result = await arriereBackoffice.queries.revenueManagement({
    __alias: 'revenueAgentsWorkflows',
    workflows: {
      __args: { excludeSetMembers: opts?.excludeSetMembers ?? false },
      ...WORKFLOW_SUMMARY_FIELDS,
    },
  })

  return (result.workflows ?? [])
    .map(mapWorkflowSummary)
    .filter((wf): wf is WorkflowSummary => !!wf)
}

/** Lists every workflow set (each a multi-market fan-out) as a parent row. */
export async function getWorkflowSets(): Promise<WorkflowSet[]> {
  const result = await arriereBackoffice.queries.revenueManagement({
    __alias: 'revenueAgentsWorkflowSets',
    workflowSets: WORKFLOW_SET_FIELDS,
  })

  return (result.workflowSets ?? []).map(mapWorkflowSet).filter((set): set is WorkflowSet => !!set)
}

/**
 * Loads the full workflow (summary + nested policy) for the detail panel.
 */
export async function getWorkflow(id: string): Promise<WorkflowDetail | null> {
  const result = await arriereBackoffice.queries.revenueManagement({
    __alias: 'revenueAgentsWorkflow',
    workflow: {
      __args: { id },
      ...WORKFLOW_DETAIL_FIELDS,
    },
  })
  return mapWorkflowDetail(result.workflow)
}

// ── Workflow Authoring sessions (B7 chat history) ────────────────────────
// Read-only browse of past authoring conversations — parity with the
// execution-chat history. The live chat itself is still driven through the
// SSE routes (see workflowAuthoringChatApi.ts); these queries only power the
// history list + a past session's transcript.

export type WorkflowAuthoringSessionStatus = 'open' | 'closed' | string
export type WorkflowAuthoringMessageRole = 'human' | 'assistant' | 'tool' | 'system'

export interface WorkflowAuthoringSessionSummary {
  id: string
  /** AI-generated short title for the History list; null for untitled/legacy sessions. */
  title: string | null
  reviewerId: string | null
  reviewerName: string
  status: WorkflowAuthoringSessionStatus
  agentProvider: string | null
  agentModel: string | null
  schemaContextVersion: string | null
  createdAt: string | null
  closedAt: string | null
  messageCount: number
  /** The workflow this chat produced (drafted), or null. */
  createdWorkflowId: string | null
  /** The workflow this chat was opened to edit/iterate on, or null. */
  baseWorkflowId: string | null
}

export interface WorkflowAuthoringMessage {
  id: string
  messageOrder: number
  role: WorkflowAuthoringMessageRole
  content: string | null
  toolName: string | null
  toolCallId: string | null
  toolInput: unknown
  toolOutput: unknown
  createdAt: string | null
}

export interface WorkflowAuthoringSessionDetail extends WorkflowAuthoringSessionSummary {
  /** The candidate policy as last left in the session (opaque JSON). */
  workingPolicy: unknown
  messages: WorkflowAuthoringMessage[]
}

const WORKFLOW_AUTHORING_SESSION_SUMMARY_FIELDS = {
  id: true,
  title: true,
  reviewerId: true,
  reviewerName: true,
  status: true,
  agentProvider: true,
  agentModel: true,
  schemaContextVersion: true,
  createdAt: true,
  closedAt: true,
  messageCount: true,
  createdWorkflowId: true,
  baseWorkflowId: true,
} as const

const WORKFLOW_AUTHORING_MESSAGE_FIELDS = {
  id: true,
  messageOrder: true,
  role: true,
  content: true,
  toolName: true,
  toolCallId: true,
  toolInput: true,
  toolOutput: true,
  createdAt: true,
} as const

type RawWorkflowAuthoringMessage = {
  id?: string | null
  messageOrder?: number | null
  role?: string | null
  content?: string | null
  toolName?: string | null
  toolCallId?: string | null
  toolInput?: unknown
  toolOutput?: unknown
  createdAt?: unknown
}

type RawWorkflowAuthoringSession = {
  id?: string | null
  title?: string | null
  reviewerId?: string | null
  reviewerName?: string | null
  status?: string | null
  agentProvider?: string | null
  agentModel?: string | null
  schemaContextVersion?: string | null
  createdAt?: unknown
  closedAt?: unknown
  messageCount?: number | null
  createdWorkflowId?: string | null
  baseWorkflowId?: string | null
  workingPolicy?: unknown
  messages?: Array<RawWorkflowAuthoringMessage | null> | null
}

function mapWorkflowAuthoringSessionSummary(
  raw: RawWorkflowAuthoringSession | null | undefined,
): WorkflowAuthoringSessionSummary | null {
  if (!raw?.id) return null
  return {
    id: raw.id,
    title: raw.title ?? null,
    reviewerId: raw.reviewerId ?? null,
    reviewerName: raw.reviewerName ?? '',
    status: (raw.status ?? 'open') as WorkflowAuthoringSessionStatus,
    agentProvider: raw.agentProvider ?? null,
    agentModel: raw.agentModel ?? null,
    schemaContextVersion: raw.schemaContextVersion ?? null,
    createdAt: raw.createdAt ? String(raw.createdAt) : null,
    closedAt: raw.closedAt ? String(raw.closedAt) : null,
    messageCount: typeof raw.messageCount === 'number' ? raw.messageCount : 0,
    createdWorkflowId: raw.createdWorkflowId ?? null,
    baseWorkflowId: raw.baseWorkflowId ?? null,
  }
}

function mapWorkflowAuthoringMessage(
  raw: RawWorkflowAuthoringMessage | null | undefined,
  idx: number,
): WorkflowAuthoringMessage | null {
  if (!raw) return null
  return {
    id: raw.id ?? `msg-${idx}`,
    messageOrder: typeof raw.messageOrder === 'number' ? raw.messageOrder : idx,
    role: (raw.role ?? 'assistant') as WorkflowAuthoringMessageRole,
    content: raw.content ?? null,
    toolName: raw.toolName ?? null,
    toolCallId: raw.toolCallId ?? null,
    toolInput: raw.toolInput ?? null,
    toolOutput: raw.toolOutput ?? null,
    createdAt: raw.createdAt ? String(raw.createdAt) : null,
  }
}

export interface WorkflowAuthoringSessionsFilters {
  reviewerId?: string
  baseWorkflowId?: string
  status?: string
  limit?: number
  offset?: number
}

/**
 * Lists past workflow-authoring chat sessions (most recent first) for the
 * read-only history browser.
 */
export async function getWorkflowAuthoringSessions(
  filters: WorkflowAuthoringSessionsFilters = {},
): Promise<WorkflowAuthoringSessionSummary[]> {
  const result = await arriereBackoffice.queries.revenueManagement({
    __alias: 'revenueAgentsWorkflowAuthoringSessions',
    workflowAuthoringSessions: {
      __args: {
        reviewerId: filters.reviewerId || undefined,
        baseWorkflowId: filters.baseWorkflowId || undefined,
        status: filters.status || undefined,
        limit: filters.limit ?? 25,
        offset: filters.offset ?? 0,
      },
      ...WORKFLOW_AUTHORING_SESSION_SUMMARY_FIELDS,
    },
  })
  return (result.workflowAuthoringSessions ?? [])
    .map(mapWorkflowAuthoringSessionSummary)
    .filter((s): s is WorkflowAuthoringSessionSummary => !!s)
}

/**
 * Loads one authoring session with its full transcript, for the read-only
 * history detail view.
 */
export async function getWorkflowAuthoringSession(
  id: string,
): Promise<WorkflowAuthoringSessionDetail | null> {
  const result = await arriereBackoffice.queries.revenueManagement({
    __alias: 'revenueAgentsWorkflowAuthoringSession',
    workflowAuthoringSession: {
      __args: { id },
      ...WORKFLOW_AUTHORING_SESSION_SUMMARY_FIELDS,
      workingPolicy: true,
      messages: WORKFLOW_AUTHORING_MESSAGE_FIELDS,
    },
  })
  const raw = result.workflowAuthoringSession as RawWorkflowAuthoringSession | null | undefined
  const summary = mapWorkflowAuthoringSessionSummary(raw)
  if (!summary) return null
  return {
    ...summary,
    workingPolicy: raw?.workingPolicy ?? null,
    messages: (raw?.messages ?? [])
      .map((m, idx) => mapWorkflowAuthoringMessage(m, idx))
      .filter((m): m is WorkflowAuthoringMessage => !!m),
  }
}

export interface WorkflowRunStats {
  children: number | null
  error: string | null
  excludedMembers: number | null
  membersSelected: number | null
  nightsDiscounted: number | null
  nightsFloorCapped: number | null
  nightsSkippedFloor: number | null
  proposals: number | null
  wouldProduceNothing: number | null
}

/** One execution of a workflow (from `RevenueManagementQueries.workflowRuns`). */
export interface WorkflowRun {
  id: string
  runAt: string | null
  scheduledFor: string | null
  status: WorkflowRunStatus | null
  decisionsCreated: number | null
  triggeredBy: string | null
  stats: WorkflowRunStats | null
}

const WORKFLOW_RUN_FIELDS = {
  id: true,
  runAt: true,
  scheduledFor: true,
  status: true,
  decisionsCreated: true,
  triggeredBy: true,
  stats: {
    children: true,
    error: true,
    excludedMembers: true,
    membersSelected: true,
    nightsDiscounted: true,
    nightsFloorCapped: true,
    nightsSkippedFloor: true,
    proposals: true,
    wouldProduceNothing: true,
  },
} as const

type RawWorkflowRun = {
  id?: string | null
  runAt?: unknown
  scheduledFor?: unknown
  status?: RevenueManagementWorkflowRunStatus | null
  decisionsCreated?: number | null
  triggeredBy?: string | null
  stats?: {
    children?: number | null
    error?: string | null
    excludedMembers?: number | null
    membersSelected?: number | null
    nightsDiscounted?: number | null
    nightsFloorCapped?: number | null
    nightsSkippedFloor?: number | null
    proposals?: number | null
    wouldProduceNothing?: number | null
  } | null
}

function mapWorkflowRun(raw: RawWorkflowRun | null | undefined): WorkflowRun | null {
  if (!raw?.id) return null
  return {
    id: raw.id,
    runAt: raw.runAt ? String(raw.runAt) : null,
    scheduledFor: raw.scheduledFor ? String(raw.scheduledFor) : null,
    status: (raw.status ?? null) as WorkflowRunStatus | null,
    decisionsCreated: typeof raw.decisionsCreated === 'number' ? raw.decisionsCreated : null,
    triggeredBy: raw.triggeredBy ?? null,
    stats: raw.stats
      ? {
          children: typeof raw.stats.children === 'number' ? raw.stats.children : null,
          error: raw.stats.error ?? null,
          excludedMembers:
            typeof raw.stats.excludedMembers === 'number' ? raw.stats.excludedMembers : null,
          membersSelected:
            typeof raw.stats.membersSelected === 'number' ? raw.stats.membersSelected : null,
          nightsDiscounted:
            typeof raw.stats.nightsDiscounted === 'number' ? raw.stats.nightsDiscounted : null,
          nightsFloorCapped:
            typeof raw.stats.nightsFloorCapped === 'number' ? raw.stats.nightsFloorCapped : null,
          nightsSkippedFloor:
            typeof raw.stats.nightsSkippedFloor === 'number' ? raw.stats.nightsSkippedFloor : null,
          proposals: typeof raw.stats.proposals === 'number' ? raw.stats.proposals : null,
          wouldProduceNothing:
            typeof raw.stats.wouldProduceNothing === 'number'
              ? raw.stats.wouldProduceNothing
              : null,
        }
      : null,
  }
}

export const WORKFLOW_RUN_DECISIONS_PAGE_SIZE = 10

export async function getWorkflowRunsPage(workflowId: string, page = 1) {
  const { data, errors } = await arriereBackoffice.queries.revenueManagement.raw({
    __alias: 'revenueAgentsWorkflowRunsPage',
    workflowRunsPage: {
      __args: { workflowId, pagination: { page, size: RUN_LOOKUP_WINDOW } },
      results: WORKFLOW_RUN_FIELDS,
      hasMore: true,
    },
  })
  const result = data?.workflowRunsPage
  if (errors?.length || !result?.results || typeof result.hasMore !== 'boolean') {
    throw new Error('Failed to load executions.')
  }
  return {
    results: result.results.map(mapWorkflowRun).filter((run): run is WorkflowRun => !!run),
    hasMore: result.hasMore,
  }
}

export async function getWorkflowRunDecisions(runId: string, page = 1) {
  const { data, errors } = await arriereBackoffice.queries.revenueManagement.raw({
    __alias: 'revenueAgentsWorkflowRunDecisions',
    workflowRun: {
      __args: { id: runId },
      ...WORKFLOW_RUN_FIELDS,
      decisions: {
        __args: { pagination: { page, size: WORKFLOW_RUN_DECISIONS_PAGE_SIZE } },
        results: {
          id: true,
          decisionType: true,
          status: true,
          createdAt: true,
          reviewedAt: true,
          summaryChanging: true,
          decisionScope: true,
          cohortType: true,
          totalUnits: true,
          property: { id: true, name: true },
        },
        hasMore: true,
      },
    },
  })
  if (errors?.length || !data || data.workflowRun === undefined) {
    throw new Error('Failed to load execution decisions.')
  }
  if (data.workflowRun === null) return null
  const run = mapWorkflowRun(data.workflowRun)
  const result = data.workflowRun.decisions
  if (!run || !result?.results || typeof result.hasMore !== 'boolean') {
    throw new Error('Failed to load execution decisions.')
  }
  return {
    run,
    results: result.results.flatMap(decision =>
      decision?.id ? [{ ...decision, id: decision.id }] : [],
    ),
    hasMore: result.hasMore,
  }
}

export type WorkflowRunDecision = NonNullable<
  Awaited<ReturnType<typeof getWorkflowRunDecisions>>
>['results'][number]

// ---------------------------------------------------------------------------
// Workflow-run exclusions (cohort review)
// ---------------------------------------------------------------------------

export type WorkflowRunExclusionStage = `${RevenueManagementWorkflowExclusionStage}`

export interface WorkflowRunExclusion {
  propertyId: string | null
  label: string | null
  reason: string | null
  stage: WorkflowRunExclusionStage | null
  /** Which BE field the row came from (`exclusions` vs `notSelected`). */
  source: 'excluded' | 'not_selected'
}

// ---------------------------------------------------------------------------
// Yield chart (run evidence snapshot)
// Snapshot of what the AI saw at judgment time, not today's warehouse.
// `curvesAsOf` (warehouse capture) and `judgedAt` (when the model reasoned)
// usually differ — surface both.
// ---------------------------------------------------------------------------

/** Booked nights on a stay date by booking lead time: dod = same day, week1 = 1–6d,
 *  week2 = 7–13d, week3 = 14–20d, week4 = 21–27d, week5Plus = 28d+. Sums to `reserved`. */
export interface YieldChartPickupNights {
  dod: number | null
  week1: number | null
  week2: number | null
  week3: number | null
  week4: number | null
  week5Plus: number | null
}

/** One stay date. Null metrics are gaps, never zero — chart lines must break. */
export interface YieldChartDay {
  date: string
  ourOcc: number | null
  ourOccTransient: number | null
  longStayShare: number | null
  marketOcc: number | null
  ourPickup: number | null
  ourPickupNights: YieldChartPickupNights | null
  marketPickup: number | null
  sellable: number | null
  reserved: number | null
  blockedNights: number | null
  marketReserved: number | null
  marketSellable: number | null
  ourAsk: number | null
  marketAsk: number | null
}

/** One verdict window. `action` is 'cut' or 'none'. Use server-resolved `nights`;
 *  don't re-derive from `dayFilter` (a full-range shade would include excluded nights). */
export interface YieldChartWindow {
  startDate: string | null
  endDate: string | null
  action: string | null
  dayFilter: string | null
  nights: string[]
  magnitudePct: number | null
  confidence: number | null
  marketOccPct: number | null
  rationale: string | null
  flags: string[]
  /** Hotel runs judge each ROOM TYPE separately, so every window names the one
   *  it belongs to. Null on market runs, which judge the cohort as a whole. */
  roomTypeId: string | null
  roomType: string | null
}

export interface YieldChartMeta {
  homesNow: number | null
  stlyHomes: number | null
  /** False = same-store base too thin to cite YoY. */
  stlyUsable: boolean | null
  horizonDays: number | null
}

export interface YieldChartMarket {
  /** An RM market name, or `hotel:<uuid>` for a hotel yield run. */
  market: string
  /** Display name — the building for a hotel run. Falls back to `market`. */
  label: string
  days: YieldChartDay[]
  windows: YieldChartWindow[]
  meta: YieldChartMeta | null
}

export interface YieldChart {
  runId: string | null
  model: string | null
  curvesAsOf: string | null
  /** Null + empty windows = judgment failed; curves still render unshaded. */
  judgedAt: string | null
  markets: YieldChartMarket[]
}

/** Exclusions + yield-chart snapshot for the run that produced a cohort.
 *  `yieldChart: null` is a real answer — show "no chart", not an error. */
export interface WorkflowRunEvidence {
  runId: string
  excluded: WorkflowRunExclusion[]
  yieldChart: YieldChart | null
  /** True = the chart query itself failed, so "no chart" would be a lie. */
  yieldChartUnavailable: boolean
}

interface YieldChartResult {
  chart: YieldChart | null
  unavailable: boolean
}

const WORKFLOW_RUN_EXCLUSION_FIELDS = {
  propertyId: true,
  label: true,
  reason: true,
  stage: true,
} as const

type RawWorkflowRunExclusion = {
  propertyId?: string | null
  label?: string | null
  reason?: string | null
  stage?: RevenueManagementWorkflowExclusionStage | null
}

function mapWorkflowRunExclusion(
  raw: RawWorkflowRunExclusion | null | undefined,
  source: WorkflowRunExclusion['source'],
): WorkflowRunExclusion | null {
  if (!raw || (!raw.propertyId && !raw.label && !raw.reason)) return null
  return {
    propertyId: raw.propertyId ?? null,
    label: raw.label ?? null,
    reason: raw.reason ?? null,
    stage: (raw.stage ?? null) as WorkflowRunExclusionStage | null,
    source,
  }
}

const YIELD_CHART_FIELDS = {
  runId: true,
  model: true,
  curvesAsOf: true,
  judgedAt: true,
  markets: {
    market: true,
    label: true,
    meta: { homesNow: true, stlyHomes: true, stlyUsable: true, horizonDays: true },
    days: {
      date: true,
      ourOcc: true,
      ourOccTransient: true,
      longStayShare: true,
      marketOcc: true,
      ourPickup: true,
      ourPickupNights: {
        dod: true,
        week1: true,
        week2: true,
        week3: true,
        week4: true,
        week5Plus: true,
      },
      marketPickup: true,
      sellable: true,
      reserved: true,
      blockedNights: true,
      marketReserved: true,
      marketSellable: true,
      ourAsk: true,
      marketAsk: true,
    },
    windows: {
      startDate: true,
      endDate: true,
      action: true,
      dayFilter: true,
      nights: true,
      magnitudePct: true,
      confidence: true,
      marketOccPct: true,
      rationale: true,
      flags: true,
      roomTypeId: true,
      roomType: true,
    },
  },
} as const

const numberOrNull = (value: unknown): number | null => (typeof value === 'number' ? value : null)

const pickupNightsOrNull = (value: unknown): YieldChartPickupNights | null => {
  if (!value || typeof value !== 'object') return null
  const bands = value as Record<string, unknown>
  return {
    dod: numberOrNull(bands.dod),
    week1: numberOrNull(bands.week1),
    week2: numberOrNull(bands.week2),
    week3: numberOrNull(bands.week3),
    week4: numberOrNull(bands.week4),
    week5Plus: numberOrNull(bands.week5Plus),
  }
}

type RawYieldChartDay = (Record<string, unknown> & { date?: string | null }) | null

type RawYieldChartWindow =
  | (Record<string, unknown> & {
      startDate?: string | null
      endDate?: string | null
      nights?: Array<string | null> | null
      flags?: Array<string | null> | null
    })
  | null

type RawYieldChartMarket = {
  market?: string | null
  label?: string | null
  meta?: {
    homesNow?: number | null
    stlyHomes?: number | null
    stlyUsable?: boolean | null
    horizonDays?: number | null
  } | null
  days?: RawYieldChartDay[] | null
  windows?: RawYieldChartWindow[] | null
} | null

type RawYieldChart = {
  runId?: string | null
  model?: string | null
  curvesAsOf?: string | null
  judgedAt?: string | null
  markets?: RawYieldChartMarket[] | null
} | null

function mapYieldChartMarket(market: NonNullable<RawYieldChartMarket>): YieldChartMarket {
  return {
    market: market.market ?? '',
    label: market.label || market.market || '',
    meta: market.meta
      ? {
          homesNow: numberOrNull(market.meta.homesNow),
          stlyHomes: numberOrNull(market.meta.stlyHomes),
          stlyUsable: typeof market.meta.stlyUsable === 'boolean' ? market.meta.stlyUsable : null,
          horizonDays: numberOrNull(market.meta.horizonDays),
        }
      : null,
    days: (market.days ?? [])
      .filter((day): day is Record<string, unknown> & { date: string } => !!day?.date)
      .map(day => ({
        date: day.date,
        ourOcc: numberOrNull(day.ourOcc),
        ourOccTransient: numberOrNull(day.ourOccTransient),
        longStayShare: numberOrNull(day.longStayShare),
        marketOcc: numberOrNull(day.marketOcc),
        ourPickup: numberOrNull(day.ourPickup),
        ourPickupNights: pickupNightsOrNull(day.ourPickupNights),
        marketPickup: numberOrNull(day.marketPickup),
        sellable: numberOrNull(day.sellable),
        reserved: numberOrNull(day.reserved),
        blockedNights: numberOrNull(day.blockedNights),
        marketReserved: numberOrNull(day.marketReserved),
        marketSellable: numberOrNull(day.marketSellable),
        ourAsk: numberOrNull(day.ourAsk),
        marketAsk: numberOrNull(day.marketAsk),
      }))
      .sort((a, b) => (a.date < b.date ? -1 : 1)),
    windows: (market.windows ?? [])
      .filter((window): window is NonNullable<RawYieldChartWindow> => !!window)
      .map(window => ({
        startDate: window.startDate ?? null,
        endDate: window.endDate ?? null,
        action: typeof window.action === 'string' ? window.action : null,
        dayFilter: typeof window.dayFilter === 'string' ? window.dayFilter : null,
        nights: (window.nights ?? []).filter((night): night is string => !!night),
        magnitudePct: numberOrNull(window.magnitudePct),
        confidence: numberOrNull(window.confidence),
        marketOccPct: numberOrNull(window.marketOccPct),
        rationale: typeof window.rationale === 'string' ? window.rationale : null,
        flags: (window.flags ?? []).filter((flag): flag is string => !!flag),
        roomTypeId: typeof window.roomTypeId === 'string' ? window.roomTypeId : null,
        roomType: typeof window.roomType === 'string' ? window.roomType : null,
      })),
  }
}

function mapYieldChart(raw: RawYieldChart | undefined): YieldChart | null {
  if (!raw) return null
  return {
    runId: raw.runId ?? null,
    model: raw.model ?? null,
    curvesAsOf: raw.curvesAsOf ?? null,
    judgedAt: raw.judgedAt ?? null,
    markets: (raw.markets ?? [])
      .filter((market): market is NonNullable<RawYieldChartMarket> => !!market)
      .map(mapYieldChartMarket),
  }
}

/**
 * Recent-runs window the runs chart caps its clickable points to.
 */
export const RUN_LOOKUP_WINDOW = 30

// `exclusions` and `notSelected` should be disjoint; dedupe so a review list
// shows one row per property.
function dedupeRunExclusionRows(run: {
  exclusions?: Array<RawWorkflowRunExclusion | null> | null
  notSelected?: Array<RawWorkflowRunExclusion | null> | null
}): WorkflowRunExclusion[] {
  const seen = new Set<string>()
  const excluded: WorkflowRunExclusion[] = []
  const sourced = [
    ...(run.exclusions ?? []).map(raw => ({ raw, source: 'excluded' as const })),
    ...(run.notSelected ?? []).map(raw => ({ raw, source: 'not_selected' as const })),
  ]
  for (const { raw, source } of sourced) {
    const mapped = mapWorkflowRunExclusion(raw, source)
    if (!mapped) continue
    const key = mapped.propertyId ?? `label:${mapped.label}:${mapped.reason}`
    if (seen.has(key)) continue
    seen.add(key)
    excluded.push(mapped)
  }
  return excluded
}

// Never selected alongside the exclusions: a backend without `yieldChart`
// rejects the whole document, which would blank the excluded list too.
async function fetchRunYieldChart(workflowRunId: string): Promise<YieldChartResult> {
  try {
    const result = await arriereBackoffice.queries.revenueManagement({
      __alias: 'revenueAgentsWorkflowRunYieldChart',
      workflowRun: {
        __args: { id: workflowRunId },
        id: true,
        yieldChart: YIELD_CHART_FIELDS,
      },
    })
    const run = result.workflowRun
    if (!run) return { chart: null, unavailable: false }
    return { chart: mapYieldChart(run.yieldChart as RawYieldChart), unavailable: false }
  } catch {
    return { chart: null, unavailable: true }
  }
}

/**
 * Exclusions for ONE run (PRI-1565's run surface).
 *
 * Deliberately never fetched across a runs list: `notSelected` performs a
 * warehouse read per run. Null = no run has that id, unlike an empty list
 * which is a real "nothing was excluded".
 */
export async function getWorkflowRunExclusions(
  workflowRunId: string,
): Promise<WorkflowRunExclusion[] | null> {
  const result = await arriereBackoffice.queries.revenueManagement({
    __alias: 'revenueAgentsRunExclusions',
    workflowRun: {
      __args: { id: workflowRunId },
      id: true,
      exclusions: WORKFLOW_RUN_EXCLUSION_FIELDS,
      notSelected: WORKFLOW_RUN_EXCLUSION_FIELDS,
    },
  })
  const run = result.workflowRun
  if (!run) return null
  return dedupeRunExclusionRows(run)
}

export async function findWorkflowRunEvidence(
  workflowRunId: string,
): Promise<WorkflowRunEvidence | null> {
  const [result, yieldResult] = await Promise.all([
    arriereBackoffice.queries.revenueManagement({
      __alias: 'revenueAgentsWorkflowRunEvidence',
      workflowRun: {
        __args: { id: workflowRunId },
        id: true,
        exclusions: WORKFLOW_RUN_EXCLUSION_FIELDS,
        notSelected: WORKFLOW_RUN_EXCLUSION_FIELDS,
      },
    }),
    fetchRunYieldChart(workflowRunId),
  ])

  const run = result.workflowRun
  if (!run) return null

  return {
    runId: workflowRunId,
    excluded: dedupeRunExclusionRows(run),
    yieldChart: yieldResult.chart,
    yieldChartUnavailable: yieldResult.unavailable,
  }
}

export async function pauseWorkflow(id: string): Promise<WorkflowSummary | null> {
  const result = await arriereBackoffice.mutations.revenueManagement({
    __alias: 'pauseWorkflow',
    pauseWorkflow: {
      __args: { id },
      ...WORKFLOW_SUMMARY_FIELDS,
    },
  })
  return mapWorkflowSummary(result.pauseWorkflow)
}

/**
 * Validates and activates a draft workflow. The returned workflow and compiled
 * policy are surfaced in the activation modal.
 */
export async function activateWorkflow(id: string): Promise<WorkflowDetail | null> {
  const result = await arriereBackoffice.mutations.revenueManagement({
    __alias: 'activateWorkflow',
    activateWorkflow: {
      __args: { id },
      ...WORKFLOW_DETAIL_FIELDS,
    },
  })
  return mapWorkflowDetail(result.activateWorkflow)
}

/**
 * Soft-deletes a workflow by archiving it.
 */
export async function archiveWorkflow(id: string): Promise<WorkflowSummary | null> {
  const result = await arriereBackoffice.mutations.revenueManagement({
    __alias: 'archiveWorkflow',
    archiveWorkflow: {
      __args: { id },
      ...WORKFLOW_SUMMARY_FIELDS,
    },
  })
  return mapWorkflowSummary(result.archiveWorkflow)
}

/**
 * Creates a new cohort pricing workflow from authored controls.
 * Backend compiles controls → policy and creates the workflow as a draft.
 */
export async function createWorkflow(
  controls: CreateWorkflowControls,
): Promise<WorkflowSummary | null> {
  const result = await arriereBackoffice.mutations.revenueManagement({
    __alias: 'createWorkflow',
    createWorkflow: {
      __args: { controls },
      ...WORKFLOW_SUMMARY_FIELDS,
    },
  })
  return mapWorkflowSummary(result.createWorkflow)
}

/**
 * Approve a B7 policy-author chat session's assembled policy: saves it as a
 * DRAFT workflow(s). Gated server-side — the current policy must have a
 * completed, clean (not exclusion-matched-all) dry-run, and the approval is
 * bound to that dry-run's fingerprint. If the operator edited the policy after
 * the last dry-run, this THROWS (re-run the dry-run first). Multi-market
 * sessions return one independently successful or refused result per market.
 */
const WORKFLOW_AUTHORING_SAVE_RESULT_FIELDS = {
  workflowId: true,
  name: true,
  market: true,
  error: true,
} as const

export async function createWorkflowFromAuthoringSession(
  sessionId: string,
): Promise<WorkflowAuthoringSaveResult[]> {
  const result = await arriereBackoffice.mutations.revenueManagement({
    __alias: 'createWorkflowFromAuthoringSession',
    createWorkflowFromAuthoringSession: {
      __args: { sessionId },
      ...WORKFLOW_AUTHORING_SAVE_RESULT_FIELDS,
    },
  })

  return (result.createWorkflowFromAuthoringSession ?? []).map(saveResult => ({
    workflowId: saveResult.workflowId ?? null,
    name: saveResult.name?.trim() || 'Untitled workflow',
    market: saveResult.market ?? null,
    error: saveResult.error ?? null,
  }))
}

// ── Workflow Set operations ──────────────────────────────────────────────────
// All set operations return one result PER MEMBER — partial success is normal
// (a market the operator doesn't own is refused while the rest succeed). Callers
// must render the per-member breakdown, never a blanket success.

/**
 * Edits the targeted set (or adds a batch of markets to it) from a B7 authoring
 * session. This is the routing target when a `workflow_proposed` SSE event
 * carries a non-null `target_workflow_set_id` — sending such a proposal to
 * `createWorkflowFromAuthoringSession` instead would mint a brand-new orphan set.
 */
export async function updateWorkflowSetFromAuthoringSession(
  sessionId: string,
): Promise<WorkflowSetMemberResult[]> {
  const result = await arriereBackoffice.mutations.revenueManagement({
    __alias: 'updateWorkflowSetFromAuthoringSession',
    updateWorkflowSetFromAuthoringSession: {
      __args: { sessionId },
      ...WORKFLOW_SET_MEMBER_RESULT_FIELDS,
    },
  })
  return (result.updateWorkflowSetFromAuthoringSession ?? []).map(mapWorkflowSetMemberResult)
}

/** Activates every member of a set ("activate all"). */
export async function activateWorkflowSet(
  workflowSetId: string,
): Promise<WorkflowSetMemberResult[]> {
  const result = await arriereBackoffice.mutations.revenueManagement({
    __alias: 'activateWorkflowSet',
    activateWorkflowSet: {
      __args: { workflowSetId },
      ...WORKFLOW_SET_MEMBER_RESULT_FIELDS,
    },
  })
  return (result.activateWorkflowSet ?? []).map(mapWorkflowSetMemberResult)
}

/** Pauses every member of a set. */
export async function pauseWorkflowSet(workflowSetId: string): Promise<WorkflowSetMemberResult[]> {
  const result = await arriereBackoffice.mutations.revenueManagement({
    __alias: 'pauseWorkflowSet',
    pauseWorkflowSet: {
      __args: { workflowSetId },
      ...WORKFLOW_SET_MEMBER_RESULT_FIELDS,
    },
  })
  return (result.pauseWorkflowSet ?? []).map(mapWorkflowSetMemberResult)
}

/** Archives every member of a set (the set itself persists). */
export async function archiveWorkflowSet(
  workflowSetId: string,
): Promise<WorkflowSetMemberResult[]> {
  const result = await arriereBackoffice.mutations.revenueManagement({
    __alias: 'archiveWorkflowSet',
    archiveWorkflowSet: {
      __args: { workflowSetId },
      ...WORKFLOW_SET_MEMBER_RESULT_FIELDS,
    },
  })
  return (result.archiveWorkflowSet ?? []).map(mapWorkflowSetMemberResult)
}

/**
 * Triggers an off-schedule run of every active member. Each result carries a
 * `runRequestId` — poll each via `getWorkflowRunRequest` (same as `runWorkflowNow`).
 */
export async function runWorkflowSetNow(workflowSetId: string): Promise<WorkflowSetMemberResult[]> {
  const result = await arriereBackoffice.mutations.revenueManagement({
    __alias: 'runWorkflowSetNow',
    runWorkflowSetNow: {
      __args: { workflowSetId },
      ...WORKFLOW_SET_MEMBER_RESULT_FIELDS,
    },
  })
  return (result.runWorkflowSetNow ?? []).map(mapWorkflowSetMemberResult)
}

/** Removes (archives) a single market from a set. The set persists. */
export async function removeMarketFromSet(
  workflowSetId: string,
  workflowId: string,
): Promise<WorkflowSetMemberResult> {
  const result = await arriereBackoffice.mutations.revenueManagement({
    __alias: 'removeMarketFromSet',
    removeMarketFromSet: {
      __args: { workflowSetId, workflowId },
      ...WORKFLOW_SET_MEMBER_RESULT_FIELDS,
    },
  })
  return mapWorkflowSetMemberResult(result.removeMarketFromSet)
}

/** Renames a set — a cosmetic label change that cascades to member names. */
export async function renameWorkflowSet(
  workflowSetId: string,
  name: string,
): Promise<WorkflowSet | null> {
  const result = await arriereBackoffice.mutations.revenueManagement({
    __alias: 'renameWorkflowSet',
    renameWorkflowSet: {
      __args: { workflowSetId, name },
      ...WORKFLOW_SET_FIELDS,
    },
  })
  return mapWorkflowSet(result.renameWorkflowSet)
}

export type WorkflowRunRequestStatus = `${RevenueManagementWorkflowRunRequestStatus}`

export interface WorkflowRunRequest {
  id: string | null
  runId: string | null
  workflowId: string | null
  status: WorkflowRunRequestStatus | null
  requestedAt: string | null
  requestedBy: string | null
  error: string | null
  finishedAt?: string | null
}

export const WORKFLOW_RUN_REQUEST_FIELDS = {
  id: true,
  runId: true,
  workflowId: true,
  status: true,
  requestedAt: true,
  requestedBy: true,
  finishedAt: true,
  error: true,
} as const

type RawWorkflowRunRequest = {
  id?: string | null
  runId?: string | null
  workflowId?: string | null
  status?: RevenueManagementWorkflowRunRequestStatus | null
  requestedAt?: unknown
  requestedBy?: string | null
  finishedAt?: unknown
  error?: string | null
}

export function mapWorkflowRunRequest(
  raw: RawWorkflowRunRequest | null | undefined,
): WorkflowRunRequest | null {
  if (!raw) return null
  return {
    id: raw.id ?? null,
    runId: raw.runId ?? null,
    workflowId: raw.workflowId ?? null,
    status: (raw.status as WorkflowRunRequestStatus | undefined) ?? null,
    requestedAt: raw.requestedAt ? String(raw.requestedAt) : null,
    requestedBy: raw.requestedBy ?? null,
    finishedAt: raw.finishedAt ? String(raw.finishedAt) : null,
    error: raw.error ?? null,
  }
}

/**
 * Triggers an off-schedule run of an active workflow. The backend queues a run
 * request; the returned status reflects the queued/running state, not the final
 * run outcome (which shows up later in the workflow's runs history).
 */
export async function runWorkflowNow(id: string): Promise<WorkflowRunRequest | null> {
  const result = await arriereBackoffice.mutations.revenueManagement({
    __alias: 'runWorkflowNow',
    runWorkflowNow: {
      __args: { id },
      ...WORKFLOW_RUN_REQUEST_FIELDS,
    },
  })

  return mapWorkflowRunRequest(result.runWorkflowNow)
}

/**
 * Poll the state of a run request created by `runWorkflowNow`. `id` is the
 * run-request id (`WorkflowRunRequest.id`), not the workflow id. Poll (~every
 * 2.5s) while `status` is `pending`/`running` until it reaches `done`/`error`.
 */
export async function getWorkflowRunRequest(id: string): Promise<WorkflowRunRequest | null> {
  const result = await arriereBackoffice.queries.revenueManagement({
    __alias: 'workflowRunRequest',
    workflowRunRequest: {
      __args: { id },
      ...WORKFLOW_RUN_REQUEST_FIELDS,
    },
  })

  return mapWorkflowRunRequest(result.workflowRunRequest)
}

// ---------------------------------------------------------------------------
// Auto-execution
//
// Decisions a policy has scheduled to execute on their own once a veto window
// closes. Everything here is read-only except `cancelAutoExecution` — the veto
// itself.
//
// The whole auto-execution GraphQL surface is nullable (there is not one `!` on
// any output field), so these mappers are the single place nullability is
// resolved: callers get rows whose identity field is guaranteed and whose
// optional fields are explicitly `| null`. Rows arriving without their identity
// key are dropped rather than rendered under an undefined React key.
// ---------------------------------------------------------------------------

/** `decision_dispatch_queue.status` values that reach `autoExecutionQueue`.
 *  Deferred rows are NOT here — they surface under `autoExecutionBlocked` with
 *  `disposition: 'capacity'`, so the two views never double-count. */
export type AutoExecQueueStatus = 'pending' | 'claimed'

/** `verdict` — a gate decided against it, a human owns it now, no retry.
 *  `capacity` — still eligible, waiting on headroom, retried automatically.
 *  Note the wire value is `capacity`; the backend's internal type calls the
 *  same concept `defer`. Never switch on `defer`. */
export type AutoExecBlockedDisposition = 'verdict' | 'capacity'

/**
 * `drift` is the odd one out: live Wheelhouse state changed during the veto
 * window, so auto-execution declined to overwrite an operator's edit and left
 * the decision `rejected_stale`. It arrives in this list, but it is an outcome
 * rather than a fault and nobody needs to act on it — see FAILURE_STATUS_COPY.
 */
export type AutoExecFailureStatus = 'failed' | 'dead' | 'aged_out' | 'drift'

export type AutoExecEventType = 'auto_exec_fired' | 'auto_exec_reverted'

/** Thrown by `cancelAutoExecution` (HTTP 409 in `extensions.statusCode`) when
 *  the row is mid-dispatch or has already fired. The server's message
 *  distinguishes the two — surface it verbatim rather than string-matching. */
export const AUTO_EXECUTION_NOT_CANCELLABLE = 'AUTO_EXECUTION_NOT_CANCELLABLE'

/** The date span a row touches, unioned across its members. */
export interface AutoExecTargetWindow {
  start: string | null
  end: string | null
}

/**
 * One listing inside a row.
 *
 * Only the cohort PARENT is enqueued, so a single row can stand for up to ~95
 * listings. A single-property decision returns exactly one member.
 */
export interface AutoExecMember {
  decisionId: string | null
  propertyId: string | null
  propertyName: string | null
  /** Server-rendered size. Display verbatim — formatting `change` yourself
   *  reports a multiplier as a percentage. */
  magnitudeSummary: string | null
  builtPayloadCount: number | null
  /** False when this member carries no rollback payload — it cannot be undone. */
  rollbackAvailable: boolean | null
  /**
   * What happened to THIS listing at dispatch. Deliberately typed as an open
   * `string`: the documented values are success / failed / skipped /
   * rolled_back / null, but the underlying column has no CHECK constraint and
   * the aggregation falls back to a lexicographic `max()`, so `pending` and
   * `executing` reach us for a member whose tasks are still running.
   *
   * This is the field that makes a silently dead-lettered cohort visible — the
   * parent looked healthy while no member was touched.
   */
  executionStatus: string | null
  decisionStatus?: string | null
  executionReason?: string | null
  executionReasonCode?: string | null
}

export interface AutoExecQueueRow {
  origin: string | null
  action: string | null
  reviewerName: string | null
  driftPolicy: string | null
  queueId: string | null
  decisionId: string
  propertyId: string | null
  propertyName: string | null
  market: string | null
  decisionType: string | null
  decisionScope: string | null
  /** Pre-rendered server-side because the units differ per change kind — an
   *  `adjustment` of 100 is NO change, and a `delta_pct` of 0 is also no
   *  change. Display verbatim; never recompute from the raw change. */
  magnitudeSummary: string | null
  /** Exposure magnitude, not a P&L forecast. `null` is common and legitimate. */
  estRevenueDelta: number | null
  status: AutoExecQueueStatus | null
  attempt: number | null
  notBefore: string | null
  /** Seconds until it may fire, as of `fetchedAtMs`. NEGATIVE means it is due
   *  and waiting on a dispatcher — that is queue lag, not a countdown. */
  firesInSeconds: number | null
  /** Absolute deadline in epoch ms, derived once at map time. Prefer this over
   *  `firesInSeconds` when counting down: `firesInSeconds` is a snapshot taken
   *  when the response was built, so it drifts, while `notBefore` is a real
   *  timestamp. `null` when neither field came back. */
  firesAtMs: number | null
  createdAt: string | null
  /** When the veto window opened, in epoch ms — the row was enqueued at the
   *  same moment the decision flipped to `scheduled`. Paired with `firesAtMs`
   *  it gives the full window, which is what a progress bar needs; the
   *  countdown alone cannot say how much of the window is left. */
  queuedAtMs: number | null
  /** The policy that scheduled it, and the workflow that produced the
   *  decision. `workflowId` is what turns the name into a link; both are null
   *  for analyst-originated decisions, which have no workflow. */
  policyId: string | null
  policyName: string | null
  workflowId: string | null
  workflowName: string | null
  /** Listings this row covers. Always >= 1 server-side (a single-property
   *  decision is a cohort of one), so `> 1` is the test for "this is a cohort",
   *  not `!= null`. */
  memberCount: number | null
  /** Null for change kinds with no top-level dates — base_price,
   *  weekly_discount, min_price. Absence is not "all dates". */
  targetWindow: AutoExecTargetWindow | null
  /** Empty unless the selection asked for it — the two are indistinguishable on
   *  the wire, so drive the UI from requestedness, never from length. */
  members: AutoExecMember[]
}

export interface AutoExecHistoryRow {
  /** Monotonic ledger sequence, serialized as a string because it is a bigint.
   *  The sort key — `occurredAt` ties within a transaction. */
  eventNo: string
  decisionId: string | null
  eventType: AutoExecEventType | null
  /** Groups every decision fired in one dispatcher drain. */
  executionGroupId: string | null
  market: string | null
  occurredAt: string | null
  propertyId: string | null
  propertyName: string | null
  reason: string | null
  rolledBack: boolean
  taskCount: number | null
  taskFailures: number | null
  /** The policy that scheduled it, and the workflow that produced the
   *  decision. `workflowId` is what turns the name into a link; both are null
   *  for analyst-originated decisions, which have no workflow. */
  policyId: string | null
  policyName: string | null
  workflowId: string | null
  workflowName: string | null
  /** Listings this row covers. Always >= 1 server-side (a single-property
   *  decision is a cohort of one), so `> 1` is the test for "this is a cohort",
   *  not `!= null`. */
  memberCount: number | null
  /** Null for change kinds with no top-level dates — base_price,
   *  weekly_discount, min_price. Absence is not "all dates". */
  targetWindow: AutoExecTargetWindow | null
  /** Empty unless the selection asked for it — the two are indistinguishable on
   *  the wire, so drive the UI from requestedness, never from length. */
  members: AutoExecMember[]
}

export interface AutoExecBlockedRow {
  /** Null once the queue row has been reaped (90d) and the row is recovered
   *  from the permanent ledger. Never use this as a React key. */
  queueId: string | null
  decisionId: string
  propertyId: string | null
  propertyName: string | null
  market: string | null
  /** Null on ledger-recovered rows — the gate name lived on the reaped row. */
  gate: string | null
  reason: string | null
  disposition: AutoExecBlockedDisposition | null
  status: string | null
  occurredAt: string | null
  /** The policy that scheduled it, and the workflow that produced the
   *  decision. `workflowId` is what turns the name into a link; both are null
   *  for analyst-originated decisions, which have no workflow. */
  policyId: string | null
  policyName: string | null
  workflowId: string | null
  workflowName: string | null
  /** Listings this row covers. Always >= 1 server-side (a single-property
   *  decision is a cohort of one), so `> 1` is the test for "this is a cohort",
   *  not `!= null`. */
  memberCount: number | null
  /** Null for change kinds with no top-level dates — base_price,
   *  weekly_discount, min_price. Absence is not "all dates". */
  targetWindow: AutoExecTargetWindow | null
  /** Empty unless the selection asked for it — the two are indistinguishable on
   *  the wire, so drive the UI from requestedness, never from length. */
  members: AutoExecMember[]
}

export interface AutoExecFailureRow {
  /** Nullable for the same reason as on blocked rows. */
  queueId: string | null
  decisionId: string
  propertyId: string | null
  propertyName: string | null
  attempt: number | null
  /** Separates OUR gate refusing from WHEELHOUSE refusing. Conflating the two
   *  sends people debugging the wrong layer. */
  errorClass: string | null
  lastError: string | null
  nextAttemptAt: string | null
  status: AutoExecFailureStatus | null
  updatedAt: string | null
  /** The policy that scheduled it, and the workflow that produced the
   *  decision. `workflowId` is what turns the name into a link; both are null
   *  for analyst-originated decisions, which have no workflow. */
  policyId: string | null
  policyName: string | null
  workflowId: string | null
  workflowName: string | null
  /** Listings this row covers. Always >= 1 server-side (a single-property
   *  decision is a cohort of one), so `> 1` is the test for "this is a cohort",
   *  not `!= null`. */
  memberCount: number | null
  /** Null for change kinds with no top-level dates — base_price,
   *  weekly_discount, min_price. Absence is not "all dates". */
  targetWindow: AutoExecTargetWindow | null
  /** Empty unless the selection asked for it — the two are indistinguishable on
   *  the wire, so drive the UI from requestedness, never from length. */
  members: AutoExecMember[]
}

export interface AutoExecBreakerState {
  /** `global` is the cross-policy backstop; any other key is a policy id. */
  key: string | null
  /** `closed` | `open`. */
  state: string | null
  reason: string | null
  trippedAt: string | null
  selfTrips24h: number | null
  manualBlock: boolean
  /** Exempt from AUTOMATIC trips only — a manual block still holds. */
  exempt: boolean
}

export interface AutoExecHealth {
  queueDepth: number | null
  dueNow: number | null
  deferredDepth: number | null
  /** Failed dispatches awaiting a retry — active work, and in neither of the
   *  other two depth counters. */
  retryingDepth: number | null
  /** Lag of the oldest ACTIVE row (due-and-pending or claimed). */
  oldestDueLagSeconds: number | null
  firedLast24h: number | null
  blockedLast24h: number | null
  failedLast24h: number | null
  deadLast24h: number | null
  /** Decisions skipped in the last 24h because live state had drifted. NOT a
   *  failure count — never add it to `deadLast24h`, which is the one that means
   *  something is broken. */
  driftLast24h: number | null
  budgetAvailable: number | null
  budgetCapacity: number | null
  /** Wheelhouse write headroom. The Pro API limit is 12/min. */
  budgetPerMinute: number | null
  /** Lag past 15 min AND nothing fired or blocked in 24h. The lag threshold is
   *  what stops it firing on a cold start. */
  stalled: boolean
  breakers: AutoExecBreakerState[]
}

export interface AutoExecPulse {
  /** Opaque change token. Compare for equality; never parse it. */
  version: string | null
  queued: number | null
  executing: number | null
  deferred: number | null
  retrying: number | null
  dueNow: number | null
  breakerOpen: boolean
  /** Server-chosen poll cadence so it can be retuned without a FE deploy. */
  pollAfterMs: number | null
}

export interface AutoExecListOptions {
  limit?: number
  /** Scopes to one analyst's properties, matching the screen-wide filter. */
  analystId?: string
  /**
   * Widen the selection to pull each row's listings. The server resolves
   * `members` lazily but batches it across the page, so this is one extra
   * query for the whole list rather than one per expanded row.
   */
  withMembers?: boolean
}

interface AutoExecListResult<T> {
  rows: T[]
  /** True when the response carried GraphQL errors — `rows` may be partial. */
  hasErrors: boolean
  /** When the snapshot was taken, so time-relative fields can be aged. */
  fetchedAtMs: number
}

export type AutoExecQueueResult = AutoExecListResult<AutoExecQueueRow>
export type AutoExecHistoryResult = AutoExecListResult<AutoExecHistoryRow>
export type AutoExecBlockedResult = AutoExecListResult<AutoExecBlockedRow>
export type AutoExecFailuresResult = AutoExecListResult<AutoExecFailureRow>

const num = (value: unknown): number | null => (typeof value === 'number' ? value : null)
const str = (value: unknown): string | null => (value == null ? null : String(value))

/** Shared by all four row types — the trio is defined identically on each. */
const AUTO_EXEC_ROW_SHARED_FIELDS = {
  memberCount: true,
  targetWindow: { start: true, end: true },
  // What put this row here. All four row types carry the pair, and the id is
  // what makes the name a link rather than a dead-end label.
  policyId: true,
  policyName: true,
  workflowId: true,
  workflowName: true,
} as const

/**
 * The member expansion. Selected only when a row is open, because the server
 * resolves it lazily — but batched across the whole page by a DataLoader keyed
 * on decisionId, so asking for every row costs one query, not one per row.
 */
const AUTO_EXEC_MEMBER_FIELDS = {
  members: {
    decisionId: true,
    propertyId: true,
    propertyName: true,
    magnitudeSummary: true,
    builtPayloadCount: true,
    rollbackAvailable: true,
    executionStatus: true,
    decisionStatus: true,
    executionReason: true,
    executionReasonCode: true,
  },
} as const

const AUTO_EXEC_QUEUE_FIELDS = {
  origin: true,
  action: true,
  reviewerName: true,
  driftPolicy: true,

  ...AUTO_EXEC_ROW_SHARED_FIELDS,
  queueId: true,
  decisionId: true,
  propertyId: true,
  propertyName: true,
  market: true,
  decisionType: true,
  decisionScope: true,
  magnitudeSummary: true,
  estRevenueDelta: true,
  status: true,
  attempt: true,
  notBefore: true,
  firesInSeconds: true,
  createdAt: true,
} as const

const AUTO_EXEC_HISTORY_FIELDS = {
  ...AUTO_EXEC_ROW_SHARED_FIELDS,
  eventNo: true,
  decisionId: true,
  eventType: true,
  executionGroupId: true,
  market: true,
  occurredAt: true,
  propertyId: true,
  propertyName: true,
  reason: true,
  rolledBack: true,
  taskCount: true,
  taskFailures: true,
} as const

const AUTO_EXEC_BLOCKED_FIELDS = {
  ...AUTO_EXEC_ROW_SHARED_FIELDS,
  queueId: true,
  decisionId: true,
  propertyId: true,
  propertyName: true,
  market: true,
  gate: true,
  reason: true,
  disposition: true,
  status: true,
  occurredAt: true,
} as const

const AUTO_EXEC_FAILURE_FIELDS = {
  ...AUTO_EXEC_ROW_SHARED_FIELDS,
  queueId: true,
  decisionId: true,
  propertyId: true,
  propertyName: true,
  attempt: true,
  errorClass: true,
  lastError: true,
  nextAttemptAt: true,
  status: true,
  updatedAt: true,
} as const

interface RawAutoExecRowShared {
  policyId?: string | null
  policyName?: string | null
  workflowId?: string | null
  workflowName?: string | null
  memberCount?: number | null
  targetWindow?: { start?: string | null; end?: string | null } | null
  members?: Array<{
    decisionId?: string | null
    propertyId?: string | null
    propertyName?: string | null
    magnitudeSummary?: string | null
    builtPayloadCount?: number | null
    rollbackAvailable?: boolean | null
    executionStatus?: string | null
    decisionStatus?: string | null
    executionReason?: string | null
    executionReasonCode?: string | null
  }> | null
}

/** The `memberCount` / `targetWindow` / `members` trio, identical on all four
 *  row types. */
function mapAutoExecRowShared(raw: RawAutoExecRowShared): {
  policyId: string | null
  policyName: string | null
  workflowId: string | null
  workflowName: string | null
  memberCount: number | null
  targetWindow: AutoExecTargetWindow | null
  members: AutoExecMember[]
} {
  const start = raw.targetWindow?.start ?? null
  const end = raw.targetWindow?.end ?? null
  return {
    policyId: raw.policyId ?? null,
    policyName: raw.policyName ?? null,
    workflowId: raw.workflowId ?? null,
    workflowName: raw.workflowName ?? null,
    memberCount: num(raw.memberCount),
    // Both ends absent means the change kind has no dates at all (base_price,
    // weekly_discount, min_price) — a window object with two nulls would read
    // as "we have dates and they are empty", which is a different claim.
    targetWindow: start === null && end === null ? null : { start, end },
    members: (raw.members ?? []).map(member => ({
      decisionId: member.decisionId ?? null,
      propertyId: member.propertyId ?? null,
      propertyName: member.propertyName ?? null,
      magnitudeSummary: member.magnitudeSummary ?? null,
      builtPayloadCount: num(member.builtPayloadCount),
      rollbackAvailable:
        typeof member.rollbackAvailable === 'boolean' ? member.rollbackAvailable : null,
      executionStatus: member.executionStatus ?? null,
      decisionStatus: member.decisionStatus ?? null,
      executionReason: member.executionReason ?? null,
      executionReasonCode: member.executionReasonCode ?? null,
    })),
  }
}

function mapAutoExecQueueRow(
  fetchedAtMs: number,
  raw: {
    origin?: string | null
    action?: string | null
    reviewerName?: string | null
    driftPolicy?: string | null
    queueId?: string | null
    decisionId?: string | null
    propertyId?: string | null
    propertyName?: string | null
    market?: string | null
    decisionType?: string | null
    decisionScope?: string | null
    magnitudeSummary?: string | null
    estRevenueDelta?: number | null
    status?: string | null
    attempt?: number | null
    notBefore?: unknown
    firesInSeconds?: number | null
    createdAt?: unknown
  } & RawAutoExecRowShared,
): AutoExecQueueRow | null {
  if (!raw.decisionId) return null
  const notBefore = str(raw.notBefore)
  const createdAt = str(raw.createdAt)
  const createdAtMs = createdAt ? Date.parse(createdAt) : NaN
  const firesInSeconds = num(raw.firesInSeconds)
  // `firesInSeconds` is the authority, NOT `notBefore`.
  //
  // It is computed on the database clock and floored at zero. Deriving the
  // countdown from `notBefore` instead compares a server absolute against the
  // viewer's clock, so a few minutes of skew renders "fires in -3 min" on a
  // decision that has not fired, or hides one that is seconds away.
  //
  // Anchoring on the server's duration and subtracting locally-measured
  // elapsed time uses the local clock only for an interval, which is immune to
  // skew. `notBefore` is kept for showing the exact time, nothing else.
  const notBeforeMs = notBefore ? Date.parse(notBefore) : NaN
  const firesAtMs =
    firesInSeconds !== null
      ? fetchedAtMs + firesInSeconds * 1000
      : Number.isFinite(notBeforeMs)
        ? notBeforeMs
        : null
  return {
    origin: raw.origin ?? null,
    action: raw.action ?? null,
    reviewerName: raw.reviewerName ?? null,
    driftPolicy: raw.driftPolicy ?? null,
    queueId: raw.queueId ?? null,
    decisionId: raw.decisionId,
    propertyId: raw.propertyId ?? null,
    propertyName: raw.propertyName ?? null,
    market: raw.market ?? null,
    decisionType: raw.decisionType ?? null,
    decisionScope: raw.decisionScope ?? null,
    magnitudeSummary: raw.magnitudeSummary ?? null,
    estRevenueDelta: num(raw.estRevenueDelta),
    status: (raw.status ?? null) as AutoExecQueueStatus | null,
    attempt: num(raw.attempt),
    notBefore,
    firesInSeconds,
    firesAtMs,
    createdAt,
    queuedAtMs: Number.isFinite(createdAtMs) ? createdAtMs : null,
    ...mapAutoExecRowShared(raw),
  }
}

function mapAutoExecHistoryRow(
  raw: {
    eventNo?: string | null
    decisionId?: string | null
    eventType?: string | null
    executionGroupId?: string | null
    market?: string | null
    occurredAt?: unknown
    policyId?: string | null
    propertyId?: string | null
    propertyName?: string | null
    reason?: string | null
    rolledBack?: boolean | null
    taskCount?: number | null
    taskFailures?: number | null
  } & RawAutoExecRowShared,
): AutoExecHistoryRow | null {
  if (!raw.eventNo) return null
  return {
    eventNo: raw.eventNo,
    decisionId: raw.decisionId ?? null,
    eventType: (raw.eventType ?? null) as AutoExecEventType | null,
    executionGroupId: raw.executionGroupId ?? null,
    market: raw.market ?? null,
    occurredAt: str(raw.occurredAt),
    propertyId: raw.propertyId ?? null,
    propertyName: raw.propertyName ?? null,
    reason: raw.reason ?? null,
    rolledBack: raw.rolledBack === true,
    taskCount: num(raw.taskCount),
    taskFailures: num(raw.taskFailures),
    ...mapAutoExecRowShared(raw),
  }
}

function mapAutoExecBlockedRow(
  raw: {
    queueId?: string | null
    decisionId?: string | null
    propertyId?: string | null
    propertyName?: string | null
    market?: string | null
    policyId?: string | null
    gate?: string | null
    reason?: string | null
    disposition?: string | null
    status?: string | null
    occurredAt?: unknown
  } & RawAutoExecRowShared,
): AutoExecBlockedRow | null {
  if (!raw.decisionId) return null
  return {
    queueId: raw.queueId ?? null,
    decisionId: raw.decisionId,
    propertyId: raw.propertyId ?? null,
    propertyName: raw.propertyName ?? null,
    market: raw.market ?? null,
    gate: raw.gate ?? null,
    reason: raw.reason ?? null,
    disposition: (raw.disposition ?? null) as AutoExecBlockedDisposition | null,
    status: raw.status ?? null,
    occurredAt: str(raw.occurredAt),
    ...mapAutoExecRowShared(raw),
  }
}

function mapAutoExecFailureRow(
  raw: {
    queueId?: string | null
    decisionId?: string | null
    propertyId?: string | null
    propertyName?: string | null
    attempt?: number | null
    errorClass?: string | null
    lastError?: string | null
    nextAttemptAt?: unknown
    status?: string | null
    updatedAt?: unknown
  } & RawAutoExecRowShared,
): AutoExecFailureRow | null {
  if (!raw.decisionId) return null
  return {
    queueId: raw.queueId ?? null,
    decisionId: raw.decisionId,
    propertyId: raw.propertyId ?? null,
    propertyName: raw.propertyName ?? null,
    attempt: num(raw.attempt),
    errorClass: raw.errorClass ?? null,
    lastError: raw.lastError ?? null,
    nextAttemptAt: str(raw.nextAttemptAt),
    status: (raw.status ?? null) as AutoExecFailureStatus | null,
    updatedAt: str(raw.updatedAt),
    ...mapAutoExecRowShared(raw),
  }
}

/**
 * Decisions scheduled to auto-execute and not yet fired, soonest first.
 *
 * Uses the silent `.raw` endpoint so a partial response still renders — the
 * queue is the surface someone watches to catch a bad batch, and showing
 * nothing at all is the worst outcome.
 */
export async function getAutoExecutionQueue(
  options: AutoExecListOptions = {},
): Promise<AutoExecQueueResult> {
  const { limit, analystId, withMembers } = options
  const { data, errors } = await arriereBackoffice.queries.revenueManagement.raw({
    __alias: 'revenueAgentsAutoExecQueue',
    autoExecutionQueue: {
      __args: { limit, analystId },
      ...AUTO_EXEC_QUEUE_FIELDS,
      // Widening the selection is the whole mechanism: `members` is resolved
      // lazily server-side, so not asking for it is what makes the unexpanded
      // list cheap.
      ...(withMembers ? AUTO_EXEC_MEMBER_FIELDS : {}),
    },
  })

  // Stamped once, before the map, so every row on this page ages against the
  // same instant.
  const fetchedAtMs = Date.now()
  const rows = (data?.autoExecutionQueue ?? [])
    .map(row => mapAutoExecQueueRow(fetchedAtMs, row))
    .filter((row): row is AutoExecQueueRow => !!row)
    .sort((a, b) => (a.firesAtMs ?? Infinity) - (b.firesAtMs ?? Infinity))

  return { rows, hasErrors: !!errors?.length, fetchedAtMs }
}

/**
 * Auto-executions that fired or were reverted, newest first. Sourced from the
 * append-only ledger, so it outlives the 90-day queue cleanup.
 */
export async function getAutoExecutionHistory(
  options: AutoExecListOptions = {},
): Promise<AutoExecHistoryResult> {
  const { limit, analystId, withMembers } = options
  const { data, errors } = await arriereBackoffice.queries.revenueManagement.raw({
    __alias: 'revenueAgentsAutoExecHistory',
    autoExecutionHistory: {
      __args: { limit, analystId },
      ...AUTO_EXEC_HISTORY_FIELDS,
      // Widening the selection is the whole mechanism: `members` is resolved
      // lazily server-side, so not asking for it is what makes the unexpanded
      // list cheap.
      ...(withMembers ? AUTO_EXEC_MEMBER_FIELDS : {}),
    },
  })

  const rows = (data?.autoExecutionHistory ?? [])
    .map(mapAutoExecHistoryRow)
    .filter((row): row is AutoExecHistoryRow => !!row)
    .sort((a, b) => compareEventNoDesc(a.eventNo, b.eventNo))

  return { rows, hasErrors: !!errors?.length, fetchedAtMs: Date.now() }
}

/**
 * Decisions that did NOT auto-execute. Two very different populations share
 * this list — see `disposition`; presenting them together reads as a mass
 * rejection when most rows are simply queued behind a breaker.
 */
export async function getAutoExecutionBlocked(
  options: AutoExecListOptions = {},
): Promise<AutoExecBlockedResult> {
  const { limit, analystId, withMembers } = options
  const { data, errors } = await arriereBackoffice.queries.revenueManagement.raw({
    __alias: 'revenueAgentsAutoExecBlocked',
    autoExecutionBlocked: {
      __args: { limit, analystId },
      ...AUTO_EXEC_BLOCKED_FIELDS,
      // Widening the selection is the whole mechanism: `members` is resolved
      // lazily server-side, so not asking for it is what makes the unexpanded
      // list cheap.
      ...(withMembers ? AUTO_EXEC_MEMBER_FIELDS : {}),
    },
  })

  const rows = (data?.autoExecutionBlocked ?? [])
    .map(mapAutoExecBlockedRow)
    .filter((row): row is AutoExecBlockedRow => !!row)

  return { rows, hasErrors: !!errors?.length, fetchedAtMs: Date.now() }
}

/**
 * Dispatches retrying, dead-lettered, or aged out. A decision the system
 * lacked credentials to execute is NOT here — that is a gate verdict, so it
 * goes to `autoExecutionBlocked` and back to the review queue.
 */
export async function getAutoExecutionFailures(
  options: AutoExecListOptions = {},
): Promise<AutoExecFailuresResult> {
  const { limit, analystId, withMembers } = options
  const { data, errors } = await arriereBackoffice.queries.revenueManagement.raw({
    __alias: 'revenueAgentsAutoExecFailures',
    autoExecutionFailures: {
      __args: { limit, analystId },
      ...AUTO_EXEC_FAILURE_FIELDS,
      // Widening the selection is the whole mechanism: `members` is resolved
      // lazily server-side, so not asking for it is what makes the unexpanded
      // list cheap.
      ...(withMembers ? AUTO_EXEC_MEMBER_FIELDS : {}),
    },
  })

  const rows = (data?.autoExecutionFailures ?? [])
    .map(mapAutoExecFailureRow)
    .filter((row): row is AutoExecFailureRow => !!row)

  return { rows, hasErrors: !!errors?.length, fetchedAtMs: Date.now() }
}

/** Queue depth and lag, 24h volume, breaker and rate-budget state. */
export async function getAutoExecutionHealth(): Promise<AutoExecHealth | null> {
  const { data } = await arriereBackoffice.queries.revenueManagement.raw({
    __alias: 'revenueAgentsAutoExecHealth',
    autoExecutionHealth: {
      queueDepth: true,
      dueNow: true,
      deferredDepth: true,
      retryingDepth: true,
      oldestDueLagSeconds: true,
      firedLast24h: true,
      blockedLast24h: true,
      failedLast24h: true,
      deadLast24h: true,
      driftLast24h: true,
      budgetAvailable: true,
      budgetCapacity: true,
      budgetPerMinute: true,
      stalled: true,
      breakers: {
        key: true,
        state: true,
        reason: true,
        trippedAt: true,
        selfTrips24h: true,
        manualBlock: true,
        exempt: true,
      },
    },
  })

  const raw = data?.autoExecutionHealth
  if (!raw) return null
  return {
    queueDepth: num(raw.queueDepth),
    dueNow: num(raw.dueNow),
    deferredDepth: num(raw.deferredDepth),
    retryingDepth: num(raw.retryingDepth),
    oldestDueLagSeconds: num(raw.oldestDueLagSeconds),
    firedLast24h: num(raw.firedLast24h),
    blockedLast24h: num(raw.blockedLast24h),
    failedLast24h: num(raw.failedLast24h),
    deadLast24h: num(raw.deadLast24h),
    driftLast24h: num(raw.driftLast24h),
    budgetAvailable: num(raw.budgetAvailable),
    budgetCapacity: num(raw.budgetCapacity),
    budgetPerMinute: num(raw.budgetPerMinute),
    stalled: raw.stalled === true,
    breakers: (raw.breakers ?? []).map(breaker => ({
      key: breaker.key ?? null,
      state: breaker.state ?? null,
      reason: breaker.reason ?? null,
      trippedAt: str(breaker.trippedAt),
      selfTrips24h: num(breaker.selfTrips24h),
      manualBlock: breaker.manualBlock === true,
      exempt: breaker.exempt === true,
    })),
  }
}

/**
 * The heartbeat that drives the live panel. Cheap (one index-only scan) and
 * polled on the server's own `pollAfterMs` cadence; the lists are refetched
 * only when `version` changes.
 *
 * `__retry: false` on purpose — a failed heartbeat should just wait for the
 * next tick rather than pile up retries behind a struggling API.
 */
export async function getAutoExecutionPulse(): Promise<AutoExecPulse | null> {
  const { data } = await arriereBackoffice.queries.revenueManagement.raw({
    __alias: 'revenueAgentsAutoExecPulse',
    __retry: false,
    autoExecutionPulse: {
      version: true,
      queued: true,
      executing: true,
      deferred: true,
      retrying: true,
      dueNow: true,
      breakerOpen: true,
      pollAfterMs: true,
    },
  })

  const raw = data?.autoExecutionPulse
  if (!raw) return null
  return {
    version: raw.version ?? null,
    queued: num(raw.queued),
    executing: num(raw.executing),
    deferred: num(raw.deferred),
    retrying: num(raw.retrying),
    dueNow: num(raw.dueNow),
    breakerOpen: raw.breakerOpen === true,
    pollAfterMs: num(raw.pollAfterMs),
  }
}

/**
 * The veto. Returns the decision to `pending_review`; the queue row is
 * cancelled by a DB trigger, so no second call is needed.
 *
 * Throws with `extensions.code === AUTO_EXECUTION_NOT_CANCELLABLE` (409) when
 * a dispatcher got there first. The message distinguishes "being dispatched
 * right now" from "already fired" — show it verbatim.
 */
export async function cancelAutoExecution(decisionId: string, reason?: string): Promise<boolean> {
  const result = await arriereBackoffice.mutations.revenueManagement({
    __alias: 'cancelAutoExecution',
    cancelAutoExecution: {
      __args: { decisionId, reason: reason?.trim() || undefined },
    },
  })

  return result.cancelAutoExecution === true
}

// ---------------------------------------------------------------------------
// Auto-execution policies — the second of the two switches
//
// A decision auto-executes only when BOTH are on: the workflow's routing is
// `auto_approve`, AND an enabled policy's scope matches it. Neither alone does
// anything, and both default off.
//
// Note the asymmetry this file cannot fix: analyst decisions have no workflow,
// so they have no second switch and the policy scope is the only gate.
// ---------------------------------------------------------------------------

/** Only these two. `routing` is a plain String on the wire, so nothing but this
 *  list stops a typo reaching the server — `dry_run` is reserved and refused. */
export type WorkflowRouting = 'pending_review' | 'auto_approve'

export interface AutoExecPolicyScope {
  /** Workflow levers. The right way to scope a workflow cohort — cohorts are
   *  enqueued with `decision_type: 'cohort_policy'`, never the lever name. */
  adjustmentType: AutoExecutionLever[]
  /** Deliberately open: the domain grows with the pipeline and the backend keeps
   *  it untyped so the frontend is not a deploy dependency. Never a dropdown. */
  decisionType: string[]
  marketId: string[]
  propertyIds: string[]
  workflowSetId: string[]
}

export interface AutoExecPolicyCaps {
  /** Percentage POINTS of change, not the raw adjustment: an adjustment of 90 is
   *  a 10% cut, so `maxPct: 15` permits it. */
  maxPct: number | null
  /** Absolute CURRENCY ceiling. */
  maxAbs: number | null
  /** NIGHTS, not currency — kept apart from `maxAbs` on purpose. */
  maxNights: number | null
  maxDiscountPct: number | null
  maxPerRun: number | null
  maxPerMarketPerDay: number | null
  /** FAIL-CLOSED: a decision whose revenue impact cannot be estimated is
   *  BLOCKED, not allowed. The estimate is null on every queue row today, so
   *  arming this silently blocks everything. */
  maxEstRevenueDeltaRun: number | null
  /** Same fail-closed caveat as `maxEstRevenueDeltaRun`. */
  maxEstRevenueDeltaMarketDay: number | null
}

export interface AutoExecPolicyIssue {
  field: string
  /** Written for the person configuring this — render verbatim. */
  message: string
  /** `error` refuses the write, `warning` saves but will not behave as intended. */
  severity: string
}

export interface AutoExecPolicy {
  id: string
  name: string | null
  enabled: boolean
  scope: AutoExecPolicyScope
  caps: AutoExecPolicyCaps
  windowHours: number | null
  /** ISO weekdays, 1 = Monday. NOT cron indices. */
  fireDays: number[] | null
  fireTimezone: string | null
  queueCeiling: number | null
  maxQueuedAgeHours: number | null
  requireRollback: boolean
  /** Live queue rows attributed to this policy — pending, claimed, deferred AND
   *  failed. Editing a policy holding decisions re-scopes them at fire time. */
  activeQueueDepth: number | null
  /**
   * Set when an earlier enabled policy at the SAME specificity level overlaps
   * this one, so it wins the decisions they share.
   *
   * The most specific matching policy governs (properties > workflow set >
   * market > lever only), so a narrower policy always wins over a broader one.
   * Saving a same-level overlap is refused; this flags rows written before that.
   */
  shadowedBy: { id: string; name: string | null } | null
  createdAt: string | null
  createdBy: string | null
  updatedAt: string | null
}

export interface AutoExecPolicySaveResult {
  policy: AutoExecPolicy | null
  /** Warnings only. An error-severity finding makes the server throw instead —
   *  see `AUTO_EXEC_POLICY_INVALID`. */
  issues: AutoExecPolicyIssue[]
}

/** Thrown (not returned) when validation refuses the write. The message is one
 *  multi-line blob of `  - field: message` lines. */
export const AUTO_EXEC_POLICY_INVALID = 'AUTO_EXEC_POLICY_INVALID'
export const AUTO_EXEC_POLICY_EXISTS = 'AUTO_EXEC_POLICY_EXISTS'

const AUTO_EXEC_POLICY_FIELDS = {
  id: true,
  name: true,
  enabled: true,
  windowHours: true,
  fireDays: true,
  fireTimezone: true,
  queueCeiling: true,
  maxQueuedAgeHours: true,
  requireRollback: true,
  activeQueueDepth: true,
  shadowedBy: { id: true, name: true },
  createdAt: true,
  createdBy: true,
  updatedAt: true,
  scope: {
    adjustmentType: true,
    decisionType: true,
    marketId: true,
    propertyIds: true,
    workflowSetId: true,
  },
  caps: {
    maxPct: true,
    maxAbs: true,
    maxNights: true,
    maxDiscountPct: true,
    maxPerRun: true,
    maxPerMarketPerDay: true,
    maxEstRevenueDeltaRun: true,
    maxEstRevenueDeltaMarketDay: true,
  },
} as const

const AUTO_EXEC_POLICY_SAVE_SELECTION = {
  policy: AUTO_EXEC_POLICY_FIELDS,
  issues: { field: true, message: true, severity: true },
} as const

function mapAutoExecPolicy(raw: {
  id?: string | null
  name?: string | null
  enabled?: boolean | null
  windowHours?: number | null
  fireDays?: number[] | null
  fireTimezone?: string | null
  queueCeiling?: number | null
  maxQueuedAgeHours?: number | null
  requireRollback?: boolean | null
  activeQueueDepth?: number | null
  shadowedBy?: { id?: string | null; name?: string | null } | null
  createdAt?: unknown
  createdBy?: string | null
  updatedAt?: unknown
  scope?: {
    adjustmentType?: AutoExecutionLever[] | null
    decisionType?: string[] | null
    marketId?: string[] | null
    propertyIds?: string[] | null
    workflowSetId?: string[] | null
  } | null
  caps?: {
    maxPct?: number | null
    maxAbs?: number | null
    maxNights?: number | null
    maxDiscountPct?: number | null
    maxPerRun?: number | null
    maxPerMarketPerDay?: number | null
    maxEstRevenueDeltaRun?: number | null
    maxEstRevenueDeltaMarketDay?: number | null
  } | null
}): AutoExecPolicy | null {
  if (!raw.id) return null
  return {
    id: raw.id,
    name: raw.name ?? null,
    // Both default off server-side; anything but an explicit true is off.
    enabled: raw.enabled === true,
    scope: {
      adjustmentType: raw.scope?.adjustmentType ?? [],
      decisionType: raw.scope?.decisionType ?? [],
      marketId: raw.scope?.marketId ?? [],
      propertyIds: raw.scope?.propertyIds ?? [],
      workflowSetId: raw.scope?.workflowSetId ?? [],
    },
    caps: {
      maxPct: num(raw.caps?.maxPct),
      maxAbs: num(raw.caps?.maxAbs),
      maxNights: num(raw.caps?.maxNights),
      maxDiscountPct: num(raw.caps?.maxDiscountPct),
      maxPerRun: num(raw.caps?.maxPerRun),
      maxPerMarketPerDay: num(raw.caps?.maxPerMarketPerDay),
      maxEstRevenueDeltaRun: num(raw.caps?.maxEstRevenueDeltaRun),
      maxEstRevenueDeltaMarketDay: num(raw.caps?.maxEstRevenueDeltaMarketDay),
    },
    windowHours: num(raw.windowHours),
    fireDays: raw.fireDays ?? null,
    fireTimezone: raw.fireTimezone ?? null,
    queueCeiling: num(raw.queueCeiling),
    maxQueuedAgeHours: num(raw.maxQueuedAgeHours),
    // Defaults true server-side, and it is the safety rail — only an explicit
    // false turns it off.
    requireRollback: raw.requireRollback !== false,
    activeQueueDepth: num(raw.activeQueueDepth),
    shadowedBy: raw.shadowedBy?.id
      ? { id: raw.shadowedBy.id, name: raw.shadowedBy.name ?? null }
      : null,
    createdAt: str(raw.createdAt),
    createdBy: raw.createdBy ?? null,
    updatedAt: str(raw.updatedAt),
  }
}

function mapAutoExecPolicyIssues(
  raw:
    | Array<{ field?: string | null; message?: string | null; severity?: string | null }>
    | null
    | undefined,
): AutoExecPolicyIssue[] {
  return (raw ?? []).map(issue => ({
    field: issue.field ?? '',
    message: issue.message ?? '',
    severity: issue.severity ?? 'warning',
  }))
}

/**
 * One selectable lever, and what the server will demand of a policy that scopes
 * it.
 *
 * `requiredCap` arrives in the WIRE spelling (`max_pct`), not the camelCase the
 * input type uses. It is derived server-side from the same map the validator
 * enforces, which is the whole reason to read it rather than keep a copy: a
 * hardcoded table is the only way the form and the validator can disagree.
 */
export interface AutoExecLeverOption {
  value: string
  requiredCap: string | null
  /** False for levers whose writes need a reviewer's identity — no policy
   *  changes that, so the form disables rather than hides them. */
  autoExecutable: boolean
  blockedReason: string | null
}

/**
 * What a policy scope may be built from.
 *
 * `decisionTypes` is deliberately selected but not mapped: it belongs to the
 * analyst path, which is out of scope here. Leaving it out of the app type keeps
 * the omission explicit rather than accidental.
 */
export async function getAutoExecutionScopeOptions(): Promise<AutoExecLeverOption[]> {
  const { data } = await arriereBackoffice.queries.revenueManagement.raw({
    __alias: 'revenueAgentsAutoExecScopeOptions',
    autoExecutionScopeOptions: {
      levers: {
        value: true,
        requiredCap: true,
        autoExecutable: true,
        blockedReason: true,
      },
    },
  })

  return (data?.autoExecutionScopeOptions?.levers ?? [])
    .filter((lever): lever is { value: string } & typeof lever => !!lever.value)
    .map(lever => ({
      value: lever.value,
      requiredCap: lever.requiredCap ?? null,
      // Only an explicit true opts a lever in — an absent flag must not read as
      // "safe to automate".
      autoExecutable: lever.autoExecutable === true,
      blockedReason: lever.blockedReason ?? null,
    }))
}

/**
 * All policies, in the order the evaluator loads them.
 *
 * That order is the whole story: matching is FIRST-MATCH-WINS by creation time
 * with no specificity ranking, so a broad policy created in January shadows a
 * narrow one created in March. Render them in this order and do not re-sort.
 */
export async function getAutoExecutionPolicies(
  /** Narrows to policies that could claim this lever — including those that do
   *  not constrain the lever at all, since an absent key is unconstrained. */
  adjustmentType?: AutoExecutionLever,
): Promise<AutoExecPolicy[]> {
  const { data } = await arriereBackoffice.queries.revenueManagement.raw({
    __alias: 'revenueAgentsAutoExecPolicies',
    autoExecutionPolicies: { __args: { adjustmentType }, ...AUTO_EXEC_POLICY_FIELDS },
  })

  return (data?.autoExecutionPolicies ?? [])
    .map(mapAutoExecPolicy)
    .filter((policy): policy is AutoExecPolicy => !!policy)
}

export type { AutoExecutionPolicyInput }

/**
 * Create a policy. The CLIENT supplies the id — it is also the breaker key and
 * surfaces as `policyId` on queue rows, so it wants to be a readable slug.
 *
 * Throws `AUTO_EXEC_POLICY_INVALID` on any error-severity finding; only warnings
 * come back in `issues`.
 */
export async function createAutoExecutionPolicy(
  id: string,
  input: AutoExecutionPolicyInput,
): Promise<AutoExecPolicySaveResult> {
  const result = await arriereBackoffice.mutations.revenueManagement({
    __alias: 'createAutoExecutionPolicy',
    createAutoExecutionPolicy: {
      __args: { id, input },
      ...AUTO_EXEC_POLICY_SAVE_SELECTION,
    },
  })
  const raw = result.createAutoExecutionPolicy
  return {
    policy: raw?.policy ? mapAutoExecPolicy(raw.policy) : null,
    issues: mapAutoExecPolicyIssues(raw?.issues),
  }
}

/**
 * Patch a policy. Only the provided fields change — and validation runs against
 * the MERGED result, so a patch touching only caps can be refused for a scope
 * set months ago.
 *
 * Absent and null differ: omitting a key leaves it alone, sending null clears it.
 */
export async function updateAutoExecutionPolicy(
  id: string,
  input: AutoExecutionPolicyInput,
): Promise<AutoExecPolicySaveResult> {
  const result = await arriereBackoffice.mutations.revenueManagement({
    __alias: 'updateAutoExecutionPolicy',
    updateAutoExecutionPolicy: {
      __args: { id, input },
      ...AUTO_EXEC_POLICY_SAVE_SELECTION,
    },
  })
  const raw = result.updateAutoExecutionPolicy
  return {
    policy: raw?.policy ? mapAutoExecPolicy(raw.policy) : null,
    issues: mapAutoExecPolicyIssues(raw?.issues),
  }
}

/**
 * Would a workflow that does not exist yet auto-execute?
 *
 * `workflow.autoExecution` can only answer once it is saved, but the state most
 * worth warning about is the one at creation: routing switched on while no
 * policy covers the workflow. Nothing errors in that case — it saves, looks
 * automated, and silently never fires.
 *
 * Shares a resolver with the saved field, so preview and reality cannot
 * disagree. Note `routing` is the ENUM here while `setWorkflowRouting` takes a
 * plain string for the same concept — pass through `toRoutingEnum` rather than
 * a literal, because a wrong case resolves to `not_opted_in`, which is the one
 * answer that suppresses the warning this exists to raise.
 */
export async function previewWorkflowAutoExecution(args: {
  routing: WorkflowRouting
  /** The RAW lever, and only when it is one — `WorkflowAdjustmentType` is wider
   *  than `AutoExecutionLever`. Omit rather than coerce. */
  adjustmentType?: AutoExecutionLever
  workflowSetId?: string
}): Promise<AutoExecutionState | null> {
  const { data } = await arriereBackoffice.queries.revenueManagement.raw({
    __alias: 'previewWorkflowAutoExecution',
    previewWorkflowAutoExecution: {
      __args: {
        routing: toRoutingEnum(args.routing),
        adjustmentType: args.adjustmentType,
        workflowSetId: args.workflowSetId,
      },
      ...AUTO_EXECUTION_STATE_FIELDS,
    },
  })
  return mapAutoExecutionState(data?.previewWorkflowAutoExecution)
}

/** The same concept is spelled three ways across this feature and two of them
 *  are plain strings, so the conversion lives here rather than at call sites. */
export function toRoutingEnum(routing: WorkflowRouting): RevenueManagementWorkflowRouting {
  return routing === 'auto_approve'
    ? RevenueManagementWorkflowRouting.autoApprove
    : RevenueManagementWorkflowRouting.pendingReview
}

export interface SetWorkflowRoutingResult {
  routing: string | null
  /** Decisions this change does NOT affect. Auto-execution is decided when a run
   *  persists its decisions, so routing changes future runs only — surface this
   *  or "I turned it on, why is my backlog still there" is guaranteed. */
  unaffectedPendingDecisions: number | null
}

/**
 * Turn auto-execution routing on or off for one workflow.
 *
 * Re-runs the workflow's SQL artifact gate against the warehouse before writing,
 * so it is not a cheap toggle and can fail for reasons unrelated to routing.
 */
export async function setWorkflowRouting(
  id: string,
  routing: WorkflowRouting,
): Promise<SetWorkflowRoutingResult | null> {
  const result = await arriereBackoffice.mutations.revenueManagement({
    __alias: 'setWorkflowRouting',
    setWorkflowRouting: {
      __args: { id, routing },
      routing: true,
      unaffectedPendingDecisions: true,
    },
  })

  const raw = result.setWorkflowRouting
  if (!raw) return null
  return {
    routing: raw.routing ?? null,
    unaffectedPendingDecisions: num(raw.unaffectedPendingDecisions),
  }
}
